import App, { should_skip_tutorial } from './application-container';
import {
    set_button_states,
    has_selected_objects,
} from './non_graphical_ui_actions';
import * as THREE from 'three';
import {
    animate,
    show_reference_partstudio_model,
    show_reference_stl_model,
    resize_all_reference_vertices_for_zoom,
    hide_reference_partstudio_model,
    hide_reference_stl_model,
    toggle_grid,
    start_adding_vertex,
    stop_adding_vertex,
    display_axes,
    initialize_axes,
    abort_drawing_edge_on_face,
    start_drawing_edge_on_face,
    interrupt_offset_face,
    hide_edge_midpoint,
    add_edge_action,
    unhighlight_currently_highlighted_object,
    start_offset_face,
    add_vertex_location_while_drawing_edge_on_face,
    set_grid_size_and_adjust_grid,
    show_edge_midpoint,
    hide_reference_parasolid_model,
} from './scene';
import {
    switch_to_lights,
    switch_to_background,
    create_materials_for_face,
    uid_unhighlight_all_objects,
    hide_shadows,
    show_shadows,
    set_default_vertex_color,
    switch_to_material,
} from './colours_materials_appearance';
import {
    uil_get_body_of_vertex,
    uil_get_edges_of_face,
    uil_get_vertices_of_edge,
    uil_get_visible_edges_of_vertex,
    uil_delete_vertices,
    uil_delete_edges,
    uil_get_faces_of_edge,
    uil_is_object_fixed,
    uil_get_edges_of_vertex,
    uil_get_body_of_face,
    uil_get_face_label,
    uil_save_undo_state,
    uil_is_object_visible,
    in_compound_operation,
    uil_get_edge_tangent_at_vertex,
    uil_is_joint_smooth,
    uil_get_body_of_edge,
    uil_set_display_face_triangulation_res_for_drag,
    uil_translate_edge_list,
    uil_get_face_geometry,
    uil_end_drag_manipulator,
    uil_add_new_vertex,
    uil_is_vertex_of_face,
    uil_inflate_face,
    uil_rotate_edge_list_around_axis,
    uil_redo,
    uil_delete_selected_objects,
    uil_make_hole_or_handle_multi,
    uil_get_face_connected_components,
    uil_make_faces_visible,
    uil_pop_faces,
    uil_make_faces_invisible,
    uil_listify_edges,
    uil_get_point_at_edge_param,
    uil_add_new_edge_quickly,
    uil_change_object_visibility,
    uil_move_vertex_to_coords,
    uil_get_edge_length,
    uil_unfix_objects,
} from './ui_logic';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import { CoreFunctionCallDescriptions } from '@phi/logging/core-function-descriptions';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';
import { Line2 } from 'three/examples/jsm/lines/Line2';
import {
    can_move_selected_objects,
    vertices_of_face,
    selected_objects_are_for_rotating_edge_tangent,
    faces_of_body,
    get_neighbouring_face_wrt_edge,
    can_delete_selected_objects,
    mustNotDisplayVertex,
    mustNotDisplayEdge,
    mustNotDisplayFace,
    remove_from_symmetry_set,
    add_to_symmetry_set,
    find_all_pairs_in_listified_edge_list,
    get_object_from_id,
} from './lattice';
import {
    find_edge_closest_vertex_to_coordinates,
    push_nodups,
    recalculate_view_dimensions,
    suppress_animate,
    suppress_undo_tracking,
    unsuppress_animate_if_ready,
    unsuppress_undo_tracking_if_ready,
    reset_undo_tracking,
    suppress_ui_messages,
    unsuppress_ui_messages_if_ready,
    adjust_camera_up_and_update_projection_matrix,
    get_camera_look_vector,
    object_is_selected,
    request_setting_button_states,
    find_currently_pointed_object,
    raycaster_intersect_objects,
    update_dom_text_values_and_coordinates,
    find_intersection_object,
    unsuppress_core_face_tessellation_if_ready,
    suppress_core_face_tessellation,
    reset_core_face_tessellation,
    reset_animate_count,
    reset_zero_thickness_checking_count,
    suppress_properly_splitting_invisible_components,
    unsuppress_properly_splitting_invisible_components_if_ready,
    properly_splitting_invisible_components_is_suppressed,
} from './utility';
import HashTable from './hashtable';
import RigidMoveManipulator from './RigidMoveManipulator';
import { next_frame } from '@vaaas/fpts/promise';
import { compose, pipe } from '@vaaas/fpts/function';
import { inside, of as array, unique } from '@vaaas/fpts/array';
import { inside as sinside, of as set, intersect } from '@vaaas/fpts/set';
import {
    filter,
    bind,
    count,
    each,
    every,
    flatten,
    map,
    foldl,
    partition,
    combinations,
    some,
    optimumBy,
    foldl1,
} from '@vaaas/fpts/iter';
import { lefts } from '@vaaas/fpts/alist';
import { show_modal, hide_modal } from './modals';
import * as maths from './maths';
import * as vector3 from './vector3';
import { user_interaction_state_provider } from './UserInteractionState'
import { handle_missing_tangencies, uid_download } from './input_output';
import { find_edge_shared_vertex, get_core_version_info, get_global_id, is_object_visible, reset_global_id } from './JCore/jcore';
import {
    end_move_edge_list,
    end_move_vertices,
    get_face_curvatures,
    get_face_normals,
    init,
    move_edge_list,
    move_vertices,
    start_move_edge_list,
    start_move_vertices,
} from './JCore/logging_jcore';
import { urls } from '@phi/constants';
import { WrappedValue } from '@phi/wrapped-value';
import { display_tangencies, fix_edge_tangent_at_vertex, hide_tangencies, smoothen_objects, unfix_edge_tangent_at_vertex } from './uid_continuity';
import { withProgress, withSpinner } from './state/spinner';
import { uil_add_joint_smoothness } from './uil_continuity';
import { is_connected_to_onshape, model_is_dirty, viewsize_to_vertex_diameter_ratio } from './state';
import { FAINT_STRENGTH } from './vars_and_data_structures';
import {
    delete_sketch_of_face,
    undelete_sketch_of_face,
    non_deleted_reference_sketches,
    vertex_is_for_sketch,
    edge_is_for_sketch,
    face_is_for_sketch,
    show_sketches,
    hide_sketches,
    stop_editing_sketches,
    start_editing_sketches,
} from './reference_sketches';
import { set_control, set_gui, set_shift, set_alt, shift_pressed } from './keypress';
import { lt } from '@vaaas/fpts/maths';
import * as operation_state from './state/operations';
import { show_read_coords } from '../../../../src/features/read-coordinates';
import { check_that_user_subscription_is_valid } from './user_authentication';
import Halo from './Halo';
import BBInfo from './BBInfo';
import EdgeProjectionData from './EdgeProjectionData';
import { clone_body, clone_edge, get_grid_point, plane_ray_intersect, optimal_face_triangles_for_drag, vertex_distance, get_inward_looking_edge_tangent_at_vertex } from './geometry_and_math';
import { pointer_normalised } from './pointer';
import { Uid_edge, Uid_face, Uid_face_triangle, Uid_ref_face, Uid_vertex } from './graph_objects';
import { clipping_model_editable, toggle_clipping, toggle_clipping_edit_model } from '../../../../src/features/clipping';
import { hide_indicated_vertices } from '../../../../src/features/indicate-vertices';
import { frontend_service_container } from '@phi/frontend/service-container';
import { move_pair_of_vertices_for_scaling, rescale_proportionately_changed} from '@phi/scale';
import { export_as_step_to_parasolid } from '@phi/export/frontend';
import { psdata } from './parasoliddata';

var uid_highlighted_object_o3d_id;

// Initializes required data structures and variables
export function uid_init(first_init) {
  // Initialize user interaction state
  uid_state = user_interaction_state_provider();

  cfcd = new CoreFunctionCallDescriptions();
  vertices_and_edges_visible = true;
  App.state.hidden_edges_visible = false;
  App.state.wireframe_visible = false;
  //coordinates_visible = false;

  reset_global_id();
  uid_ball_size = 1.0;
  uid_default_ball_size = 1.6;
  uid_ball_hardness = 0.0;
  curvature_coloring_factor = 1.0;
  uid_selected_manipulator = null;
  uid_selected_manipulator_type = "";
  uid_face_rendering_type = "solid";
  uid_default_face_material_name = "plastic";

  singled_out_faces_to_hide = [];
  singled_out_vertices_to_hide = [];
  singled_out_edges_to_hide = [];

  // Initialize the core
  init(first_init);

  do_not_load_reference_vertices = false;

  uid_drag_object = null;
  uid_drag_edge_for_vertex = null;
  uid_highlighted_object_o3d_id = -1;

  // Default grid size
  uid_grid_origin = new THREE.Vector3();
  App.state.grid_visible = false;
  set_grid_size(currently_selected_grid_button);

  uid_selected_edges = new HashTable();
  uid_selected_vertices = new HashTable();
  uid_selected_faces = new HashTable();
  uid_selected_edges_click_coordinates = new HashTable();
  currently_selected_objects = [];
  uid_selected_edge_click_point = null;

  uid_vertices = new HashTable();
  uid_edges = new HashTable();
  uid_faces = new HashTable();
  uid_bodies = new HashTable();
  uid_object3dIDToUid_object = new HashTable();

  uid_face_colors = new HashTable();
  uid_face_materials = new HashTable();

  uid_reference_vertices = new HashTable();
  uid_reference_faces = new HashTable();
  uid_reference_edges = new HashTable();
  uid_reference_bodies = new HashTable();
  uid_reference_vertex_coords_hash = new HashTable();
  uid_selected_reference_faces = [];

  uid_vertices_with_undeclared_tangencies = new HashTable();

  uid_materials_hash = new HashTable();

  user_modified_objects_hash = new HashTable();
  core_modified_objects_hash = new HashTable();
  core_entire_drag_modified_objects_hash = new HashTable();

  dom_text_elements = new HashTable();
  dom_edge_tangency_strings = new HashTable();

  scale_face_object = null;
  scale_edge_objects = new Array();
  scale_center = new THREE.Vector3();
  scale_factor = 1.0;
  uid_rigid_move_manipulator = null;
  uid_edge_tangent_1st_manipulator = null;
  uid_edge_tangent_2nd_manipulator = null;

  addEdge_draw_raycasting_origins = new Array();
  addEdge_draw_raycasting_new_vertex_origins = new Array();
  addEdge_draw_raycasting_direction = new THREE.Vector3();
  addEdge_draw_intersected_vertices = new Array();
  addEdge_draw_intersected_edges = new Array();
  addEdge_draw_intersected_faces = new Array();

  App.state.symmetry_plane_selected = false;
  symmetry_edges_list_first = new Array();
  symmetry_edges_list_second = new Array();

  App.state.mirror_plane_selected = false;

  uid_raynode = uid_objects_raynode;
  uid_rotmanip_center = new THREE.Vector3(0, 0, 0);

  drag_start_ray_origin = new THREE.Vector3();
  drag_start_hit_point = new THREE.Vector3();
  drag_start_movement_direction = "none";

  working_plane_base = new THREE.Vector3();
  working_plane_normal = new THREE.Vector3();
  working_plane_set = false;

  uid_rigid_move_manip_center = new THREE.Vector3(0, 0, 0);
  uid_rigid_move_manip_normal_vector_direction = new THREE.Vector3(1, 0, 0);
  //uid_rigid_move_manip_rotate_vector_direction = new THREE.Vector3( 0, 1, 0 );

  uid_manip_previous_raycaster_origin = new THREE.Vector3();
  uid_manip_current_raycaster_origin = new THREE.Vector3();
  uid_manip_previous_raycaster_direction = new THREE.Vector3();
  uid_manip_current_raycaster_direction = new THREE.Vector3();
  uid_snap_delta = new THREE.Vector3(0, 0, 0);

  uid_dragging_raycaster_previous_origin = new THREE.Vector3();

  uid_camera_click_origin = new THREE.Vector3();
  uid_camera_click_direction = new THREE.Vector3();
  uid_camera_click_up = new THREE.Vector3();

  pointer_normalised.x = 0;
  pointer_normalised.y = 0;

  App.state.stored_viewports.length = 0;

  uid_camera.left = (-aspectRatio * viewSize) / 2;
  uid_camera.right = (aspectRatio * viewSize) / 2;
  uid_camera.top = viewSize / 2;
  uid_camera.bottom = -viewSize / 2;

  // Make camera point towards origin from diagonal position
  uid_camera.position.set(40, -20, 10);
  uid_camera.up = new THREE.Vector3(0, 0, 1);
  var t = new THREE.Vector3(0, 0, 0);
  uid_camera.lookAt(t);
  adjust_camera_up_and_update_projection_matrix(uid_camera);
  //uid_camera.updateProjectionMatrix();

  uid_vertex_diameter = viewSize / viewsize_to_vertex_diameter_ratio.value;

  // Halo
  uid_halo = new Halo();

  // 0-thickness face hashtable
  zero_thickness_faces_hash = new HashTable();

  stl_triplets_hash = null;
  reference_object_for_attaching_surface = null;
  vertices_to_attach_move_directions = new HashTable();

  edges_for_which_to_display_tangency = [];

  // Loaded reference objects arrays
  App.state.imported_sketches = [];
  App.state.imported_partstudio_reference_models = [];
  App.state.imported_stl_models = [];

  // Panning and rotation state
  App.state.panning = false;
  App.state.rotating = false;
  App.state.allow_rotate = true;
  App.state.currently_rotating_viewpoint = false;

  // Initialize the list of shortcuts
  shortcuts = new Shortcuts();

  shortcuts.add_shortcut("all", "drag", "Click-drag vertex/edge", "Move freely", "Freely deform edge or move vertex");
  shortcuts.add_shortcut("all", "inflate", "Click-drag face", "Inflate/deflate", "Inflate or deflate face");
  shortcuts.add_shortcut("all", "elevator", "Shift-click-drag vertex/edge", "Move perpendicular", "Deform edge or move vertex perpendicular to surface");
  shortcuts.add_shortcut("all", "crosshair", "Shift-click-drag face", "Slice face", "Slice face with an edge");

  shortcuts.add_shortcut("#rigid-move-button", "M", "M", "Move rigidly", "Move selected object(s) rigidly");
  shortcuts.add_shortcut("#add-vertex-button", "V", "V", "Add vertex", "Add vertex on edge");
  shortcuts.add_shortcut("#add-vertex-button", "sV", "Shift+V", "Add vertex at middle", "Add vertex at middle of edge");
  shortcuts.add_shortcut("#draw-edge-button", "E", "E", "Draw edges", "Draw Edge(s) on face(s) or add edge between selected vertices");
  shortcuts.add_shortcut("#rigid-move-button", "manipulatorL", "L", "Local coordinates", "Switch manipulator to local coordinate system, perpendicular to surface");
  shortcuts.add_shortcut("#rigid-move-button", "manipulatorG", "G", "Global coordinates", "Switch manipulator to global coordinate system, along xyz axes");
  shortcuts.add_shortcut("#popout-button", "P", "P", "Pop-out", "Pop-out a face");
  shortcuts.add_shortcut("#carve-button", "C", "C", "Carve", "Carve body, slicing through it");

  shortcuts.add_shortcut("sketches", "sketchup", "Up", "Scale image up", "Scale up the selected images");
  shortcuts.add_shortcut("sketches", "sketchdown", "Down", "Scale image down", "Scale down the selected images");
  shortcuts.add_shortcut("sketches", "sketchfliph", "H", "Flip image horizontal", "Flip horizontally the selected images");
  shortcuts.add_shortcut("sketches", "sketchflipv", "V", "Flip image vertical", "Flip vertically the selected images");
  shortcuts.add_shortcut("sketches", "sketchmovex", "X", "Move image along X", "Move along X the selected images");
  shortcuts.add_shortcut("sketches", "sketchmovey", "Y", "Move image along Y", "Move along Y the selected images");
  shortcuts.add_shortcut("sketches", "sketchmovez", "Z", "Move image along Z", "Move along Z the selected images");

  // Switch on lights and set material
  create_reflection_cube(urls.colors);
  switch_to_material("plastic");

  switch_to_lights(App.settings.lights);
  switch_to_background(App.settings.background);

  // Initialize the axis system.
  initialize_axes();
  hide_axes();
  //initialize_orthogonal_planes();
  initialize_symmetry_plane();
  initialize_mirror_plane();

  // Zoom out a bit
  zoom(-1, null, 3.0);

  debug_vars = [];
  request_setting_button_states();
  animate();

  uil_set_display_face_triangulation_res_for_drag(optimal_face_triangles_for_drag([], [], "initialize"));
  frontend_service_container().edge_tightness.set_loose_edges();

  if (!first_init || (should_skip_tutorial() && !App.state.is_demo))
    App.state.modal.selectModelModal = true;
  else if (App.state.is_demo)
    App.state.modal.tutorialsModalDemo = true;
  else
    App.state.modal.tutorialsModal = true;
}

export function activate_transparent_reference() {
  uid_reference_faces.values().forEach(function (f) {
    set_default_face_color(f.uid_object.id);
    f.uid_object.object3d.material.opacity = 0.5;
    f.uid_object.object3d.material.transparent = true;
    f.uid_object.object3d.material.shininess = 80;
  });
  uid_reference_edges.values().forEach(function (e) {
    set_default_edge_color(e.uid_object.id);
  });
  uid_reference_vertices.values().forEach(function (e) {
    set_default_vertex_color(e.uid_object.id);
  });
  animate();
}

export function deactivate_transparent_reference() {
  uid_reference_faces.values().forEach(function (f) {
    set_default_face_color(f.uid_object.id);
    f.uid_object.object3d.material.opacity = 1;
    f.uid_object.object3d.material.transparent = false;
    f.uid_object.object3d.material.shininess = 30;
  });
  uid_reference_edges.values().forEach(function (e) {
    set_default_edge_color(e.uid_object.id);
  });
  uid_reference_vertices.values().forEach(function (e) {
    set_default_vertex_color(e.uid_object.id);
  });
  animate();
}

export function uid_add_vector_to_scene(bx, by, bz, dx, dy, dz, l, r, g, b) {
  if (!uid_debug_objs_visible) {
    scene.add(uid_debug_obj_raynode);
    uid_debug_objs_visible = true;
  }
  var fv_material = new THREE.MeshBasicMaterial({ color: 0x777777 });
  fv_material.color.setRGB(r/256,g/256,b/256);
  var fv_geometry = new THREE.SphereGeometry(uid_vertex_diameter / 4.0, 8, 6);
  fv_geometry.dynamic = true;
  var fv_mesh = new THREE.Mesh(fv_geometry, fv_material);
  // Position the object in 3D space
  fv_mesh.position.set(bx, by, bz);
  fv_mesh.name = "fence_vector";
  uid_debug_obj_raynode.add(fv_mesh);

  var l_geometry = new THREE.Geometry();
  l_geometry.dynamic = true;
  l_geometry.vertices = new Array();
  var p1 = new THREE.Vector3(bx, by, bz);
  var p2 = new THREE.Vector3(bx+l*dx, by+l*dy, bz+l*dz);
  l_geometry.vertices.push(p1);
  l_geometry.vertices.push(p2);
  var l_material = new THREE.LineBasicMaterial({
    color: 0x555555,
    linewidth: 2
  });
  l_material.color.setRGB(r/256,g/256,b/256);
  var l_mesh = new THREE.Line(l_geometry, l_material, THREE.LineStrip);
  uid_debug_obj_raynode.add(l_mesh);
  animate();
}

export function uid_add_point_to_scene(px, py, pz, r, g, b) {
  if (!uid_debug_objs_visible) {
    scene.add(uid_debug_obj_raynode);
    uid_debug_objs_visible = true;
  }
  var p_material = new THREE.MeshBasicMaterial({ color: 0x777777 });
  p_material.color.setRGB(r/256,g/256,b/256);
  var p_geometry = new THREE.SphereGeometry(uid_vertex_diameter / 4.0, 8, 6);
  p_geometry.dynamic = true;
  var p_mesh = new THREE.Mesh(p_geometry, p_material);
  // Position the object in 3D space
  p_mesh.position.set(px, py, pz);
  p_mesh.name = "interp_point";
  uid_debug_obj_raynode.add(p_mesh);
  animate();
}

export function uid_add_points_polyline_to_scene(point_vectors, r, g, b, point_size="normal") {
  if (!uid_debug_objs_visible) {
    scene.add(uid_debug_obj_raynode);
    uid_debug_objs_visible = true;
  }

  let p_material = new THREE.MeshBasicMaterial({ color: 0x777777 });
  p_material.color.setRGB(r/256,g/256,b/256);
  let e_material = new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 2 });
  e_material.color.setRGB(r/256,g/256,b/256);

  for (let i = 0; i < point_vectors.length; i++) {
    if (point_size != "no") {
      if (point_size == "normal")
        var p_geometry = new THREE.SphereGeometry(uid_vertex_diameter / 4.0, 8, 6);
      else
        var p_geometry = new THREE.SphereGeometry(uid_vertex_diameter / 16.0, 8, 6);
      p_geometry.dynamic = true;
      var p_mesh = new THREE.Mesh(p_geometry, p_material);
      // Position the object in 3D space
      p_mesh.position.set(point_vectors[i].x, point_vectors[i].y, point_vectors[i].z);
      p_mesh.name = "cpoint_" + i;
      uid_debug_obj_raynode.add(p_mesh);
    }
    if (i > 0) {
      var e_geometry = new THREE.Geometry();
      e_geometry.dynamic = true;
      e_geometry.vertices = new Array();
      e_geometry.vertices.push(point_vectors[i-1]);
      e_geometry.vertices.push(point_vectors[i]);
      var e_mesh = new THREE.Line(e_geometry, e_material, THREE.LineStrip);
      uid_debug_obj_raynode.add(e_mesh);
    }
  }
  animate();
}

export function show_all_reference_partstudio_vertices() {
  if (App.state.imported_partstudio_reference_models.length == 0)
    return;

  if (!App.state.showing_reference_partstudio_models)
    return;

  // First we'll make all reference partstudio models visible, in case they are not.
  show_reference_partstudio_model();

  uid_reference_partstudio_raynode.add(uid_ref_partstudio_vertex_raynode);
  resize_all_reference_vertices_for_zoom(0,0,0);
  uid_state.showing_reference_partstudio_vertices = true;
}

export function hide_all_reference_partstudio_vertices() {
  uid_reference_partstudio_raynode.remove(uid_ref_partstudio_vertex_raynode);
  uid_state.showing_reference_partstudio_vertices = false;
}

export function show_all_reference_stl_vertices() {
  if (App.state.imported_stl_models.length == 0)
    return;

  if (!App.state.showing_reference_stl)
      return;

  // First we'll make all reference STL models visible, in case they are not.
  show_reference_stl_model();

  uid_reference_stl_raynode.add(uid_ref_stl_vertex_raynode);
  resize_all_reference_vertices_for_zoom(0,0,0);
  uid_state.showing_reference_stl_vertices = true;
}

export function hide_all_reference_stl_vertices() {
  uid_reference_stl_raynode.remove(uid_ref_stl_vertex_raynode);
  uid_state.showing_reference_stl_vertices = false;
}

export function uid_add_vertex_geometry(uidv, x, y, z, is_reference, dont_show_reference, source_if_reference) {
  uid_vertex_diameter = viewSize / viewsize_to_vertex_diameter_ratio.value;
  var vertex_geometry = new THREE.SphereGeometry( uid_vertex_diameter / vertex_scaling_factor, 8, 6);
  vertex_geometry.dynamic = true;
  if (is_reference) {
    var r, g, b;
    if (source_if_reference == "stl") {
      r = ref_stl_face_color_r;
      g = ref_stl_face_color_g;
      b = ref_stl_face_color_b;
    }
    else if (source_if_reference == "parasolid") {
      r = ref_parasolid_face_color_r;
      g = ref_parasolid_face_color_g;
      b = ref_parasolid_face_color_b;
    }
    else { // partstudio
      r = ref_partstudio_face_color_r;
      g = ref_partstudio_face_color_g;
      b = ref_partstudio_face_color_b;
    }
    var vertex_material = new THREE.MeshBasicMaterial({ color: rgbToHex(r, g, b), opacity: 1 });
  }
  else {
    var vertex_material = new THREE.MeshBasicMaterial({ color: 0xffffff, opacity: 1 });
  }
  var vertex_mesh = new THREE.Mesh(vertex_geometry, vertex_material);
  // Position the vertex in 3D space
  vertex_mesh.position.set(x, y, z);
  uidv.setObject3d(vertex_mesh, is_reference, dont_show_reference, source_if_reference);
  if (!is_reference)
    set_default_vertex_color(uidv.id);
}

export function uid_add_edge_geometry(uide, points, is_reference, source_if_reference) {
  // Build the interactive edge. This is transparent so the user doesn't see it, but it responds to raytracing so the user can interact with it and deform it.
  var interactive_edge_geometry = new THREE.Geometry();
  interactive_edge_geometry.dynamic = true;
  for (var i = 0; i < points.length; i++)
    interactive_edge_geometry.vertices.push(points[i]);

  if (!is_reference)
    free_array(points);

  if (is_reference)
    var interactive_edge_material =  new THREE.LineBasicMaterial({ color: 0xFF0000, linewidth: 2});
  else
    var interactive_edge_material =  new THREE.LineBasicMaterial({ color: 0xffffff, linewidth: 2});

  var interactive_edge_mesh = new THREE.Line(interactive_edge_geometry, interactive_edge_material);

  // Build the visible edge. This is based on the LineDashed library, it is visible, but doesn't respond to raytracing.
  // Depending on whether the edge is linear we give it a different appearance.

  var resolution = new THREE.Vector2(App.state.canvas.width, App.state.canvas.height);

  var visible_line_mesh2 = new LineGeometry();
  var positions = [];
  interactive_edge_geometry.vertices.forEach(function(v){
    positions.push(v.x);
    positions.push(v.y);
    positions.push(v.z);
  });

  visible_line_mesh2.setPositions( positions );

  free_array(positions);

  var visible_edge_material2 = new LineMaterial( {
    color: 0xffffff,
    linewidth: default_line_width, // in pixels
    vertexColors: true,
    resolution:  new THREE.Vector2( 600, 400),
    dashed: false,
    transparent: false,
    opacity: 1
  } );

  var visible_edge_mesh2 = new Line2( visible_line_mesh2, visible_edge_material2 );
  visible_edge_mesh2.computeLineDistances();
  visible_edge_mesh2.scale.set(1,1,1);

  interactive_edge_mesh.renderOrder = 998;
  interactive_edge_mesh.depthTest = false;
  visible_edge_mesh2.renderOrder = 999;
  visible_edge_mesh2.depthTest = true;

  uide.setObject3d(interactive_edge_mesh, is_reference, source_if_reference);

  uide.set_visible_edge_mesh(visible_edge_mesh2, is_reference, source_if_reference);

  if (uid_selected_edges.hasItem(uide.id)) {
    uid_selected_edges.removeItem(uide.id);
    uid_select_object(uide.id, false)
  }

  set_default_edge_color(uide.id);

  if (App.state.currently_hiding_edges && uil_is_object_visible(uide.id)) {
    display_toggle_edge_visibility(true);
  }

}

export function uid_add_face_geometry(uidf, points, triangles, is_reference, is_for_sketch, source_if_reference) {
  var surf_geometry = new THREE.BufferGeometry();
  var idxCount = 0;

  // Read face geometry from core to get face normal.
  var offset_amount = 0.0;
  var normal = new THREE.Vector3(1.0, 0.0, 0.0);
  if (!is_reference) {
    var center = new THREE.Vector3();
    var wv_radius = new WrappedValue(0);
    var wv_flatness = new WrappedValue(0);
    var characteristic_dir_coords = new THREE.Vector3();
    uil_get_face_geometry(uidf.id, center, normal, wv_radius, characteristic_dir_coords, wv_flatness);
    normal.normalize();
    if (zero_thickness_faces_hash && zero_thickness_faces_hash.hasItem(uidf.id)) {
      normal.multiplyScalar(wv_radius.v/20000.0);
      zero_thickness_faces_hash.setItem(uidf.id, normal);
    }
  }

  if (!should_display_mirror_side() || is_reference || is_for_sketch) {
    var faceIndices = new Uint32Array( triangles.length * 3 );
    var vertices = new Float32Array( points.length * 3 );
  }
  else {
    var faceIndices = new Uint32Array( triangles.length * 3 * 2 );
    var vertices = new Float32Array( points.length * 3 * 2);
  }

  surf_geometry.setIndex( new THREE.BufferAttribute( faceIndices, 1 ) );
  surf_geometry.setAttribute( 'position', new THREE.BufferAttribute( vertices, 3 ).setUsage( THREE.DynamicDrawUsage ) );

  free_array(faceIndices);
  free_array(vertices);

  for (var i = 0; i < points.length; i++) {
    var posIdx = i * 3;
    surf_geometry.attributes.position.array[ posIdx ]  = points[i].x + normal.x * offset_amount;
    surf_geometry.attributes.position.array[ posIdx + 1 ]  = points[i].y + normal.y * offset_amount;
    surf_geometry.attributes.position.array[ posIdx + 2 ]  = points[i].z + normal.z * offset_amount;

    if (should_display_mirror_side() && !is_reference && !is_for_sketch) {
      var new_point = mirror_point(points[i], mirror_plane_base, mirror_plane_normal);

      surf_geometry.attributes.position.array[ points.length * 3 + posIdx ]  = new_point.x + normal.x * offset_amount;
      surf_geometry.attributes.position.array[ points.length * 3 + posIdx + 1 ]  = new_point.y + normal.y * offset_amount;
      surf_geometry.attributes.position.array[ points.length * 3 + posIdx + 2 ]  = new_point.z + normal.z * offset_amount;
    }
  }

  surf_geometry.attributes.position.needsUpdate = true;

  for (var i = 0; i < triangles.length; i++) {
    surf_geometry.index.array[ idxCount     ] = triangles[i].index1; // right-bottom
    surf_geometry.index.array[ idxCount + 1 ] = triangles[i].index2;
    surf_geometry.index.array[ idxCount + 2 ] = triangles[i].index3;
    if (should_display_mirror_side() && !is_reference && !is_for_sketch) {
      surf_geometry.index.array[ triangles.length * 3 + idxCount     ] = triangles[i].index1 + points.length; // right-bottom
      surf_geometry.index.array[ triangles.length * 3 + idxCount + 1 ] = triangles[i].index2 + points.length;
      surf_geometry.index.array[ triangles.length * 3 + idxCount + 2 ] = triangles[i].index3 + points.length;
    }
    idxCount += 3;
  }

  if (is_reference) {
    var ms = create_materials_for_face(uidf, is_reference);
    var surf_material = ms[0];
    var surf_mesh = new THREE.Mesh(surf_geometry, surf_material);
    surf_mesh.renderOrder = 1;
    surf_mesh.castShadow = true;
  }
  else {
    var surf_materials = create_materials_for_face(uidf, is_reference);
    surf_geometry.clearGroups();
    surf_geometry.addGroup( 0, triangles.length * 3 , 0 );
    surf_geometry.addGroup( 0, triangles.length * 3 , 1 );
    if (should_display_mirror_side()) {
      // Add groups and materials for mirror side.
      surf_geometry.addGroup( triangles.length * 3, triangles.length * 3, 3 );
      surf_geometry.addGroup( triangles.length * 3, triangles.length * 3, 2 );
    }
    var surf_mesh = new THREE.Mesh(surf_geometry, surf_materials);
    surf_mesh.renderOrder = 1;
    surf_mesh.castShadow = true;
  }

  surf_geometry.computeBoundingSphere();
  surf_geometry.computeFaceNormals();
  surf_geometry.computeVertexNormals();
  uidf.setObject3d(surf_mesh, source_if_reference);

  if (!is_reference) {
    set_default_face_color(uidf.id);
  }

  // Add to raytracing
  if (is_reference) {
    if (source_if_reference == "partstudio")
      uid_ref_partstudio_faces_raynode.add(surf_mesh);
    else if (source_if_reference == "parasolid")
      uid_ref_parasolid_faces_raynode.add(surf_mesh);
    else if (source_if_reference == "stl")
      uid_ref_stl_faces_raynode.add(surf_mesh);
  }
  else if (face_is_for_sketch(uidf.id)) {
    if (!mustNotDisplayFace(uidf.id))
      current_scene_raynode_for_sketch_objects().add(surf_mesh);
    else
      uid_hidden_sketch_objects_raynode.add(surf_mesh);
  }
  else {
    if (!mustNotDisplayFace(uidf.id))
      uid_objects_raynode.add(surf_mesh);
  }
}

export function display_add_STL_face_triangles(id, points, triangles) {
  var stl_surf_geometry = new THREE.Geometry();

  stl_surf_geometry.vertices = new Array();
  for (var i = 0; i < points.length; i++) {
    stl_surf_geometry.vertices.push(points[i]);
  }

  stl_surf_geometry.faces = new Array();
  for (var i = 0; i < triangles.length; i++) {
    stl_surf_geometry.faces.push(
      new THREE.Face3(
        triangles[i].index1,
        triangles[i].index2,
        triangles[i].index3
      )
    );
  }

  stl_surfaces_hash.setItem(id, stl_surf_geometry);
}

export function uid_calculate_viewport_object_bounding_box () {
  let vs = uid_vertices.keys()
    .filter(vid => !mustNotDisplayVertex(vid))
    .filter(vid => !vertex_is_for_sketch(vid))
    .filter(vid => vertex_is_in_viewport(vid));
  let es = uid_edges.keys()
    .filter(eid => !mustNotDisplayEdge(eid))
    .filter(eid => !edge_is_for_sketch(eid))
    .filter(eid => edge_is_in_viewport(eid));
  let fs = uid_faces.keys()
    .filter(fid => !mustNotDisplayFace(fid))
    .filter(fid => !face_is_for_sketch(fid))
    .filter(fid => face_is_in_viewport(fid));

  if (vs.length == 0 && es.length == 0 && fs.length == 0)
    return uid_calculate_global_bounding_box(true, false, false);

  return uid_calculate_bounding_box(vs, es, fs, true, true, false);
}

export function uid_calculate_global_bounding_box(fast_and_approximate, ignore_reference_objects = false, only_part_in_viewport = false) {
  return uid_calculate_bounding_box(uid_vertices.keys(), uid_edges.keys(), uid_faces.keys(), fast_and_approximate, ignore_reference_objects, only_part_in_viewport);
}

export function uid_calculate_bounding_box_of_selected_objects(fast_and_approximate) {
  return uid_calculate_bounding_box(uid_selected_vertices.keys(), uid_selected_edges.keys(), uid_selected_faces.keys(), fast_and_approximate, true, false);
}

export function uid_calculate_bounding_box_of_objects(vertices, edges, faces, fast_and_approximate) {
  return uid_calculate_bounding_box(vertices, edges, faces, fast_and_approximate, true, false);
}

function vertex_is_in_viewport(vid) {
  return point_is_in_viewport(uid_vertices.getItem(vid).object3d.position);
}

function edge_is_in_viewport(eid) {
  if (point_is_in_viewport(uil_get_vertices_of_edge(eid)[0]) || point_is_in_viewport(uil_get_vertices_of_edge(eid)[1]))
    return true;

  var at_least_one_sample_is_in = false;
  const e = uid_edges.getItem(eid);
  let l = e.object3d.geometry.vertices.length;
  let s = Math.floor(l/10.0);
  for (var i = 0; i < l; i += s) {
    if (point_is_in_viewport(e.object3d.geometry.vertices[i]))
      at_least_one_sample_is_in = true;
  }
  return at_least_one_sample_is_in;
}

function face_is_in_viewport(fid) {
  var at_least_one_is_in = false;
  vertices_of_face(fid).forEach(function(vid) {
    if (vertex_is_in_viewport(vid))
      at_least_one_is_in = true;
  });
  if (at_least_one_is_in)
    return true;

  uil_get_edges_of_face(fid).forEach(function(eid){
    if (edge_is_in_viewport(eid))
      at_least_one_is_in = true;
  });
  if (at_least_one_is_in)
    return true;

  let f = uid_faces.getItem(fid);

  var l = f.object3d.geometry.attributes.position.array.length / 3.0;
  var s = Math.floor(l/100.0);

  for (var i = 0; i < l; i += s) {
    if (point_is_in_viewport(
      f.object3d.geometry.attributes.position.array[ i * 3 ],
      f.object3d.geometry.attributes.position.array[ i * 3 + 1 ],
      f.object3d.geometry.attributes.position.array[ i * 3 + 2 ]))
      at_least_one_is_in = true;
  }
  return at_least_one_is_in;
}

export function point_is_in_viewport(p) {
  var pv = from_3d_coordinates_to_2d_screen_coordinates(p);
  if (-1.0 < pv.x && 1.0 > pv.x && -1.0 < pv.y && 1.0 > pv.y)
      return true;
    else
      return false;
  }

export function uid_calculate_bounding_box(vertices, edges, faces, fast_and_approximate, ignore_reference_objects = false,
  only_part_in_viewport = false) {
    if (vertices.length == 0 && edges.length == 0 && faces.length == 0 &&
        (ignore_reference_objects || uid_reference_vertices.length == 0))
        // No phi objects present, return default
        return new BBInfo(
            (-1) * viewSize / 5,
            viewSize / 5,
            (-1) * viewSize / 5,
            viewSize / 5,
            (-1) * viewSize / 5,
            viewSize / 5,
        );


    // Then we calculate a global bounding box.
    let bb = pipe(
        [
            vertices,
            edges.flatMap(uil_get_vertices_of_edge),
            faces.flatMap(vertices_of_face),
        ],
        flatten,
        filter(x => uid_vertices.hasItem(x)),
        filter(uil_is_object_visible),
        filter(x => !only_part_in_viewport || vertex_is_in_viewport(x)),
        map(x => uid_vertices.getItem(x).object3d.position),
        foldl(bb => v => {
            if (v.x < bb.minx)
                bb.minx = v.x;
            if (v.x > bb.maxx)
                bb.maxx = v.x;
            if (v.y < bb.miny)
                bb.miny = v.y;
            if (v.y > bb.maxy)
                bb.maxy = v.y;
            if (v.z < bb.minz)
                bb.minz = v.z;
            if (v.z > bb.maxz)
                bb.maxz = v.z;
            return bb;
        }, {
            minx: Infinity,
            miny: Infinity,
            minz: Infinity,
            maxx: -Infinity,
            maxy: -Infinity,
            maxz: -Infinity,
        }),
    );

    // Also consider reference vertices, if they have been loaded, otherwise reference faces.
    if (!ignore_reference_objects) {
        if (!do_not_load_reference_vertices)
            bb = pipe(
                uid_reference_vertices.values(),
                filter(x => x.uid_object && x.uid_object.object3d),
                map(x => x.uid_object.object3d.position),
                foldl(bb => v => {
                    if (v.x < bb.minx)
                        bb.minx = v.x;
                    if (v.x > bb.maxx)
                        bb.maxx = v.x;
                    if (v.y < bb.miny)
                        bb.miny = v.y;
                    if (v.y > bb.maxy)
                        bb.maxy = v.y;
                    if (v.z < bb.minz)
                        bb.minz = v.z;
                    if (v.z > bb.maxz)
                        bb.maxz = v.z;
                    return bb;
                }, { ...bb }),
            );
        else
            bb = pipe(
                uid_reference_faces.values(),
                filter(f => f.uid_object && f.uid_object.object3d && f.uid_object.object3d.geometry),
                map(f => f.uid_object.object3d.geometry.boundingSphere),
                foldl(bb => f => {
                    if (f.center.x - f.radius < bb.minx)
                        bb.minx = f.center.x - f.radius;
                    if (f.center.x + f.radius > bb.maxx)
                        bb.maxx = f.center.x + f.radius;
                    if (f.center.y - f.radius < bb.miny)
                        bb.miny = f.center.y - f.radius;
                    if (f.center.y + f.radius > bb.maxy)
                        bb.maxy = f.center.y + f.radius;
                    if (f.center.z - f.radius < bb.minz)
                        bb.minz = f.center.z - f.radius;
                    if (f.center.z + f.radius > bb.maxz)
                        bb.maxz = f.center.z + f.radius;
                    return bb;
                }, { ...bb }),
            );
    }

    // If we want more precise bb, then we must consider all edge and face samples as well.
    if (!fast_and_approximate)
        bb = pipe(
            [
                pipe(
                    edges,
                    map(x => uid_edges.getItem(x)),
                    filter(x => uil_is_object_visible(x.id)),
                    bind(x => x.object3d.geometry.vertices),
                ),
                pipe(
                    faces,
                    map(x => uid_faces.getItem(x)),
                    filter(x => uil_is_object_visible(x.id)),
                    bind(x => x.object3d.geometry.attributes.position.array),
                ),
            ],
            flatten,
            filter(x => !only_part_in_viewport || point_is_in_viewport(x)),
            foldl(bb => v => {
                if (v.x < bb.minx)
                    bb.minx = v.x;
                if (v.x > bb.maxx)
                    bb.maxx = v.x;
                if (v.y < bb.miny)
                    bb.miny = v.y;
                if (v.y > bb.maxy)
                    bb.maxy = v.y;
                if (v.z < bb.minz)
                    bb.minz = v.z;
                if (v.z > bb.maxz)
                    bb.maxz = v.z;
                return bb;
            }, { ...bb })
        );

    const bb_info = new BBInfo(bb.minx, bb.maxx, bb.miny, bb.maxy, bb.minz, bb.maxz);

    // If only one object was selected, bb.dimension will be 0.
    if (bb.dimension == 0)
        bb.dimension = viewSize / 5;

    return bb_info;
}


export function uid_calculate_reference_bounding_box() {

  if (uid_reference_vertices.length == 0 && uid_reference_faces.length == 0 && uid_reference_edges.length == 0) {
    // No reference objects present, return default
    var bb = new BBInfo( (-1) * viewSize / 5, viewSize / 5, (-1) * viewSize / 5, viewSize / 5, (-1) * viewSize / 5, viewSize / 5,);
    return bb;
  }

  // Then we calculate a global bounding box.
  var minx = 10000000;
  var miny = 10000000;
  var minz = 10000000;
  var maxx = -10000000;
  var maxy = -10000000;
  var maxz = -10000000;

  if (!do_not_load_reference_vertices) {
    uid_reference_vertices.values().forEach(function(v) {
      if (v.uid_object && v.uid_object.object3d) {
        if (v.uid_object.object3d.position.x < minx) {
          minx = v.uid_object.object3d.position.x;
        }
        if (v.uid_object.object3d.position.x > maxx) {
          maxx = v.uid_object.object3d.position.x;
        }
        if (v.uid_object.object3d.position.y < miny) {
          miny = v.uid_object.object3d.position.y;
        }
        if (v.uid_object.object3d.position.y > maxy) {
          maxy = v.uid_object.object3d.position.y;
        }
        if (v.uid_object.object3d.position.z < minz) {
          minz = v.uid_object.object3d.position.z;
        }
        if (v.uid_object.object3d.position.z > maxz) {
          maxz = v.uid_object.object3d.position.z;
        }
      }
    });
  }
  else {
    uid_reference_faces.values().forEach(function(f) {
      if (f.uid_object && f.uid_object.object3d && f.uid_object.object3d.geometry) {
        if (f.uid_object.object3d.geometry.boundingSphere.center.x - f.uid_object.object3d.geometry.boundingSphere.radius < minx)
          minx = f.uid_object.object3d.geometry.boundingSphere.center.x - f.uid_object.object3d.geometry.boundingSphere.radius;
        if (f.uid_object.object3d.geometry.boundingSphere.center.x + f.uid_object.object3d.geometry.boundingSphere.radius > maxx)
          maxx = f.uid_object.object3d.geometry.boundingSphere.center.x + f.uid_object.object3d.geometry.boundingSphere.radius;
        if (f.uid_object.object3d.geometry.boundingSphere.center.y - f.uid_object.object3d.geometry.boundingSphere.radius < miny)
          miny = f.uid_object.object3d.geometry.boundingSphere.center.y - f.uid_object.object3d.geometry.boundingSphere.radius;
        if (f.uid_object.object3d.geometry.boundingSphere.center.y + f.uid_object.object3d.geometry.boundingSphere.radius > maxy)
          maxy = f.uid_object.object3d.geometry.boundingSphere.center.y + f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.z - f.uid_object.object3d.geometry.boundingSphere.radius < minz)
          minz = f.uid_object.object3d.geometry.boundingSphere.center.z - f.uid_object.object3d.geometry.boundingSphere.radius;
        if (f.uid_object.object3d.geometry.boundingSphere.center.z + f.uid_object.object3d.geometry.boundingSphere.radius > maxz)
          maxz = f.uid_object.object3d.geometry.boundingSphere.center.z + f.uid_object.object3d.geometry.boundingSphere.radius;
      }
    });
  }

  var bb = new BBInfo(minx, maxx, miny, maxy, minz, maxz);

  // Now calculate 2 points A and B within the body on either side of center

  var main_axis;
  if (maxx - minx > maxy - miny  && maxx - minx > maxz - minz)
    main_axis = "x";
  else if (maxy - miny > maxx - minx  && maxy - miny > maxz - minz)
    main_axis = "y";
  else
    main_axis = "z";

  // Re-calculate for pointA and pointB.
  var minAx = 10000000;
  var minAy = 10000000;
  var minAz = 10000000;
  var maxAx = -10000000;
  var maxAy = -10000000;
  var maxAz = -10000000;

  var minBx = 10000000;
  var minBy = 10000000;
  var minBz = 10000000;
  var maxBx = -10000000;
  var maxBy = -10000000;
  var maxBz = -10000000;

  if (!do_not_load_reference_vertices) {
    uid_reference_vertices.values().forEach(function(v) {
      if ((main_axis == "x" && v.uid_object && v.uid_object.object3d && v.uid_object.object3d.position.x > bb.centerx) ||
          (main_axis == "y" && v.uid_object && v.uid_object.object3d && v.uid_object.object3d.position.y > bb.centery) ||
          (main_axis == "z" && v.uid_object && v.uid_object.object3d && v.uid_object.object3d.position.z > bb.centerz)) {
        if (v.uid_object && v.uid_object.object3d) {
          if (v.uid_object.object3d.position.x < minAx) {
            minAx = v.uid_object.object3d.position.x;
          }
          if (v.uid_object.object3d.position.x > maxAx) {
            maxAx = v.uid_object.object3d.position.x;
          }
          if (v.uid_object.object3d.position.y < minAy) {
            minAy = v.uid_object.object3d.position.y;
          }
          if (v.uid_object.object3d.position.y > maxAy) {
            maxAy = v.uid_object.object3d.position.y;
          }
          if (v.uid_object.object3d.position.z < minAz) {
            minAz = v.uid_object.object3d.position.z;
          }
          if (v.uid_object.object3d.position.z > maxAz) {
            maxAz = v.uid_object.object3d.position.z;
          }
        }
      }
      else {
        if (v.uid_object && v.uid_object.object3d) {
          if (v.uid_object.object3d.position.x < minBx) {
            minBx = v.uid_object.object3d.position.x;
          }
          if (v.uid_object.object3d.position.x > maxBx) {
            maxBx = v.uid_object.object3d.position.x;
          }
          if (v.uid_object.object3d.position.y < minBy) {
            minBy = v.uid_object.object3d.position.y;
          }
          if (v.uid_object.object3d.position.y > maxBy) {
            maxBy = v.uid_object.object3d.position.y;
          }
          if (v.uid_object.object3d.position.z < minBz) {
            minBz = v.uid_object.object3d.position.z;
          }
          if (v.uid_object.object3d.position.z > maxBz) {
            maxBz = v.uid_object.object3d.position.z;
          }
        }
      }
    });
  }
  else {
    uid_reference_faces.values().forEach(function(f) {
      if (f.uid_object && f.uid_object.object3d && f.uid_object.object3d.geometry) {
        if ((main_axis == "x" && f.uid_object.object3d.geometry.boundingSphere.center.x > bb.centerx) ||
            (main_axis == "y" && f.uid_object.object3d.geometry.boundingSphere.center.y > bb.centery) ||
            (main_axis == "z" && f.uid_object.object3d.geometry.boundingSphere.center.z > bb.centerz)) {
          if (f.uid_object.object3d.geometry.boundingSphere.center.x - f.uid_object.object3d.geometry.boundingSphere.radius < minAx)
            minAx = f.uid_object.object3d.geometry.boundingSphere.center.x - f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.x + f.uid_object.object3d.geometry.boundingSphere.radius > maxAx)
            maxAx = f.uid_object.object3d.geometry.boundingSphere.center.x + f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.y - f.uid_object.object3d.geometry.boundingSphere.radius < minAy)
            minAy = f.uid_object.object3d.geometry.boundingSphere.center.y - f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.y + f.uid_object.object3d.geometry.boundingSphere.radius > maxAy)
            maxAy = f.uid_object.object3d.geometry.boundingSphere.center.y + f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.z - f.uid_object.object3d.geometry.boundingSphere.radius < minAz)
            minAz = f.uid_object.object3d.geometry.boundingSphere.center.z - f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.z + f.uid_object.object3d.geometry.boundingSphere.radius > maxAz)
            maxAz = f.uid_object.object3d.geometry.boundingSphere.center.z + f.uid_object.object3d.geometry.boundingSphere.radius;
        }
        else {
          if (f.uid_object.object3d.geometry.boundingSphere.center.x - f.uid_object.object3d.geometry.boundingSphere.radius < minBx)
            minBx = f.uid_object.object3d.geometry.boundingSphere.center.x - f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.x + f.uid_object.object3d.geometry.boundingSphere.radius > maxBx)
            maxBx = f.uid_object.object3d.geometry.boundingSphere.center.x + f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.y - f.uid_object.object3d.geometry.boundingSphere.radius < minBy)
            minBy = f.uid_object.object3d.geometry.boundingSphere.center.y - f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.y + f.uid_object.object3d.geometry.boundingSphere.radius > maxBy)
            maxBy = f.uid_object.object3d.geometry.boundingSphere.center.y + f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.z - f.uid_object.object3d.geometry.boundingSphere.radius < minBz)
            minBz = f.uid_object.object3d.geometry.boundingSphere.center.z - f.uid_object.object3d.geometry.boundingSphere.radius;
          if (f.uid_object.object3d.geometry.boundingSphere.center.z + f.uid_object.object3d.geometry.boundingSphere.radius > maxBz)
            maxBz = f.uid_object.object3d.geometry.boundingSphere.center.z + f.uid_object.object3d.geometry.boundingSphere.radius;
        }
      }
    });
  }
  bb.set_pointA((maxAx+minAx)/2.0, (maxAy+minAy)/2.0, (maxAz+minAz)/2.0);
  bb.set_pointB((maxBx+minBx)/2.0, (maxBy+minBy)/2.0, (maxBz+minBz)/2.0);

  return bb;
}

export function uid_calculate_reference_points_subset (bb) {

  var points = [];
  var rad = bb.dimension / 25.0;

  uid_reference_faces.values().forEach(function(rf) {
    rf.triangle_vertices.forEach(function(v) {
      var reject_this_point = false;
      points.forEach(function(p) {
        if (!reject_this_point && vector3.point_point_distance(p, v) < rad)
          reject_this_point = true;
      });
      if (!reject_this_point)
        points.push(v);
    });
  });

  /*
  if (!do_not_load_reference_vertices) {
    uid_reference_vertices.values().forEach(function(v) {
      var reject_this_point = false;
      points.forEach(function(p) {
        if (!reject_this_point && v.uid_object && v.uid_object.object3d && point_point_distance(p, v.uid_object.object3d.position) < rad)
          reject_this_point = true;
      });
      if (!reject_this_point)
        points.push(v.uid_object.object3d.position);
    });
  }
  else {
    uid_reference_faces.values().forEach(function(f) {
      var reject_this_point = false;
      points.forEach(function(p) {
        if (!reject_this_point && f.uid_object && f.uid_object.object3d && f.uid_object.object3d.geometry &&
             point_point_distance(p, f.uid_object.object3d.geometry.boundingSphere.center) < rad)
          reject_this_point = true;
      });
      if (!reject_this_point)
        points.push(f.uid_object.object3d.geometry.boundingSphere.center);
    });
  }
  */

  return points;
}


export function uid_add_reference_vertex(refvObj, superimposed) {
  var id = 100000 + get_global_id();
  var r, g, b;
  if (refvObj.source == "stl") {
      r = ref_stl_face_color_r;
      g = ref_stl_face_color_g;
      b = ref_stl_face_color_b;
  }
  else if (refvObj.source == "parasolid") {
    r = ref_parasolid_face_color_r;
    g = ref_parasolid_face_color_g;
    b = ref_parasolid_face_color_b;
  }
  else { // if (refvObj.source == "partstudio") {
    r = ref_partstudio_face_color_r;
    g = ref_partstudio_face_color_g;
    b = ref_partstudio_face_color_b;
  }
  var uidv = new Uid_vertex(id, r, g, b);

  refvObj.set_uid_object(uidv);
  uid_reference_vertices.setItem(uidv.id, refvObj);
  console_log("Added to uid_reference_vertices a new reference vertex:", uidv.id);
  var str =
    Math.round(refvObj.vertex.x * 1000000).toString() +
    Math.round(refvObj.vertex.y * 1000000).toString() +
    Math.round(refvObj.vertex.z * 1000000).toString();
  uid_reference_vertex_coords_hash.setItem(str, str);
  uid_add_vertex_geometry( uidv, refvObj.vertex.x, refvObj.vertex.y, refvObj.vertex.z, true, superimposed, refvObj.source );
  return (uidv.id);
}

export function uid_add_reference_edge(ref_edge_obj) {
  var id = 200000 + get_global_id();

  var r, g, b;
  if (ref_edge_obj.source == "stl") {
      r = ref_stl_face_color_r;
      g = ref_stl_face_color_g;
      b = ref_stl_face_color_b;
  }
  else if (ref_edge_obj.source == "parasolid") {
    r = ref_parasolid_face_color_r;
    g = ref_parasolid_face_color_g;
    b = ref_parasolid_face_color_b;
  }
  else { // if (refvObj.source == "partstudio") {
    r = ref_partstudio_face_color_r;
    g = ref_partstudio_face_color_g;
    b = ref_partstudio_face_color_b;
  }

  var uide = new Uid_edge(id, r, g, b);
  ref_edge_obj.set_uid_object(uide);
  uid_reference_edges.setItem(uide.id, ref_edge_obj);
  if (ref_edge_obj.points.length == 2 && vector3.point_point_distance(ref_edge_obj.points[0], ref_edge_obj.points[1]) == 0) {
    // This is a 0-length edge, can't add it.
  }
  else {
    uid_add_edge_geometry(uide, ref_edge_obj.points, true, ref_edge_obj.source);
  }

  return (uide.id);
}

export function uid_add_reference_face(ref_face_obj) {
  var id = 300000 + get_global_id();

  var r, g, b;
  if (ref_face_obj.source == "stl") {
      r = ref_stl_face_color_r;
      g = ref_stl_face_color_g;
      b = ref_stl_face_color_b;
  }
  else if (ref_face_obj.source == "parasolid") {
    r = ref_parasolid_face_color_r;
    g = ref_parasolid_face_color_g;
    b = ref_parasolid_face_color_b;
  }
  else { // if (refvObj.source == "partstudio") {
    r = ref_partstudio_face_color_r;
    g = ref_partstudio_face_color_g;
    b = ref_partstudio_face_color_b;
  }

  var uidf = new Uid_face(id, rgbToHex(r, g, b));
  ref_face_obj.set_uid_object(uidf);
  uid_reference_faces.setItem(uidf.id, ref_face_obj);
  uid_add_face_geometry(uidf, ref_face_obj.triangle_vertices, ref_face_obj.triangles, true, false, ref_face_obj.source);
  return (uidf.id);
}


// Returns the size of the ball used for deforming edges.
export function uid_get_ball_size() {
  return uid_ball_size;
}

// Returns the hardness of the ball used for deforming edges.
export function uid_get_ball_hardness() {
  return uid_ball_hardness;
}

// Sets the size of the ball used for deforming edges.
export function uid_set_ball_size(v) {
  uid_ball_size = v;
}

// Sets the hardness of the ball used for deforming edges.
export function uid_set_ball_hardness(v) {
  uid_ball_hardness = v;
}

// Returns whether a 3D object is currently selected, based on its id
export function uid_is_obj3D_selected(id) {
  var uido = uid_object3dIDToUid_object.getItem(id);
  if (uido.type == "vertex") {
    return uid_selected_vertices.hasItem(id);
  } else if (uido.type == "edge") {
    return uid_selected_edges.hasItem(id);
  } else if (uido.type == "face") {
    return uid_selected_faces.hasItem(id);
  } else {
    // Error
  }
}

// Returns whether a UI object is currently selected
export function uid_is_uiobj_selected(uido) {
  if (uido.type == "vertex") {
    return uid_selected_vertices.hasItem(uido.id);
  } else if (uido.type == "edge") {
    return uid_selected_edges.hasItem(uido.id);
  } else if (uido.type == "face") {
    return uid_selected_faces.hasItem(uido.id);
  } else {
    // Error
  }
}

export function is_manipulator_geometry(o3d) {
  if (!o3d || !o3d.geometry) {
    var x = 0;
    return 0;
  }
  return (
    o3d.geometry.name == "edge_tangent_manip_vector_tip_1" ||
    o3d.geometry.name == "edge_tangent_manip_vector_tip_2" ||
    o3d.geometry.name == "rigid_move_manip_cube" ||
    o3d.geometry.name == "rigid_move_manip_ringx" ||
    o3d.geometry.name == "rigid_move_manip_ringy" ||
    o3d.geometry.name == "rigid_move_manip_ringz" ||
    o3d.geometry.name == "rigid_move_manip_planexy" ||
    o3d.geometry.name == "rigid_move_manip_planexz" ||
    o3d.geometry.name == "rigid_move_manip_planeyz" ||
    o3d.geometry.name == "rigid_move_manip_vectorx_rod" ||
    o3d.geometry.name == "rigid_move_manip_vectory_rod" ||
    o3d.geometry.name == "rigid_move_manip_vectorz_rod" ||
    o3d.geometry.name == "rigid_move_manip_vectorx_tip" ||
    o3d.geometry.name == "rigid_move_manip_vectory_tip" ||
    o3d.geometry.name == "rigid_move_manip_vectorz_tip" ||
    o3d.geometry.name == "rigid_move_manip_fence_tube" ||
    o3d.geometry.name == "rigid_move_manip_ringxTrans" ||
    o3d.geometry.name == "rigid_move_manip_ringyTrans" ||
    o3d.geometry.name == "rigid_move_manip_ringzTrans" ||
    o3d.geometry.name == "rigid_move_manip_vectorx_rodTrans" ||
    o3d.geometry.name == "rigid_move_manip_vectory_rodTrans" ||
    o3d.geometry.name == "rigid_move_manip_vectorz_rodTrans" ||
    o3d.geometry.name == "rigid_move_manip_vectorx_tipTrans" ||
    o3d.geometry.name == "rigid_move_manip_vectory_tipTrans" ||
    o3d.geometry.name == "rigid_move_manip_vectorz_tipTrans"
    );
}

export function add_halo_to_intersection_point(intersected_obj, mousenormalized) {

  var o3d = uid_raynode.getObjectById(intersected_obj.object.id);
  if (!o3d || !o3d.geometry || is_manipulator_geometry(o3d))
    return;

  var uido = uid_object3dIDToUid_object.getItem(o3d.id);
  if (!uido)
    return

  uid_raycaster.setFromCamera(mousenormalized, uid_camera);

  if (uido.type == "vertex") {
    var normal = find_vertex_normal(uido.id, uid_raycaster.ray.origin, uid_raycaster.ray.direction);
    if (normal && normal.length() > 0)
      show_halo(uido.object3d.position, normal, 0);
  }
  else if (uido.type == "edge") {
    var wv_parameter = new WrappedValue();
    var intersection_point = uil_get_ray_edge_intersection(uido.id, uid_raycaster.ray.origin, uid_raycaster.ray.direction, false, wv_parameter);
    var normal = find_edge_normal(uido.id, uid_raycaster.ray.origin, uid_raycaster.ray.direction);
    if (normal && normal.length() > 0)
      show_halo(intersection_point, normal, 0);
  }
  else if (uido.type == "face") {
    uid_raycaster.setFromCamera(mousenormalized, uid_camera);
    var normal = find_face_normal(uido.id, intersected_obj); // XXX Will change signature of this function

    if (normal && normal.length() > 0) {
      var intersection_point = new THREE.Vector3();
      intersection_point.copy(intersected_obj.point);
      show_halo(intersection_point, normal, 0);
      // XXX Later we will replace intersection_point with the intersection point on the face returned by the core.
    }
  }
}

export function uid_toggle_select_object(id, leave_highlighted, intersection_point = null) {
  if (!object_is_selected(id))
    uid_select_object(id, leave_highlighted, false, intersection_point);
  else
    uid_deselect_object(id, leave_highlighted);
}

export function uid_deselect_object(id, leave_highlighted) {
  var uido = get_object_from_id(id);
  if (!uido)
      return;

  if (!uid_is_uiobj_selected(uido))
  return;


  // Set correct color for selected/deselected object
  if (uido.type == "vertex") {
    uid_selected_vertices.removeItem(uido.id);
    set_default_vertex_color(uido.id);
    var scale_factor = 1.0/1.1;
    uido.object3d.geometry.scale(scale_factor, scale_factor, scale_factor);
  }
  else if (uido.type == "edge") {
    uid_selected_edges.removeItem(uido.id);
    set_default_edge_color(uido.id);
  }
  else if (uido.type == "face") {
    uid_selected_faces.removeItem(uido.id);
    set_default_face_color(uido.id);
    // Also check if there's a sketch attached, and in that case make it more opaque
    if (face_is_for_sketch(uido.id)) {
      non_deleted_reference_sketches().forEach(function(loaded_sketch_element){
        if (loaded_sketch_element.face_id == uido.id) {
          loaded_sketch_element.mesh.material.opacity = 0.6;
        }
      });
    }

  }
  else {
    // Error
  }

  request_setting_button_states();
  animate();
}

export function uid_select_object(id, leave_highlighted, dont_set_button_state = false) {
  var uido = get_object_from_id(id);
  if (!uido)
    return;

  if (uid_is_uiobj_selected(uido))
    return;

  if ((uido.type == "vertex") && (!uid_selected_vertices.hasItem(uido.id))) {
    uid_selected_vertices.setItem(uido.id, uido);
    set_default_vertex_color(uido.id);
    var scale_factor = 1.1;
    uido.object3d.geometry.scale(scale_factor, scale_factor, scale_factor);
  }
  else if ((uido.type == "edge") && (!uid_selected_edges.hasItem(uido.id))) {
    uid_selected_edges.setItem(uido.id, uido);
    set_default_edge_color(uido.id);

    // If we are in tangent manipulation mode, also add this edge to the set that we display tangents for,
    // and update to redisplay tangents
    if (App.state.editing_edge_tangent_mode) {
      if (edges_for_which_to_display_tangency && !edges_for_which_to_display_tangency.includes(uido.id)) {
        edges_for_which_to_display_tangency.push(uido.id);
        hide_tangencies(true);
        display_tangencies(false, true);
      }
    }
    if (uid_selected_edge_click_point)
      uid_selected_edges_click_coordinates.setItem(uido.id, uid_selected_edge_click_point);
  }
  else if ((uido.type == "face") && (!uid_selected_faces.hasItem(uido.id))) {
    uid_selected_faces.setItem(uido.id, uido);
    set_default_face_color(uido.id);
    // Also check if there's a sketch attached, and in that case make it more opaque
    if (face_is_for_sketch(uido.id)) {
      App.state.imported_sketches.forEach(function(loaded_sketch_element){
        if (loaded_sketch_element.face_id == uido.id) {
          loaded_sketch_element.mesh.material.opacity = 0.9;
        }
      });
    }
  }
  else {
    // Error
  }
  if (!dont_set_button_state)
    request_setting_button_states();
  animate();
}

export function uid_toggle_select_reference_face(ref_obj, apply_to_entire_body = false) {
  var uido = uid_object3dIDToUid_object.getItem(ref_obj.id);
  if (!uido)
    return

  if (!reference_object_is_selected(uido.id)) {
    if (!apply_to_entire_body)
      uid_select_reference_face(ref_obj);
    else
      uid_set_selection_state_for_all_reference_faces_in_body(ref_obj, true);
  }
  else {
    if (!apply_to_entire_body)
      uid_deselect_reference_face(ref_obj);
    else
      uid_set_selection_state_for_all_reference_faces_in_body(ref_obj, false);
  }
}

export function uid_select_reference_face(ref_obj) {
  uid_selected_reference_faces.push(ref_obj);

  var uido = uid_object3dIDToUid_object.getItem(ref_obj.id);
  if (!uido)
    return

  set_default_face_color(uido.id);
  //uido.object3d.material.color.setRGB( color_ref_face_selected_r /256, color_ref_face_selected_g /256, color_ref_face_selected_b /256 );
  animate();
}

export function uid_deselect_reference_face(ref_obj) {
  uid_selected_reference_faces.splice(uid_selected_reference_faces.indexOf(ref_obj),1);

  var uido = uid_object3dIDToUid_object.getItem(ref_obj.id);
  if (!uido)
    return

  set_default_face_color(uido.id);
  animate();
}

export function uid_set_selection_state_for_all_reference_faces_in_body(ref_obj, must_select) {
  suppress_animate();
  var uido = uid_object3dIDToUid_object.getItem(ref_obj.id);
  var ref_face = uid_reference_faces.getItem(uido.id);
  if (!ref_face) {
    unsuppress_animate_if_ready();
    return;
  }

  var face_array = [];
  uid_reference_faces.values().forEach(function(rf){
    uid_deselect_reference_face(rf.uid_object.object3d);
    if (rf.onshape_body_id == ref_face.onshape_body_id)
      face_array.push(rf);
  });
  if (must_select) {
    face_array.forEach(function(rf){
      uid_select_reference_face(rf.uid_object.object3d);
    });
  }
  unsuppress_animate_if_ready();
}

export function deselect_all_reference_faces() {
  uid_reference_faces.values().forEach(function(rf){
    uid_deselect_reference_face(rf.uid_object.object3d);
  });
}

export function display_zoom_speed_modal() {
  // Update slider range
  show_modal("zoomSpeedModal");
}

let has_entered_manage_tangents_once = false;

export function enter_editing_edge_tangent_mode() {
    const inside_raynode = inside(uid_objects_raynode.children);

    if (App.state.editing_edge_tangent_mode)
        return;

    edges_for_which_to_display_tangency = pipe(
        uid_selected_edges.length > 0
            ? uid_selected_edges
            : uid_edges,
        x => x.entries(),
        filter(([eid, edge]) =>
            uil_is_object_visible(eid)
            && !edge_is_for_sketch(eid)
            && inside_raynode(edge.object3d)
        ),
        lefts,
        array,
    );

    if (edges_for_which_to_display_tangency.length === 0) {
        display_message_to_user(LOG.WARN, 'Cannot enter tangent editing mode with all edges hidden.');
        return;
    }

    App.state.editing_edge_tangent_mode = true;
    uid_deselect_all_objects();
    display_tangencies(false, false);
    set_button_states();
    if (!has_entered_manage_tangents_once)
        display_message_to_user(
            LOG.INFO,
            "Entered Manage Tangents mode. Click on this message to read more...",
            true,
            () => display_help_modal('manage_tangents_mode'),
        );
}

export function exit_editing_edge_tangent_mode(no_user_message = false) {
  if (!App.state.editing_edge_tangent_mode)
    return;
  App.state.editing_edge_tangent_mode = false;
  hide_tangencies(false, false);
  set_button_states();
  if (!(no_user_message || has_entered_manage_tangents_once))
    display_message_to_user(LOG.INFO, "Exited Manage Tangents mode.");
  has_entered_manage_tangents_once = true;
}

export function display_set_curvature_comb_settings_modal() {
  show_modal("curvatureCombSettingsModal");
}

// Set state of dirty flag
export function uid_set_dirty_flag() {
  App.state.file_save_status = "dirty";
  update_dom_text_values_and_coordinates();
}

// Unset state of dirty flag
export function uid_unset_dirty_flag(save_type) {
  if (save_type == "to_onshape" || save_type == "to_file") {
    App.state.file_save_status = "saved";
  } else if (model_is_dirty() && save_type == "to_onshape_autosave") {
    App.state.file_save_status = "autosaved";
  } else if (save_type == "load_model") {
    App.state.file_save_status = "saved";
  }
  update_dom_text_values_and_coordinates();
}

/** @returns {'enable_fixing' | 'enable_unfixing' | 'disable'} */
export function potential_fixing_operation_for_selected_objects() {
    if (!has_selected_objects())
        return 'disable';
    else if (
        uid_raynode == uid_objects_raynode
        && App.state.editing_edge_tangent_mode
    ) {
        if (
            uid_selected_vertices.length == 1
            && uid_selected_edges.length == 1
            && uid_selected_faces.length == 0
            && uil_get_vertices_of_edge(uid_selected_edges.firstKey())
                .includes(uid_selected_vertices.firstKey())
        ) {
            const eid = uid_selected_edges.firstKey();
            const vid = uid_selected_vertices.firstKey();

            // Check if the edge tangent is already fixed
            const wv = new WrappedValue();
            const res = uil_get_edge_fixing_of_tangent_at_vertex(eid, vid, wv);
            return wv.v
                ? 'enable_unfixing'
                : 'enable_fixing';
        } else if (
            uid_selected_vertices.length == 0
            && uid_selected_edges.length == 1
            && uid_selected_faces.length == 0
            && uid_selected_edge_click_point != null
        ) {
            const eid = uid_selected_edges.firstKey();
            const vid = find_edge_closest_vertex_to_coordinates(eid, uid_selected_edge_click_point)

            // Check if the edge tangent is already fixed
            const wv = new WrappedValue();
            const res = uil_get_edge_fixing_of_tangent_at_vertex(eid, vid, wv);
            return wv.v
                ? 'enable_unfixing'
                : 'enable_fixing';
        } else
            return 'disable';
    } else if (uid_raynode == uid_objects_raynode) {
        const all_fixed = every(uil_is_object_fixed)(uid_all_selected_objects());
        return all_fixed
            ? 'enable_unfixing'
            : 'enable_fixing';
    } else
        return 'disable';
}

export function uid_set_fixing_of_selected_objects(must_fix_objects) {
    const obj_ids = [...uid_selected_edges.keys()];

    // If we are unfixing and there are more than one faces selected, add their common
    // edges to the list of objects to unfix, otherewise connected faces may not be unfixed.
    let common_edges_found = false;
    if (!must_fix_objects && uid_selected_faces.length > 1)
        pipe(
            uid_selected_faces.keys(),
            bind(uil_get_edges_of_face),
            count,
            filter(([eid, count]) => count > 1),
            lefts,
            each(eid => {
                obj_ids.push(eid);
                common_edges_found = true;
            }),
        );

    // If we're unfixing and there are faces with all faces around them fixed
    // give message.
    if (
        !must_fix_objects
        && !common_edges_found
        && uid_selected_faces.length > 0
        && uid_selected_edges.length == 0
        && uid_selected_vertices.length == 0
    ) {
        const fixed_face_count = uid_selected_faces
            .keys()
            .filter(fid =>
                uil_get_edges_of_face(fid)
                    .every(eid =>
                        uil_is_object_fixed(eid)
                        && uil_is_object_fixed(get_neighbouring_face_wrt_edge(fid, eid))))
            .length;

        if (fixed_face_count > 1)
            display_message_to_user(LOG.WARN, `There are ${fixed_face_count} selected faces that cannot be unfixed due to all objects around them being fixed. Please try unfixing edges around them first.`);
        else if (fixed_face_count === 1)
            display_message_to_user(LOG.WARN, "The selected face cannot be unfixed due to all objects around it being fixed. Please try unfixing edges around it first.");
    }

    obj_ids.push(...uid_selected_faces.keys());
    obj_ids.push(...uid_selected_vertices.keys());

    uid_set_fixing_of_objects(unique(obj_ids), must_fix_objects);
}

export function uid_set_fixing_of_objects(obj_ids, must_fix_objects) {
  var face_ids = [];
  var edge_ids = [];
  var vertex_ids = [];

  obj_ids.forEach(function(oid){
    if (isFace(oid))
      push_nodups(oid, face_ids);
    else if (isEdge(oid))
      push_nodups(oid, edge_ids);
    else if (isVertex(oid))
      push_nodups(oid, vertex_ids);
  });

  suppress_undo_tracking();
  suppress_animate();
  suppress_ui_messages();
  uil_start_compound_operation();
  face_ids.forEach(function(oid){
    uid_set_face_fixing(oid, must_fix_objects, true);
  });
  edge_ids.forEach(function(oid){
    uid_set_edge_fixing(oid, must_fix_objects, true);
  });
  vertex_ids.forEach(function(oid){
    uid_set_vertex_fixing(oid, must_fix_objects, false);
  });
  uil_end_compound_operation();
  uid_select_all_objects();
  uid_deselect_all_objects();
  unsuppress_ui_messages_if_ready();
  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);
  animate();
}

export function uid_set_vertex_fixing(oid, must_fix_object, propagate_to_edges = true) {
  if (must_fix_object && !uil_is_object_fixed(oid))
    uil_fix_objects([oid]);
  else if (!must_fix_object && uil_is_object_fixed(oid)) {
    uil_unfix_objects([oid]);

    if (propagate_to_edges) {
      // Must also unfix any edges on this vertex
      var l = uil_get_edges_of_vertex(oid);
      l.forEach(function(eid){
        uid_set_edge_fixing(eid, false, false);
        // Must also unfix the tangents of this edge, if they were fixed.
        unfix_edge_tangent_at_vertex(eid, [uil_get_vertices_of_edge(eid)[0], uil_get_vertices_of_edge(eid)[1]], true);
      });
    }
  }
}

export function uid_set_edge_fixing(oid, must_fix_object, propagate_to_vertices = true) {
  if (propagate_to_vertices) {
    if (must_fix_object && !uil_is_object_fixed(oid) ) {
      var l = uil_get_vertices_of_edge(oid);
      l.forEach(function(vid){
        uid_set_vertex_fixing(vid, must_fix_object);
      });
    }
    else if (!must_fix_object && uil_is_object_fixed(oid) ) {
      var l = uil_get_vertices_of_edge(oid);
      l.forEach(function(vid){
        var v_es = uil_get_edges_of_vertex(vid).filter(eid => (eid != oid && uil_is_object_fixed(eid)));
        if (v_es.length == 0)
          uid_set_vertex_fixing(vid, must_fix_object);
      });
    }
  }

  if (must_fix_object && !uil_is_object_fixed(oid)) {
    // Must also fix the tangents of this edge, if they are unfixed.
    fix_edge_tangent_at_vertex(oid, [uil_get_vertices_of_edge(oid)[0], uil_get_vertices_of_edge(oid)[1]], true);
    uil_fix_objects([oid]);
  }
  else if (!must_fix_object && uil_is_object_fixed(oid)) {
    uil_unfix_objects([oid]);

    // Must also unfix the tangents of this edge, if they were fixed.
    unfix_edge_tangent_at_vertex(oid, [uil_get_vertices_of_edge(oid)[0], uil_get_vertices_of_edge(oid)[1]], true);

    // Must also unfix any faces containing this edge
    var l = uil_get_faces_of_edge(oid);
    l.forEach(function(fid){
      uid_set_face_fixing(fid, false, false);
    });
  }
}

export function uid_set_face_fixing(oid, must_fix_object, propagate_to_edges = true) {

  if (propagate_to_edges) {

    if (must_fix_object && !uil_is_object_fixed(oid))  {
      // Must also fix all edges and vertices around this face
      var l = uil_get_edges_of_face(oid);
      var vs = [];
      l.forEach(function(eid){
        uid_set_edge_fixing(eid, must_fix_object, false);
        vs.push( ... uil_get_vertices_of_edge(eid));
      });

      // Also set for vertices of edges
      vs.forEach(function(vid) {
        uid_set_vertex_fixing(vid, must_fix_object, false);
      });

    }
    else if (!must_fix_object && uil_is_object_fixed(oid)) {
      // Must also unfix all edges and vertices around this face, unless other fixed objects mean that they should remain fixed.
      var l = uil_get_edges_of_face(oid);
      var vs = [];
      l.forEach(function(eid){
        var e_fs = uil_get_faces_of_edge(eid);
        if (!((e_fs[0] != oid && uil_is_object_fixed(e_fs[0])) || (e_fs[1] != oid && uil_is_object_fixed(e_fs[1])))) {
          uid_set_edge_fixing(eid, must_fix_object, false);
          vs.push( ... uil_get_vertices_of_edge(eid));
        }
      });

      vs.forEach(function(vid) {
        var v_es = uil_get_edges_of_vertex(vid).filter(eid => uil_is_object_fixed(eid));
        if (v_es.length == 0)
          uid_set_vertex_fixing(vid, must_fix_object, false);
      });
    }
  }

  /* Not actually fixing the faces. Fixedness of faces is deduced from the fixedness of edges around it. */
  if (must_fix_object && !uil_is_object_fixed(oid))
    uil_fix_objects([oid]);
  else if (!must_fix_object && uil_is_object_fixed(oid))
    uil_unfix_objects([oid]);
}

export function uid_pop(is_out, also_cut) {
  // Reset undo tracking, in case this is executed while in some suppressed undo state, as this is a basic operation
  reset_undo_tracking(false);

  var face_id = uid_selected_faces.firstKey();
  if (!face_id)
    return;

  var found_fixed = false;
  if (uil_is_object_fixed(face_id)) {
    found_fixed = true;
  }
  if (!found_fixed) {
    var es = uil_get_edges_of_face(face_id);
    es.forEach(function(eid){
      if (uil_is_object_fixed(eid))
        found_fixed = true;
    });
  }
  if (!found_fixed) {
    var vs = vertices_of_face(face_id);
    vs.forEach(function(vid){
      if (uil_is_object_fixed(vid))
        found_fixed = true;
    });
  }


  if (found_fixed) {
    display_message_to_user(LOG.WARN, "Cannot pop a fixed face.");
    return;
  }

  var was_already_in_manipulator_mode = false;
  if (uid_raynode == uid_manip_raynode) {
    if (uid_rigid_move_manipulator && uid_rigid_move_manipulator.has_operated()) {
      // We are popping after the manipulator has operated, so we need to push an entry to the undo stack
      uil_save_undo_state();
    }
    uid_hide_rigid_move_manipulator(true, 0, true);
    was_already_in_manipulator_mode = true;
  }

  uid_deselect_all_objects();
  uid_select_object(face_id, false);

  var faces_to_merge;

  if (also_cut) {
    var fixed_objects_exist = false;
    uid_faces.keys().forEach(function(oid){
      if (uil_is_object_fixed(oid))
        fixed_objects_exist = true;
    });
    uid_edges.keys().forEach(function(oid){
      if (uil_is_object_fixed(oid))
        fixed_objects_exist = true;
    });
    uid_vertices.keys().forEach(function(oid){
      if (uil_is_object_fixed(oid))
        fixed_objects_exist = true;
    });

    if (fixed_objects_exist) {
      display_message_to_user(LOG.WARN, "There are fixed objects, cannot proceed");
      return;
    }

    // Here we mark all faces that are currently present, except the one being popped, so we can merge away all the other onese
    // after the fact.
    var bid = uil_get_body_of_face(face_id);
    faces_to_merge = new Array();
    uid_faces.keys().forEach(function(fid){
      if (fid != face_id && uil_get_body_of_face(fid) == bid)
        faces_to_merge.push(fid);
    });
  }

  uid_toggle_select_object(face_id, false);
  var popped_face_id = null;
  if (is_out) {
    suppress_undo_tracking();
    var pop_return_array = uil_pop_face(face_id, 0, null, 0);
    popped_face_id = pop_return_array[0];
    if (popped_face_id && faces_to_merge) {
      // Here we merge all those faces we marked
      // Before merging them make them visible because we need to close/thicken the body, otherwise we get an invisible face in their place.
      uil_make_faces_visible(faces_to_merge);
      uil_merge_faces(faces_to_merge, false, 0, 0);
    }
    unsuppress_undo_tracking_if_ready(false);
  }
  else {
    var pop_return_array = uil_pop_face(face_id, 1, null, 0);
    popped_face_id = pop_return_array[0];
  }
  if (popped_face_id) {
    uid_toggle_select_object(popped_face_id, false);
    uid_show_rigid_move_manipulator(was_already_in_manipulator_mode, pop_return_array[1].length(), true, true, false, false, false, false);
  }
}

export function uid_pop_many(is_out, also_cut) {
  var face_ids = [];
  uid_selected_faces.keys().forEach(function(fid){
    face_ids.push(fid);
  });
  if (face_ids.length == 0)
    return;

  // If any fixed objects, stop.
  var found_fixed = false;
  face_ids.forEach(function(fid){
    if (uil_is_object_fixed(fid))
      found_fixed = true;
    if (!found_fixed) {
      var es = uil_get_edges_of_face(fid);
      es.forEach(function(eid){
        if (uil_is_object_fixed(eid))
          found_fixed = true;
      });
    }
    if (!found_fixed) {
      var vs = vertices_of_face(fid);
      vs.forEach(function(vid){
        if (uil_is_object_fixed(vid))
          found_fixed = true;
      });
    }
  });

  if (found_fixed) {
    display_message_to_user(LOG.WARN, "Cannot pop a fixed face.");
    return;
  }

  var was_already_in_manipulator_mode = false;
  if (uid_raynode == uid_manip_raynode) {
    if (uid_rigid_move_manipulator && uid_rigid_move_manipulator.has_operated()) {
      // We are popping after the manipulator has operated, so we need to push an entry to the undo stack
      uil_save_undo_state();
    }
    uid_hide_rigid_move_manipulator(true, 0, true);
    was_already_in_manipulator_mode = true;
  }

  uid_deselect_all_objects();
  face_ids.forEach(function(fid){
    uid_select_object(fid, false);
  });

  var faces_to_merge;

  if (also_cut) {
    var fixed_objects_exist = false;
    uid_faces.keys().forEach(function(oid){
      if (uil_is_object_fixed(oid))
        fixed_objects_exist = true;
    });
    uid_edges.keys().forEach(function(oid){
      if (uil_is_object_fixed(oid))
        fixed_objects_exist = true;
    });
    uid_vertices.keys().forEach(function(oid){
      if (uil_is_object_fixed(oid))
        fixed_objects_exist = true;
    });

    if (fixed_objects_exist) {
      display_message_to_user(LOG.WARN, "There are fixed objects, cannot proceed");
      return;
    }

    // Here we mark all faces that are currently present, except the one being popped, so we can merge away all the other onese
    // after the fact.
    var bid = uil_get_body_of_face(face_ids[0]);
    faces_to_merge = new Array();
    uid_faces.keys().forEach(function(fid){
      if (face_ids.indexOf(fid) == -1 && uil_get_body_of_face(fid) == bid)
        faces_to_merge.push(fid);
    });
  }

  suppress_undo_tracking();

  var pop_vector = uil_pop_faces(face_ids, is_out ? 0 : 1, null, 0);
  if (pop_vector) {
    if (faces_to_merge) {
      // Here we merge all those faces we marked
      // Before merging them make them visible because we need to close/thicken the body, otherwise we get an invisible face in their place.
      // The merge attempt may fail, in which case we don't merge. We suppress the UI message if this happens.
      suppress_ui_messages();
      uil_make_faces_visible(faces_to_merge);
      var merge_result = uil_merge_faces(faces_to_merge, false, 0, 0);
      unsuppress_ui_messages_if_ready();
      if (!merge_result)
        display_message_to_user(LOG.WARN, "Failed to dissolve remaining faces. May be violating the temporary three vertex limitation.");
    }
    unsuppress_undo_tracking_if_ready(false);

    uid_deselect_all_objects();
    face_ids.forEach(function(fid){
      uid_select_object(fid, false);
    });
    uid_show_rigid_move_manipulator(was_already_in_manipulator_mode, pop_vector.length(), true, true, false, false, false, false, false);
  }
  else {
    unsuppress_undo_tracking_if_ready(false);
  }
}

export const uid_thicken = withSpinner("Thickening", function uid_thicken() {
  if (uid_selected_faces.length == 0)
    return;

  if (App.state.rigid_move_manipulator) {
    const selected_faces = uid_selected_faces.keys();
    uid_hide_rigid_move_manipulator(false, 0, true);
    uid_deselect_all_objects();
    selected_faces.forEach(uid_select_object);
  }

  const bodies = pipe(
    uid_selected_faces.keys(),
    map(uil_get_body_of_face),
    unique,
  );

  if (bodies.length !== 1)
    return;

  const body_id = bodies[0];

  // Check if all of the face normal vectors are within an angle threshold from the common normal.
  const face_normals = uid_selected_faces.keys().map(fid => {
    const fnormal = new THREE.Vector3();
    const center = new THREE.Vector3();
    const wv_radius = new WrappedValue(0);
    const characteristic_dir_coords = new THREE.Vector3();
    const wv_flatness = new WrappedValue(0);
    uil_get_face_geometry(fid, center, fnormal, wv_radius, characteristic_dir_coords, wv_flatness);
    fnormal.normalize();
    return fnormal;
  });

  {
    const normal = face_normals.reduce((normal, fnormal) => normal.add(fnormal), new THREE.Vector3(0, 0, 0));
    const eps = 0.001;
    if (maths.proximate(normal.length(), 0, eps)) {
      display_message_to_user(LOG.WARN, "Faces are not aligned enough to perform thicken.")
      return;
    }
  }

  const angle_threshold = 135.0; // degrees.
  const radians = x => x * Math.PI/180
  const found_bad_vector = pipe(
    face_normals,
    combinations(face_normals),
    some(([a, b]) => vector3.angle(a, b) > radians(angle_threshold)),
  );

  if (found_bad_vector) {
    display_message_to_user(LOG.WARN, "Faces are not aligned enough to perform thicken.")
    return;
  }

  suppress_animate();
  suppress_undo_tracking();
  suppress_properly_splitting_invisible_components();
  suppress_core_face_tessellation();

  uid_state.in_thicken_operation = true;

  // We will thicken all selected faces, so we must make all others transparent
  const faces_to_make_invisible = pipe(
    body_id,
    faces_of_body,
    filter(fid => uil_is_object_visible(fid) && !object_is_selected(fid)),
    unique,
  );

  uil_make_faces_invisible(faces_to_make_invisible, false);
  uid_deselect_all_objects();

  // Make a copy of the body and keep track of edges and faces.
  const copied_body_id = uil_copy_bodies([body_id], true)[0];
  const copied_edges =
    uid_edges
      .keys()
      .filter(eid => uil_get_body_of_edge(eid) === copied_body_id);
  const visible_copied_edges = copied_edges.filter(uil_is_object_visible);
  const visible_copied_faces =
    uid_faces
      .keys()
      .filter(fid => uil_get_body_of_face(fid) == copied_body_id && uil_is_object_visible(fid));

  // Find normal movement direction based on all visible faces, not edges
  const center = new THREE.Vector3();
  const fnormal = new THREE.Vector3();
  const characteristic_dir_coords = new THREE.Vector3();
  const wv_radius = new WrappedValue(0);
  const wv_flatness = new WrappedValue(0);
  const normal = visible_copied_faces.reduce((normal, fid) => {
    uil_get_face_geometry(fid, center, fnormal, wv_radius, characteristic_dir_coords, wv_flatness);
    fnormal.normalize();
    normal.add(fnormal);
    return normal;
  }, new THREE.Vector3(0,0,0));
  normal.normalize();

  uil_get_edge_list_geometry(visible_copied_edges, false, center, fnormal, wv_radius, characteristic_dir_coords);
  normal.multiplyScalar(wv_radius.v/5.0);

  start_move_edge_list(copied_edges);
  const vbogus = new THREE.Vector3(0,0,1);
  move_edge_list(copied_edges, normal, 0, vbogus, vbogus);
  end_move_edge_list(copied_edges);

  uil_flip_body_inside_out(body_id);

  // Start bridging
  // Find connected component boundaries for the two objects.
  const boundary_edges1 = find_boundary_edges(body_id);
  const boundary_edges2 = find_boundary_edges(copied_body_id).filter(isEdge);

  // Split all boundary edges into lists of connected edges, for 1st face group only
  const edge_list = get_listified_lists_of_connected_edges_in_edge_list(boundary_edges1, true);

  // Bridge each edge with the one across from it
  const pairs = pair_edges_across(
    edge_list.length
      ? edge_list.flatMap(x => x.edges)
      // This is a hack. If the listifying fails, e.g. because of "+" edge nodes, we use the original list and pray...
      : boundary_edges1,
    boundary_edges2,
    normal,
  );
  for (const pair of pairs) uid_fill_faces(pair);

  uid_deselect_all_objects();
  visible_copied_faces.forEach(fid => uid_select_object(fid, false));

  unsuppress_animate_if_ready();
  unsuppress_core_face_tessellation_if_ready();
  unsuppress_undo_tracking_if_ready(false);
  unsuppress_properly_splitting_invisible_components_if_ready();

  uid_state.in_thicken_operation = false;
  uid_show_rigid_move_manipulator(false, normal.length(), true, false, false, false, false, false);
});

export function find_boundary_edges(body_id) {
  return pipe (
    body_id,
    faces_of_body,
    filter(uil_is_object_visible),
    bind(uil_get_edges_of_face),
    filter(eid =>
      uil_get_faces_of_edge(eid)
        .filter(uil_is_object_visible)
        .length === 1
    ),
    unique,
  );
}

/** for each edge in **as**, find from which edge it is across in **bs** */
export function pair_edges_across(as, bs, normal) {
  return pipe(
    as,
    map(a => {
      const vs1 = uil_get_vertices_of_edge(a);
      const p1a = new THREE.Vector3();
      p1a.copy(uid_vertices.getItem(vs1[0]).object3d.position);
      const p1b = new THREE.Vector3();
      p1b.copy(uid_vertices.getItem(vs1[1]).object3d.position);
      p1a.add(normal);
      p1b.add(normal);

      const across = optimumBy(b => {
        const vs2 = uil_get_vertices_of_edge(b);
        const p2a = new THREE.Vector3();
        p2a.copy(uid_vertices.getItem(vs2[0]).object3d.position);
        const p2b = new THREE.Vector3();
        p2b.copy(uid_vertices.getItem(vs2[1]).object3d.position);
        const d1 = vector3.point_point_distance(p1a, p2a) + vector3.point_point_distance(p1b, p2b);
        const d2 = vector3.point_point_distance(p1a, p2b) + vector3.point_point_distance(p1b, p2a);
        return Math.min(d1, d2);
      })(lt)(bs);
      return [a, across];
    }),
    filter(x => x[1]),
  );
}

export function uid_inflate_deflate(is_inflate) {
  var selected_face_object_ids = uid_selected_faces.keys();
  selected_face_object_ids.forEach(function(face_id) {
    uid_toggle_select_object(face_id, false);
    if (is_inflate)
      uil_inflate_face(face_id);
    else
      uil_deflate_face(face_id);
    uid_toggle_select_object(face_id, false);
  });
}

export function uid_set_linearity_of_selected_edges(make_linear) {
  if ( uid_selected_edges.length > 0 ) {
    var ids_of_edges = new Array();
    uid_selected_edges.values().forEach(function(e) {
      ids_of_edges.push(e.id);
    });
    uil_set_linearity_of_edges(ids_of_edges, make_linear);
    uid_deselect_all_objects();
  }
}

export function uid_make_selected_edges_linear_as_one() {
  // Depending of the fixing of any selected edges, we do different things. XXX
  if (uid_selected_edges.length == 0)
    return;

  var listified_edges = [];
  var wv_is_open_list = new WrappedValue();
  var res = uil_listify_edges(uid_selected_edges.keys(), wv_is_open_list, listified_edges);
  if (!res) {
    display_message_to_user(LOG.WARN, "Error in making edges colinear");
    return;
  }
  if (!wv_is_open_list.v) {
    display_message_to_user(LOG.WARN, "Cannot make colinear a closed loop of edges");
    return;
  }

  var must_reject = false;

  var n_fixed = 0;
  var have_found_unfixed_after_fixed = false;
  listified_edges.forEach(function(eid){
    if (uil_is_object_fixed(eid)) {
      n_fixed++;
      if (have_found_unfixed_after_fixed)
        must_reject = true;
    }
    else if (n_fixed > 0) {
      have_found_unfixed_after_fixed = true;
    }
  });

  if (must_reject) {
    display_message_to_user(LOG.WARN, "Cannot make colinear a list of edges with more than one non-consecutive fixed edges.");
    return;
  }

  must_reject = false;
  //Check that any fixed vertice lie on the fixed edge in the list
  var vertices = [];
  listified_edges.forEach(function(eid) {
    var vs = uil_get_vertices_of_edge(eid);
    vertices.push(...vs);
  });
  vertices = [...new Set(vertices)];
  vertices.forEach(function(vid) {
    if (uil_is_object_fixed(vid)) {
      var es = uil_get_edges_of_vertex(vid);
      var vertex_lies_on_so_many_unfixex_listified_edges = 0;
      es.forEach(function(eid){
        if (listified_edges.indexOf(eid) != -1 && !uil_is_object_fixed(eid) )
          vertex_lies_on_so_many_unfixex_listified_edges++;
      });
      if (vertex_lies_on_so_many_unfixex_listified_edges > 1)
        must_reject = true;
    }
  });

  if (must_reject) {
    display_message_to_user(LOG.WARN, "Spurious fixed vertices found in the list of edges.");
    return;
  }

  if (n_fixed == listified_edges.length) {
    display_message_to_user(LOG.WARN, "All selected edges are fixed.");
    return;
  }

  if (n_fixed == 0) {
    res = uid_make_edges_linear_as_one(listified_edges);
    uid_deselect_all_objects();
  }
  else if (n_fixed == 1) {
    if (uil_is_object_fixed(listified_edges[0]))
      res = uid_make_edges_linear_as_one(listified_edges);
    else if (uil_is_object_fixed(listified_edges[listified_edges.length-1])) {
      listified_edges.reverse();
      res = uid_make_edges_linear_as_one(listified_edges);
    }
    else {
      // Split list in two
      var array1 = [];
      var array2 = [];
      var found_fixed = false;
      for (var i = 0; i < listified_edges.length; i ++){
        if (!found_fixed) {
          array1.push(listified_edges[i]);
          if (uil_is_object_fixed(listified_edges[i]))
            found_fixed = true;
        }
        if (found_fixed)
          array2.push(listified_edges[i]);
      }
      array1.reverse();
      var res = uid_make_edges_linear_as_one(array1);
      var res = uid_make_edges_linear_as_one(array2);
    }
    uid_deselect_all_objects();
  }
  else if (n_fixed > 1) {
    display_message_to_user(LOG.WARN, "You can only have one fixed edge in the list to make colinear.");
    return;

    /*
    if (uil_is_object_fixed(listified_edges[0])) {
      listified_edges.splice(0,n_fixed-1);
      var res = uid_make_edges_linear_as_one(listified_edges);
    }
    else if (uil_is_object_fixed(listified_edges[listified_edges.length-1])) {
      listified_edges.splice(listified_edges.length-n_fixed+1, n_fixed-1);
      listified_edges.reverse();
      var res = uid_make_edges_linear_as_one(listified_edges);
    }
    else {
      // Split list in two
      var array1 = [];
      var array2 = [];
      var found_fixed = false;
      var fixed_count = 0;
      for (var i = 0; i < listified_edges.length; i ++){
        if (!found_fixed) {
          array1.push(listified_edges[i]);
          if (uil_is_object_fixed(listified_edges[i]))
            found_fixed = true;
        }
        if (found_fixed) {
          if (uil_is_object_fixed(listified_edges[i]))
            fix_count++;
          if (fix_count == n_fixed)
            array2.push(listified_edges[i]);
        }
      }
      array1.reverse();
      var res = uid_make_edges_linear_as_one(array1);
      var res = uid_make_edges_linear_as_one(array2);
    }
    */
  }
}

export function uid_make_edges_linear_as_one(ids_of_edges_to_make_linear) {
  suppress_undo_tracking();
  var res = uil_make_edges_linear_as_one(ids_of_edges_to_make_linear, true);
  // Now add tangencies between any edges sharing a commom vertex, if possible
  // Quadratic but list will be short so it's OK.
  if (res) {
    suppress_ui_messages();
    user_modified_objects_hash.keys().forEach(function (e1){
      if (isEdge(e1)) {
        user_modified_objects_hash.keys().forEach(function (e2){
          if (isEdge(e2) && e2 != e1) {
            var v = uil_find_edge_shared_vertex(e1, e2);
            if (v && uil_is_joint_smooth(e1, e2) == 0)
              uil_add_joint_smoothness([[e1, e2]], global_smoothen_strength, true, false);
          }
        });
      }
    });
    unsuppress_ui_messages_if_ready();
  }
  unsuppress_undo_tracking_if_ready(false)
}

export const uid_make_edges_symmetric_with_spinner = withSpinner("Adding to symmetry set", function uid_make_edges_symmetric_with_spinner(must_define_symmetry_set, must_remove_selected_edges = false) {
  uid_make_edges_symmetric(must_define_symmetry_set, must_remove_selected_edges);
}, () =>
  (
    uid_selected_edges.length
    + uid_selected_faces.length * 4
    + symmetry_edges_list_first.length
    + symmetry_edges_list_second.length
  ) ** 2 > 150
);

export function uid_make_edges_symmetric(must_define_symmetry_set, must_remove_selected_edges = false) {
  if (!App.state.symmetry_plane_selected) {
    display_message_to_user(
      LOG.INFO,
      "You have not selected a symmetry plane. To select a symmetry plane click on the 'Set symmetry plane' icon at the bottom of the scene and then select one of the three orthogonal planes that will appear, or three vertices to define a plane."
    );
    return;
  }

  if (!check_that_user_subscription_is_valid())
    return;

  if (App.state.currently_making_edges_symmetric)
    return;
  App.state.currently_making_edges_symmetric = true;

  // Before proceeding we check that any edges that are currently in the two symmetry sets are still valid, otherwise we remove them
  // from the symmetry sets.
  if (!uid_in_dragging_mode || App.state.dragging_type == "none") {
    var changed_sets = false;
    for (var i = symmetry_edges_list_first.length-1; i >= 0; i--) {
      if (!isEdge(symmetry_edges_list_first[i]) || !isEdge(symmetry_edges_list_second[i]) ) {
        console_log("Removed edges with ids " + symmetry_edges_list_first[i] + " and " + symmetry_edges_list_second[i] + " from the two symmetry sets.");
        symmetry_edges_list_first.splice(i, 1);
        symmetry_edges_list_second.splice(i, 1);
        changed_sets = true;
      }
    }
    if (changed_sets) {
      uid_unhighlight_all_objects();
    }
  }

  if (!must_define_symmetry_set && symmetry_edges_list_first.length == 0) {
    // If no symmetry set has been selected, and we are not about to define it, abort
    App.state.currently_making_edges_symmetric = false;
    return;
  }

  if (App.state.dragging_type == "none")
    suppress_undo_tracking();

  var e1modified;
  var e2modified;

  if (must_define_symmetry_set) {
    // Otherwise if we must define it, create it with the currently selected edges
    if (must_remove_selected_edges)
      remove_selected_objects_from_symmetry_set();
    else
      add_selected_objects_to_symmetry_set();
    e1modified = true;
    e2modified = false;

    if (should_display_mirror_side())
      refresh_all_faces();
  }
  else {
    // A symmetry set is already defined. Check what side of it is modified
    e1modified = false;
    e2modified = false;
    symmetry_edges_list_first.forEach(function(e){
      if (user_modified_objects_hash.hasItem(e))
        e1modified = true;
    });
    symmetry_edges_list_second.forEach(function(e){
      if (user_modified_objects_hash.hasItem(e))
        e2modified = true;
    });
  }

  // If we are not in dragging mode we must also mirror all internal tangencies from one side to the other. Since we allow mirroring in
  // both directions, we must effectively do a "union" tangencies on either side.
  if (App.state.dragging_type == "none") {
    //console.log("About to mirror tangencies");
    for (var i = 0; i < symmetry_edges_list_first.length; i++ ) {
      var first_eid = symmetry_edges_list_first[i];
      var first_neighbours = get_neighbouring_edges_of_edge(first_eid);
      first_neighbours.forEach(function(first_neid){
        var ni = symmetry_edges_list_first.indexOf(first_neid);
        if (ni != -1) {
          var second_eid = symmetry_edges_list_second[i];
          var second_neid = symmetry_edges_list_second[ni];
          if (uil_is_joint_smooth(first_eid, first_neid) && uil_is_joint_smooth(second_eid, second_neid) == 0)
            uil_add_joint_smoothness([[second_eid, second_neid]], global_smoothen_strength, false, false);
          else if (uil_is_joint_smooth(first_eid, first_neid) == 0 && uil_is_joint_smooth(second_eid, second_neid) == 1)
            uil_remove_joint_smoothness([[first_eid, first_neid]], false, false);
        }
      });
    }
  }

  var current_modified_objects = [... core_modified_objects_hash.keys()];
  var current_entire_drag_modified_objects = [... core_entire_drag_modified_objects_hash.keys()];
  // Also dump new modified objects into array with all modified objects in drag, removing duplicates
  current_modified_objects.forEach(function(i){
    push_nodups(i, current_entire_drag_modified_objects);
  });

  suppress_animate();

  let e1core_modified = false;
  let e2core_modified = false;
  symmetry_edges_list_first.forEach(function(e){
    if (core_modified_objects_hash.hasItem(e))
      e1core_modified = true;
  });
  symmetry_edges_list_second.forEach(function(e){
    if (core_modified_objects_hash.hasItem(e))
      e2core_modified = true;
  });

  if (e1modified || e2modified) {
    for (var i = 0; i < symmetry_edges_list_first.length; i++) {
      if (App.state.dragging_type != "none") {
        if (current_entire_drag_modified_objects.includes(symmetry_edges_list_first[i]) ||
             current_entire_drag_modified_objects.includes(symmetry_edges_list_second[i])) {

          if (e1core_modified)
            uil_make_edges_symmetric( symmetry_edges_list_first[i], symmetry_edges_list_second[i], symmetry_plane_base, symmetry_plane_normal, true );
          else if (e2core_modified)
            uil_make_edges_symmetric( symmetry_edges_list_second[i], symmetry_edges_list_first[i], symmetry_plane_base, symmetry_plane_normal, true );
        }
      }
      else if ((e1modified && current_entire_drag_modified_objects.includes(symmetry_edges_list_first[i])) || must_define_symmetry_set)
        uil_make_edges_symmetric( symmetry_edges_list_first[i], symmetry_edges_list_second[i], symmetry_plane_base, symmetry_plane_normal, true );
      else if (current_entire_drag_modified_objects.includes(symmetry_edges_list_second[i]))
        uil_make_edges_symmetric( symmetry_edges_list_second[i], symmetry_edges_list_first[i], symmetry_plane_base, symmetry_plane_normal, true );
    }
  }
  unsuppress_animate_if_ready();

  if (App.state.dragging_type == "none") {
    // Clear list of recently modified objects, unless we are in dragging mode, in which case we want to keep it.
    if (!in_compound_operation()) {
      user_modified_objects_hash.clear();
      core_modified_objects_hash.clear();
      core_entire_drag_modified_objects_hash.clear();
    }

    unsuppress_undo_tracking_if_ready(false);
    if (must_define_symmetry_set) {
      uid_select_all_objects();
      uid_deselect_all_objects();
    }
  }
  else {
    // Dump current core_modified_objects_hash onto current_entire_drag_modified_objects_hash
    core_modified_objects_hash.keys().forEach(function (k){
      core_entire_drag_modified_objects_hash.setItem(k, core_modified_objects_hash.getItem(k));
    });
    core_modified_objects_hash.clear()
  }

  App.state.currently_making_edges_symmetric = false;
}

export function uid_show_rigid_move_manipulator(
    only_temporarily,
    pop_length,
    must_set_button_states,
    use_local_coordinate_system,
    use_prestored_tmp_placement,
    is_for_edge_tangent,
    modifier,
    required_to_operate,
) {
  if (!has_selected_objects() || !can_move_selected_objects()) {
    display_message_to_user(LOG.WARN, "Unable to move selected elements");
    return;
  }

  if (App.state.rigid_move_manipulator)
    return;

  // First we take care of the value that would appear in the readvalue box.
  pop_amount_value = pop_length;

  // First check that no fixed objects are involved, directly or indirectly
  var cannot_proceed_due_to_fixed_objects = false;
  var edges = [];
  uid_selected_faces.keys().forEach(function(fid) {
    if (uil_is_object_fixed(fid))
      cannot_proceed_due_to_fixed_objects = true;
    edges.push(...uil_get_edges_of_face(fid));
  });
  edges.push(...uid_selected_edges.keys());
  edges = [...new Set(edges)];
  edges.forEach(function(eid){
    if (uil_is_object_fixed(eid))
      cannot_proceed_due_to_fixed_objects = true;
  });
  if (!cannot_proceed_due_to_fixed_objects && !App.state.editing_edge_tangent_mode) {
    var hanging_edges = [];
    edges.forEach(function(eid){
      var vs = uil_get_vertices_of_edge(eid);
      vs.forEach(function(vid){
        hanging_edges.push(...uil_get_edges_of_vertex(vid));
      });
    });
    edges.push(...hanging_edges);
    edges = [...new Set(edges)];
    edges.forEach(function(eid){
      if (uil_is_object_fixed(eid))
      cannot_proceed_due_to_fixed_objects = true;
    });
  }
  if (cannot_proceed_due_to_fixed_objects) {
    display_message_to_user(LOG.WARN, "Cannot scale set affecting fixed objects.")
    return;
  }

  if (App.state.snapping_to_object) {
    // Stop snapping
    abort_snap_to_object();
}
  var rigid_move_vertex_object_ids = [];
  var rigid_move_edge_object_ids = [];
  var rigid_move_face_object_ids = [];

  // Collect any selected vertices (currently none or one is allowed, but check for 2 as well)
  if (uid_selected_vertices.length >= 1)
    rigid_move_vertex_object_ids[0] = uid_selected_vertices.getKeyAtIndex(0);
  if (uid_selected_vertices.length >= 2)
    rigid_move_vertex_object_ids[1] = uid_selected_vertices.getKeyAtIndex(1);

  // Assemble all selected edges/faces
  uid_selected_edges.keys().forEach(function(eid) {
    rigid_move_edge_object_ids.push(eid);
  });
  uid_selected_faces.keys().forEach(function(fid) {
      rigid_move_face_object_ids.push(fid);
  });

  // If it's for tangent and there are no selected vertices, add the closest vertex on the edge
  if (is_for_edge_tangent && uid_selected_edges.length == 1 && uid_selected_vertices.length == 0 && uid_selected_edge_click_point != null) {
    var vid = find_edge_closest_vertex_to_coordinates(uid_selected_edges.firstKey(), uid_selected_edge_click_point)
    uid_select_object(vid);
    rigid_move_vertex_object_ids[0] = vid;
  }

  if (!only_temporarily) {
    uid_store_and_deselect_all_objects();
    //make_all_faces_semitransparent();
    uid_reselect_all_stored_objects();
    request_setting_button_states();
    uid_face_rendering_type_before_move_manipulator = uid_face_rendering_type;
    App.state.hidden_edges_visible = true;
    uid_face_rendering_type = "transparent";
  }

  var is_for_fence = (
    modifier
    && rigid_move_edge_object_ids.length == 1
    && rigid_move_face_object_ids.length == 0
    && rigid_move_vertex_object_ids.length == 0
  );

  // If it's for edge tangent, check whether the specific edge tangent is fixed, and if so unfix it.
  if (is_for_edge_tangent && rigid_move_edge_object_ids.length == 1 && rigid_move_vertex_object_ids.length == 1 &&
    rigid_move_face_object_ids.length == 0 &&
    uil_get_vertices_of_edge(rigid_move_edge_object_ids[0]).includes(rigid_move_vertex_object_ids[0])) {
      var wv_is_tan = new WrappedValue();
      var res = uil_get_edge_fixing_of_tangent_at_vertex(rigid_move_edge_object_ids[0], rigid_move_vertex_object_ids[0], wv_is_tan);
      if (wv_is_tan.v)
        unfix_edge_tangent_at_vertex(rigid_move_edge_object_ids[0], rigid_move_vertex_object_ids);
  }

  uid_rigid_move_manipulator = new RigidMoveManipulator(
    rigid_move_face_object_ids,
    rigid_move_edge_object_ids,
    rigid_move_vertex_object_ids,
    is_for_fence,
    is_for_edge_tangent,
    viewSize,
    viewsize_to_vertex_diameter_ratio.value,
  );
  uid_rigid_move_manipulator.display(use_local_coordinate_system, use_prestored_tmp_placement);

  if (required_to_operate)
    uid_rigid_move_manipulator.set_required_to_operate();

  if (!only_temporarily)
    suppress_undo_tracking();

  animate();

  App.state.rigid_move_manipulator = true;
  uid_raynode = uid_manip_raynode;
  uid_objects_raynode.remove(uid_axes_planes_raynode);
  if (App.state.axes_visible)
    uid_manip_raynode.add(uid_axes_planes_raynode);

  if (must_set_button_states)
    set_button_states();
  animate();
}

export function uid_hide_rigid_move_manipulator(only_temporarily, pop_length, must_set_button_states) {

  if (!App.state.rigid_move_manipulator)
    return;

  let manipulator_is_required_to_operate = uid_rigid_move_manipulator.is_required_to_operate();
  let manipulator_has_operated = uid_rigid_move_manipulator.has_operated();

  var edge_tangent_edge_object = 0;
  var edge_tangent_vertex_object = 0;
  var is_for_edge_tangent = false;
  if (uid_rigid_move_manipulator.is_for_edge_tangent) {
    // Make a note of the edge and vertex object if it was an edge tangent move.
    edge_tangent_edge_object = uid_rigid_move_manipulator.rigid_move_edge_object_ids[0];
    edge_tangent_vertex_object = uid_rigid_move_manipulator.rigid_move_vertex_object_ids[0];
    is_for_edge_tangent = true;
  }

  // First we take care of the value that would appear in the readvalue box.
  pop_amount_value = pop_length;

  uid_rigid_move_manipulator.remove();
  if (!only_temporarily) {
    uid_deselect_all_objects();
    request_setting_button_states();
    if (uid_face_rendering_type_before_move_manipulator == "solid") {
      App.state.hidden_edges_visible = false;
      uid_face_rendering_type = "solid";
    }
    if (App.state.axes_visible)
      uid_objects_raynode.add(uid_axes_planes_raynode);
  }

  App.state.rigid_move_manipulator = false;
  uid_raynode = uid_objects_raynode;
  uid_manip_raynode.remove(uid_axes_planes_raynode);

  if (is_for_edge_tangent) {
    // Fix the edge tangent
    fix_edge_tangent_at_vertex(edge_tangent_edge_object, [edge_tangent_vertex_object]);
  }

  if (!only_temporarily) {
    if (manipulator_is_required_to_operate) {
      unsuppress_undo_tracking_if_ready(true);
      //display_message_to_user(LOG.INFO, "don't save state")

      // If the user just did a copy body and didn't move anything, we need to save an entry in the undo stack (it won't have
      // been saved when they hit ctrl-C), and then do an undo. Also inform the user.
      if (uid_state.rigid_move_manipulator_shown_after_copy_body) {
        uid_state.rigid_move_manipulator_is_shown = false;
        uid_raynode = uid_objects_raynode;
        uil_save_undo_state();
        uil_undo();
        display_message_to_user(LOG.WARN, "We have removed the copy you just made to avoid it remaining superimposed over the original!");
      }
      uid_state.rigid_move_manipulator_shown_after_copy_body = false;
    }
    else if (!manipulator_has_operated) {
      unsuppress_undo_tracking_if_ready(true);
    }
    else {
      unsuppress_undo_tracking_if_ready(false);
    }
  }

  if (must_set_button_states)
    set_button_states();
  animate();
}

export function uid_toggle_edge_tangent_manipulators() {

  if (!uid_state.edge_tangent_manipulators_are_shown) {
    // First check that we are not manipulating the tangent of a fixed edge, or of an edge that is tangent to a fixed edge.
    var eid = uid_selected_edges.firstKey();
    var cannot_modify_first_vertex = false;
    var cannot_modify_second_vertex = false;
    if (uil_is_object_fixed(eid)) {
      var cannot_modify_first_vertex = true;
      var cannot_modify_second_vertex = true;
    }
    if (!cannot_modify_first_vertex || !cannot_modify_second_vertex) {
      // Check for any adjacent edges that are fixed and tangent to this one.
      var vs = uil_get_vertices_of_edge(eid);
      var es1 = uil_get_edges_of_vertex(vs[0]);
      var es2 = uil_get_edges_of_vertex(vs[1]);
      es1.forEach(function(eid2){
        if (eid2 != eid && uil_is_object_fixed(eid2) && uil_is_joint_smooth(eid, eid2) == 1)
          cannot_modify_second_vertex = true;
      })
      es2.forEach(function(eid2){
        if (eid2 != eid && uil_is_object_fixed(eid2) && uil_is_joint_smooth(eid, eid2) == 1)
          cannot_modify_second_vertex = true;
      })
    }
    if (cannot_modify_first_vertex && cannot_modify_second_vertex) {
      display_message_to_user(LOG.WARN, "Cannot change tangent of a fixed edge or an edge tangent to a fixed edge.");
      return;
    }

    if (!cannot_modify_first_vertex) {
      uid_edge_tangent_1st_manipulator = new Uid_edge_tangent_manipulator_object(uid_selected_edges.firstValue(), 1);
      uid_edge_tangent_1st_manipulator.display();
    }
    if (!cannot_modify_second_vertex) {
      uid_edge_tangent_2nd_manipulator = new Uid_edge_tangent_manipulator_object(uid_selected_edges.firstValue(), 2);
      uid_edge_tangent_2nd_manipulator.display();
    }

    uid_state.edge_tangent_manipulators_are_shown = true;
    uid_raynode = uid_manip_raynode;
    uid_objects_raynode.remove(uid_axes_planes_raynode);
    if (App.state.axes_visible)
      uid_manip_raynode.add(uid_axes_planes_raynode);
  }
  else {
    if (uid_edge_tangent_1st_manipulator)
      uid_edge_tangent_1st_manipulator.remove();
    if (uid_edge_tangent_2nd_manipulator)
      uid_edge_tangent_2nd_manipulator.remove();
    uid_edge_tangent_1st_manipulator = null;
    uid_edge_tangent_2nd_manipulator = null;

    uid_state.edge_tangent_manipulators_are_shown = false;
    uid_raynode = uid_objects_raynode;
    uid_manip_raynode.remove(uid_axes_planes_raynode);
    if (App.state.axes_visible)
      uid_objects_raynode.add(uid_axes_planes_raynode);

    uid_deselect_all_objects();
  }
  animate();
}

export function uid_start_scale_objects(use_first_vertex_as_center_if_possible, dont_call_scaling_function = false) {
  if (uid_selected_faces.length == 1 && uid_selected_edges.length== 0) {
    // There is only one selected face, and no selected edges
    scale_face_object = uid_selected_faces.firstValue().id;
    scale_edge_objects = [];
    scale_edge_objects = uil_get_edges_of_face(scale_face_object);
  }
  else {
    // There are one or more selected faces / edges OR no selected faces / edges
    scale_face_object = null;
    scale_edge_objects = [];
    uid_selected_edges.values().forEach(function(e) {
      // We know that there's only one element
      scale_edge_objects.push(e.id);
    });
    uid_selected_faces.values().forEach(function(f) {
      // We know that there's only one element
      var edge_ids = uil_get_edges_of_face(f.id);
      edge_ids.forEach(function(e_id) {
        scale_edge_objects.push(e_id);
      });
    });
    // Remove duplicates
    var unique = [];
    $.each(scale_edge_objects, function(i, el) {
      if ($.inArray(el, unique) === -1) unique.push(el);
    });
    scale_edge_objects = unique.slice(0);
  }

  if ((uid_selected_vertices.length == 1 || uid_selected_vertices.length == 2) && use_first_vertex_as_center_if_possible) {
    var scale_vertex_object = uid_selected_vertices.firstValue();
    scale_center.x = scale_vertex_object.object3d.position.x;
    scale_center.y = scale_vertex_object.object3d.position.y;
    scale_center.z = scale_vertex_object.object3d.position.z;
  }
  else {
    // XXX Get center from edge list.
    var norm_coords = new THREE.Vector3();
    var wv_radius = new WrappedValue(0);
    var wv_flatness = new WrappedValue(0);
    var characteristic_dir_coords = new THREE.Vector3();

    if (scale_face_object == null)
      uil_get_edge_list_geometry(scale_edge_objects, false, scale_center, norm_coords, wv_radius, characteristic_dir_coords);
    else
      uil_get_face_geometry(scale_face_object, scale_center, norm_coords, wv_radius, characteristic_dir_coords, wv_flatness);
  }
  if (!dont_call_scaling_function)
    uil_start_scale_edge_list(scale_edge_objects);
}

export function uid_scale_objects(factor, axis, use_first_vertex_as_center_if_possible, center=undefined) {
  if (factor < 0.001 || factor > 1000) {
    display_message_to_user(LOG.WARN, "Scale out of range");
    return;
  }

  if (scale_edge_objects.length == 0 || !center) {
    uid_start_scale_objects(use_first_vertex_as_center_if_possible, true);
    center = scale_center;
  }

  scale_factor = factor;

  if (scale_factor <= 0 || isNaN(scale_factor))
    scale_factor = 1;
  const nullvector = new THREE.Vector3();
  if (axis)
    uil_scale_edge_list(scale_edge_objects, center, scale_factor, true, axis);
  else
    uil_scale_edge_list(scale_edge_objects, center, scale_factor, false, nullvector);
  animate();
}

export function uid_end_scale_objects() {
  uil_end_scale_edge_list(scale_edge_objects);
  scale_face_object = null;
  scale_edge_objects = [];
  //hide_modal("scaleModal");
}

export function uid_start_offset_face(face_id, vertex_id, point_coordinates) {
  suppress_undo_tracking();
  var res = uil_start_offset_face(face_id, vertex_id, point_coordinates);
  if (res)
    App.state.offset_face_dragging = true;
  else
    interrupt_offset_face();
}

export function uid_offset_face_drag_step(face_id, vertex_id, point_coordinates) {
  var res = uil_offset_face_drag_step(face_id, vertex_id, point_coordinates);
  if (!res)
    interrupt_offset_face();
}

export function uid_end_offset_face(face_id, vertex_id) {
  var res = uil_end_offset_face(face_id, vertex_id);
  unsuppress_undo_tracking_if_ready(false);
}

export function uid_translate_body(body_id, start_ray_base, start_ray_direction) {
  // XXX
}

export function uid_merge_selected_faces() {
  var face_ids = new Array();
  uid_selected_faces.values().forEach(function(v) {
    face_ids.push(v.id);
  });
  uid_deselect_all_objects();

  return uil_merge_faces(face_ids, false, 0, 0);
}

export const uid_splitpatch_faces = withSpinner("Splitting face(s)",
async function uid_splitpatch_faces(faces, do_triangles = false, make_new_edges_linear = false, allow_many_faces = false) {
    const a_face_is_fixed = uid_selected_faces.keys().some(fid => uil_is_object_fixed(fid));
    if (a_face_is_fixed) {
        display_message_to_user(LOG.WARN, "Cannot split-patch fixed faces.");
        return;
    }

    const a_face_is_sketch = uid_selected_faces.keys().some(fid => face_is_for_sketch(fid));
    if (a_face_is_sketch) {
        display_message_to_user(LOG.WARN, "Cannot split-patch sketch faces.");
        return;
    }

    const too_many_faces = faces.length > 6 && !allow_many_faces;
    if (too_many_faces)  {
        display_message_to_user(LOG.WARN, "You can splitpatch up to 6 faces at a time. Please select fewer faces and try them in batches of up to 6.");
        return;
    }

    suppress_undo_tracking();
    suppress_animate();
    uid_deselect_all_objects();

    // As a special case, if there is an invisible face in the model
    // sharing all edges with a face being splitpatched,
    // splitpatch that one too.
    const newfaceid = uid_faces.keys()
        .filter(fid => !uil_is_object_visible(fid))
        .find(fid =>
            faces.flatMap(uil_get_edges_of_face)
                .every(inside(uil_get_edges_of_face(fid))));
    if (newfaceid)
        faces.push(newfaceid);

    await withProgress('Splitting faces', (fid) => {
        const bid = uil_get_body_of_face(fid);
        const currently_visible_faces = new Set();

        if (!uil_is_object_visible(fid)) {
            uid_faces.keys()
                .filter(fid => uil_get_body_of_face(fid) === bid && is_object_visible(fid))
                .forEach(fid => currently_visible_faces.add(fid));
            uil_make_faces_visible([fid]);
        }

        if (do_triangles)
            split_face_into_triangles(fid, false, make_new_edges_linear, 0, 0, false, []);
        else
            uid_splitpatch_face(fid, make_new_edges_linear);

        if (currently_visible_faces.size > 0)
            uid_faces.keys()
                .filter(fid => uil_get_body_of_face(fid) === bid && !currently_visible_faces.has(fid))
                .forEach(fid => uil_make_faces_invisible([fid], false));
    })(faces);

    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
});

export function uid_perform_crosshair(eventcode) {
  if (!shift_pressed())
    return;
  uid_deselect_all_objects();
  if (App.state.grid_visible && snap_to_grid && viewing_down_orthogonal_plane) {
    var normalized_on_grid = get_grid_point(pointer_normalised, uid_grid_origin, uid_grid_normal, uid_grid_min, uid_grid_max, uid_grid_step);
    var mousenormalized_snapped = from_3d_coordinates_to_2d_screen_coordinates(normalized_on_grid);
    uid_raycaster.setFromCamera(mousenormalized_snapped, uid_camera);
  }
  else {
    uid_raycaster.setFromCamera(pointer_normalised, uid_camera);
  }
  var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
  var intersected_obj = find_intersection_object(intersects);

  if (intersected_obj) {
    var o3d = uid_raynode.getObjectById(intersected_obj.object.id);
    var uido = uid_object3dIDToUid_object.getItem(o3d.id);
    if (uido && uido.type == "face") {
      var edge_ids = [];
      var coords = [];
      var wv_u = new WrappedValue();
      var wv_v = new WrappedValue();
      var intersection_point;
      intersection_point = uil_get_face_ray_intersection(uido.id, uid_raycaster.ray.origin, uid_raycaster.ray.direction, wv_u, wv_v, true);
      if (!intersection_point) {
        console_log_xml("ERROR in uil_get_face_ray_intersection");
        animate();
        return;
      }
      var res = uil_get_face_crosshair_at_params(uido.id, wv_u.v, wv_v.v, edge_ids, coords);

      // If we're putting the crosshair on discrete fractions of the edge length, we have to adapt coords accordingly
      if (put_crosshair_at_edge_eighths) {
        for (var i = 0; i < edge_ids.length; i++) {
          var min_point = new THREE.Vector3;
          var min_dist = 100;
          for (var j = 0.0; j <= 1.0; j += crosshair_edge_fraction) {
            var p = uil_get_point_at_edge_param(edge_ids[i], j);
            if (vector3.point_point_distance(p, coords[i]) < min_dist ) {
              min_dist = vector3.point_point_distance(p, coords[i]);
              min_point.copy(p);
            }
          }
          coords[i].copy(min_point);
        }
      }

      if (!res) {
        console_log("ERROR in uil_get_face_crosshair_at_params");
        animate();
        return;
      }

      hide_crosshair();
      suppress_undo_tracking();
      var v1 = null;
      var v2 = null;
      var v3 = null;
      var v4 = null;
      if (eventcode == "R" || eventcode == "G" ) {
        // Add just one edge
        if (eventcode == "R") {
          // Add new vertex unless we're hitting an existing one
          var v1 = uid_add_new_vertex_or_return_current_vertex( edge_ids[0], coords[0], uid_raycaster.ray.direction );
          var v2 = uid_add_new_vertex_or_return_current_vertex( edge_ids[1], coords[1], uid_raycaster.ray.direction );
        }
        else {
          var v1 = uid_add_new_vertex_or_return_current_vertex( edge_ids[2], coords[2], uid_raycaster.ray.direction );
          var v2 = uid_add_new_vertex_or_return_current_vertex( edge_ids[3], coords[3], uid_raycaster.ray.direction );
        }
        if (!v1 || !v2) {
          unsuppress_undo_tracking_if_ready(false);
          if (v1 || v2)
            uil_undo();
          display_message_to_user(LOG.WARN, "Cannot add crosshair at this point. Perhaps too close to existing edge.");
          animate();
          return;
        }
        var new_edge_id = add_edge_between_two_vertices_on_face(v1, v2, uido.id, null, false);
        unsuppress_undo_tracking_if_ready(false);
      }
      else {
        // Add both edges
        var v1 = uid_add_new_vertex_or_return_current_vertex( edge_ids[0], coords[0], uid_raycaster.ray.direction );
        var v2 = uid_add_new_vertex_or_return_current_vertex( edge_ids[1], coords[1], uid_raycaster.ray.direction );

        // Check that the 3rd and 4th edges still exist and were not "broken" by v1 or v2.
        if (!isEdge(edge_ids[2]))
          edge_ids[2] = find_closest_edge_to_point(uil_get_edges_of_face(uido.id), coords[2]);
        if (!isEdge(edge_ids[3]))
          edge_ids[3] = find_closest_edge_to_point(uil_get_edges_of_face(uido.id), coords[3]);

        var v3 = uid_add_new_vertex_or_return_current_vertex( edge_ids[2], coords[2], uid_raycaster.ray.direction );
        var v4 = uid_add_new_vertex_or_return_current_vertex( edge_ids[3], coords[3], uid_raycaster.ray.direction );
        if (!v1 || !v2 || !v3 || !v4) {
          unsuppress_undo_tracking_if_ready(false);
          if (v1 || v2 || v3 || v4)
            uil_undo();
          display_message_to_user(LOG.WARN, "Cannot add crosshair at this point. Perhaps too close to existing edge.");
          animate();
          return;
        }
        var new_edge_id = add_edge_between_two_vertices_on_face(v1, v2, uido.id, null, false);
        if (!new_edge_id) {
          unsuppress_undo_tracking_if_ready(false);
          uil_undo();
          display_message_to_user(LOG.WARN, "Cannot add crosshair at this point. Perhaps too close to existing edge.");
          animate();
          return;
        }
        // Add vertex on edge at crosshair hit point
        // We cannot simply use the intersection point, as we are now adding the crosshair edges at discrete fractions
        // of the edges around it. So we'll have to find the point of intersection of the two new lines that will be added.
        // We distinguish two cases: Planar faces and non-planar faces.
        if (face_is_flat(uido.id)) {
          // Face is flat. We can intersect the two edges, or in fact form a plane from the first pair of edges and intersect with
          // a ray between the other two.
          // Plane base will be v1. Plane normal will be (v2-v1) X raycaster direction
          // Ray base will be vector3. Ray direction will be v4-v3
          var o = new THREE.Vector3();
          var n = new THREE.Vector3();
          var b = new THREE.Vector3();
          var d = new THREE.Vector3();
          o.copy(uid_vertices.getItem(v1).object3d.position);
          var v = new THREE.Vector3();
          v.subVectors(uid_vertices.getItem(v2).object3d.position, uid_vertices.getItem(v1).object3d.position)
          n.crossVectors(v, uid_raycaster.ray.direction).normalize();
          b.copy(uid_vertices.getItem(v3).object3d.position);
          d.subVectors(uid_vertices.getItem(v4).object3d.position, uid_vertices.getItem(v3).object3d.position).normalize();
          intersection_point = plane_ray_intersect(o, n, b, d, 1.0);
        }
        else {
          // Face is non-flat
          // In this case we need to work with the samples.
          var sample_points1 = [];
          var sample_normals1 = [];
          var res1 = calculate_sample_planar_points_for_add_edge(50, v1, v2, uido.id, find_face_normal(uido.id, intersected_obj),
                                                                sample_points1, sample_normals1);

          var sample_points2 = [];
          var sample_normals2 = [];
          var res2 = calculate_sample_planar_points_for_add_edge(50, v3, v4, uido.id, find_face_normal(uido.id, intersected_obj),
                                                                sample_points2, sample_normals2);
          if (res1 && res2) {
            var min_dist = 100;
            var min_sample = new THREE.Vector3();
            for (var i = 0; i <= sample_points1.length ; i++ ) {
              for (var j = 0; j <= sample_points2.length; j++) {
                var d = vector3.point_point_distance(sample_points1[i], sample_points2[j]);
                if (d < min_dist) {
                  min_dist = d;
                  min_sample.copy(sample_points1[i]);
                }
              }
            }
            intersection_point.copy(min_sample);
          }
        }

        var middle_vertex_id = uil_add_new_vertex(new_edge_id, intersection_point, uid_raycaster.ray.direction);

        // Find faces containing middle vertex and v3, and middle vertex and v4.
        var face_id_for_v3 = 0;
        var face_id_for_v4 = 0;
        var edges_middle = uil_get_edges_of_vertex(middle_vertex_id);
        edges_middle.forEach(function(eid){
          var fa = uil_get_faces_of_edge(eid);
          fa.forEach(function(fid){
            if (uil_is_vertex_of_face(v3, fid))
              face_id_for_v3 = fid;
            if (uil_is_vertex_of_face(v4, fid))
              face_id_for_v4 = fid;
          });
        });
        // Add the relevant edges
        if (face_id_for_v3)
          var eid_v3 = add_edge_between_two_vertices_on_face(v3, middle_vertex_id, face_id_for_v3, null, false);
        if (face_id_for_v4)
          var eid_v4 = add_edge_between_two_vertices_on_face(v4, middle_vertex_id, face_id_for_v4, null, false);
      }
    }
  }
  unsuppress_undo_tracking_if_ready(false);
  animate();
}

export function uid_add_new_vertex_or_return_current_vertex( edge_id, ray_base, ray_direction ) {
  var edge_new_vertex_distance_threshold = 0.075; // API will fail for 0.05 < t < 0.95, so give it some "breathing space".
  var len = uil_get_edge_length(edge_id);
  var vs = uil_get_vertices_of_edge(edge_id);
  var d0 = vector3.line_point_distance(ray_base, ray_direction, uid_vertices.getItem(vs[0]).object3d.position);
  var d1 = vector3.line_point_distance(ray_base, ray_direction, uid_vertices.getItem(vs[1]).object3d.position);

  if (d0 < len * edge_new_vertex_distance_threshold)
    return vs[0];
  else if (d1 < len * edge_new_vertex_distance_threshold)
    return vs[1];
  else
    return uil_add_new_vertex( edge_id, ray_base, ray_direction );
}

export const uid_fill_faces = withSpinner('Filling gap between faces', async function uid_fill_faces(edge_ids = []) {

  // We handle different cases:
  // - If the user has selected two sets of edges on two different bodies, and each set forms a continuous chain, and on each edge
  //   there is a visible face on one side and an invisible on the other, we do a special bridging between the two bodies.
  // - If the user has selected one set of edges on a single body, and the edges are continuous, and on each edge there is a visible
  //   face on one side and an invisible one the other, we do a special dissolve + add edge between the set of edges
  // - In all other cases we turn the invisible faces to visible.

  suppress_animate();
  suppress_undo_tracking();
  suppress_core_face_tessellation();

  if (edge_ids.length == 0)
    edge_ids = [... uid_selected_edges.keys()];

  properly_split_invisible_face_connected_components([],false, false);

  // Check that all edges are on border of visible faces (not guaranteed yet)
  var all_edges_are_on_border = true;
  edge_ids.forEach(function(eid){
    var fs = uil_get_faces_of_edge(eid);
    if (((uil_is_object_visible(fs[0]) && uil_is_object_visible(fs[1])) ||
         (!uil_is_object_visible(fs[0]) && !uil_is_object_visible(fs[1]))))
      all_edges_are_on_border = false;
  });

  if (!all_edges_are_on_border) {
    uid_fill_faces_with_existing_invisible_faces(edge_ids);
    uid_deselect_all_objects();
    unsuppress_core_face_tessellation_if_ready();
    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
    return;
  }

  // Here we proceed with bridging edges at border of open faces.

  var edge_lists = get_lists_of_connected_edges_in_edge_list(edge_ids, true);

  if (edge_lists.length == 2) {
    var b1id = uil_get_body_of_edge(edge_lists[0][0]);
    var b2id = uil_get_body_of_edge(edge_lists[1][0]);
    if (b1id != b2id) {
      uid_bridge_edges_with_invisible_faces_on_them_on_two_bodies(b1id, b2id, edge_lists[0], edge_lists[1]);
    }
    else {
      uid_bridge_edges_with_invisible_faces_on_them_on_same_body(b1id, edge_lists[0], edge_lists[1]);
    }
  }
  else if (edge_lists.length == 1 && edge_lists[0].length == 1) {
    // The user has selected just one single edge. We'll see if it exactly matches another edge on another face
    // by comparing their two vertex locations
    var e1id = edge_lists[0][0];
    var v1aid = uil_get_vertices_of_edge(e1id)[0];
    var v1bid = uil_get_vertices_of_edge(e1id)[1];

    var matching_edge_id = 0;
    uid_edges.keys().forEach(function(e2id){
      if (e1id != e2id) {
        var v2aid = uil_get_vertices_of_edge(e2id)[0];
        var v2bid = uil_get_vertices_of_edge(e2id)[1];
        var daa = vector3.point_point_distance(uid_vertices.getItem(v1aid).object3d.position, uid_vertices.getItem(v2aid).object3d.position, )
        var dab = vector3.point_point_distance(uid_vertices.getItem(v1aid).object3d.position, uid_vertices.getItem(v2bid).object3d.position, )
        var dba = vector3.point_point_distance(uid_vertices.getItem(v1bid).object3d.position, uid_vertices.getItem(v2aid).object3d.position, )
        var dbb = vector3.point_point_distance(uid_vertices.getItem(v1bid).object3d.position, uid_vertices.getItem(v2bid).object3d.position, )
        var thresh = 1e-4;
        if ((daa < thresh && dbb < thresh) || (dab < thresh && dba < thresh)) {
          // Make sure we only find one matching edge. If a matching edge whas already found before,
          // set the id to -1, meaning more than 1 were found.
          if (matching_edge_id == 0)
            matching_edge_id = e2id;
          else
            matching_edge_id = -1;
        }
      }
    });
    if (matching_edge_id == 0) {
      // Found no matching edges
      display_message_to_user(LOG.WARN, "We found no matching edges to the selected one, please explicitly select both edges.")
    }
    else if (matching_edge_id == -1) {
      // Found more than one matching edges
      display_message_to_user(LOG.WARN, "We found more than one matching edges to the selected one, please explicitly select both edges.")
    }
    else {
      // Found one single proper matching edge
      var b1id = uil_get_body_of_edge(e1id);
      var b2id = uil_get_body_of_edge(matching_edge_id);
      if (b1id != b2id) {
        uid_bridge_edges_with_invisible_faces_on_them_on_two_bodies(b1id, b2id, [e1id], [matching_edge_id]);
      }
      else {
        uid_bridge_edges_with_invisible_faces_on_them_on_same_body(b1id, [e1id], [matching_edge_id]);
      }

    }

  }

  else if (edge_lists.length == 1 && edge_lists[0].length > 1) {
    var bid = uil_get_body_of_edge(edge_lists[0][0]);
    var visible_faces = [];
    var invisible_faces = [];
    uid_faces.keys().forEach(function(fid){
      if (uil_get_body_of_face(fid) == bid) {
        if (uil_is_object_visible(fid))
          push_nodups(fid, visible_faces);
        else
          push_nodups(fid, invisible_faces);
      }
    });

    var res = uid_connect_edges_with_invisible_faces_on_them(edge_lists[0]);
    if (res == 0) {
      // Above attempt failed. Try this: Merge all invisible faces into as big connected components as possible,
      // then try again.

      // First reset to visible/invisible
      uil_make_faces_invisible(invisible_faces, false);
      uil_make_faces_visible(visible_faces);

      // Group invisible objects into connected components
      var invisible_connected_components = uil_get_face_connected_components(invisible_faces);
      if (!invisible_connected_components || invisible_connected_components.length == 0) {
        unsuppress_animate_if_ready();
        unsuppress_core_face_tessellation_if_ready();
        unsuppress_undo_tracking_if_ready(false);
        return 0;
      }

      invisible_connected_components.forEach(function(c){
        var new_merged_face_ids = uil_merge_faces(c, false, 0, 0);
      });

      var res = uid_connect_edges_with_invisible_faces_on_them(edge_lists[0]);
    }
  }
  else {
    uid_fill_faces_with_existing_invisible_faces(edge_ids);
    uid_deselect_all_objects();
    unsuppress_animate_if_ready();
    unsuppress_core_face_tessellation_if_ready();
    unsuppress_undo_tracking_if_ready(false);
    return;
  }

  unsuppress_animate_if_ready();
  unsuppress_core_face_tessellation_if_ready();
  unsuppress_undo_tracking_if_ready(false);
  uid_deselect_all_objects();
});

export function uid_bridge_edges_with_invisible_faces_on_them_on_two_bodies(b1id, b2id, edge_ids1, edge_ids2) {
  // Note that b1id and b2id may be the same or different, depending if we're bridging between different objects.

  if (edge_ids1.length != 1 || edge_ids2.length != 1) {
    display_message_to_user(LOG.WARN, "Please start by bridging one edge on each side. You can then proceed bridging more edges together.");
    return 0;
  }

  suppress_animate();
  suppress_undo_tracking();

  var edge_id1 = edge_ids1[0];
  var edge_id2 = edge_ids2[0];

  /*
  var listified_edges = [];
  var wv_is_open_list1 = new WrappedValue();
  var wv_is_open_list2 = new WrappedValue();
  uil_listify_edges(edge_ids1, wv_is_open_list1, listified_edges);
  uil_listify_edges(edge_ids2, wv_is_open_list2, listified_edges);
  if (!wv_is_open_list1.v || !wv_is_open_list2.v) {
    display_message_to_user(LOG.WARN, "You currently cannot bridge between closed loops of edges. Please bridge a few edges at a time.");
    return 0;
  }
  */

  uid_deselect_all_objects();

  uid_set_fixing_of_objects(uid_faces.keys(), false);
  uid_set_fixing_of_objects(uid_edges.keys(), false);
  uid_set_fixing_of_objects(uid_vertices.keys(), false);

  var res1 = split_invisible_part_of_body(b1id, [edge_id1]);

  var face_id1;
  fs = uil_get_faces_of_edge(edge_id1);
  if (!uil_is_object_visible(fs[0]))
    face_id1 = fs[0];
  else
    face_id1 = fs[1];

  // Find endpoints of edge list
  var vs1 = find_end_vertices_of_edge_list([edge_id1]);

  var res2 = split_invisible_part_of_body(b2id, [edge_id2]);
  var face_id2;
  fs = uil_get_faces_of_edge(edge_id2);
  if (!uil_is_object_visible(fs[0]))
    face_id2 = fs[0];
  else
    face_id2 = fs[1];

  // Find endpoints of edge list
  var vs2 = find_end_vertices_of_edge_list([edge_id2]);

  var v1Aid = vs1[0];
  var v1Bid = vs1[1];
  // Test distances between pairs
  // Case A:
  var v2Aid = vs2[0];
  var v2Bid = vs2[1];
  var d1 = vector3.point_point_distance(uid_vertices.getItem(v1Aid).object3d.position, uid_vertices.getItem(v2Aid).object3d.position) +
           vector3.point_point_distance(uid_vertices.getItem(v1Bid).object3d.position, uid_vertices.getItem(v2Bid).object3d.position);
  // Case B:
  v2Aid = vs2[1];
  v2Bid = vs2[0];
  var d2 = vector3.point_point_distance(uid_vertices.getItem(v1Aid).object3d.position, uid_vertices.getItem(v2Aid).object3d.position) +
           vector3.point_point_distance(uid_vertices.getItem(v1Bid).object3d.position, uid_vertices.getItem(v2Bid).object3d.position);
  if (d1 < d2) {
    v2Aid = vs2[0];
    v2Bid = vs2[1];
  }
  else {
    v2Aid = vs2[1];
    v2Bid = vs2[0];
  }

  var v1id, v2id;

  if (vector3.point_point_distance(uid_vertices.getItem(v1Aid).object3d.position, uid_vertices.getItem(v2Aid).object3d.position) <
      vector3.point_point_distance(uid_vertices.getItem(v1Bid).object3d.position, uid_vertices.getItem(v2Bid).object3d.position)) {
    v1id = v1Aid;
    v2id = v2Aid;
  }
  else {
    v1id = v1Bid;
    v2id = v2Bid;
  }

  var vs1 = vertices_of_face(face_id1);
  var vs2 = vertices_of_face(face_id2);

  uid_deselect_all_objects();

  // Before calling the handle (bridge) check whether we have 0-distance vertices between vertices of the two faces
  var zero_dist_count = 0;
  var eps = 1e-05;
  if (maths.proximate(vector3.point_point_distance(uid_vertices.getItem(v1Aid).object3d.position, uid_vertices.getItem(v2Aid).object3d.position), 0, eps))
    zero_dist_count++;
  if (maths.proximate(vector3.point_point_distance(uid_vertices.getItem(v1Bid).object3d.position, uid_vertices.getItem(v2Bid).object3d.position), 0, eps))
    zero_dist_count++;

  if (zero_dist_count > 0) {
    // Keep a list of all vertices of 2 visible faces attached to 2 edges to be bridged
    var vf1, vf2;
    var fs = uil_get_faces_of_edge(edge_id1);
    if (uil_is_object_visible(fs[0]))
      vf1 = fs[0];
    else
      vf1 = fs[1];
    var fs = uil_get_faces_of_edge(edge_id2);
    if (uil_is_object_visible(fs[0]))
      vf2 = fs[0];
    else
      vf2 = fs[1];

    var zero_dist_visible_face_vertices = [];
    vertices_of_face(vf1).forEach(function(vid){
      push_nodups(vid, zero_dist_visible_face_vertices);
    });
    vertices_of_face(vf2).forEach(function(vid){
      push_nodups(vid, zero_dist_visible_face_vertices);
    });
  }

  var visible_faces_before_bridging = [];
  var visible_faces_centers_of_mass = [];
  uid_faces.keys().forEach(function(fid){
    if (uil_is_object_visible(fid)) {
      push_nodups(fid, visible_faces_before_bridging);
      var vs = vertices_of_face(fid);
      var face_center_of_mass = new THREE.Vector3();
      vs.forEach(function(vid){
        var v = uid_vertices.getItem(vid).object3d.position;
        face_center_of_mass.add(v);
      });
      face_center_of_mass.multiplyScalar(1/(vs.length));
      visible_faces_centers_of_mass.push(face_center_of_mass);
    }
  });

  // Note: Under the current implementation, face_ids1 and face_ids2 will contain one face each. And edge_ids1 and edge_ids2 will contain
  // one edge each. This helps.

  var res = uil_make_hole_or_handle_multi([face_id1], [face_id2], false, v1id, v2id, false, shift_pressed());

  if (!res) {
    display_message_to_user(LOG.WARN, "Failed to bridge faces");
    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    return;
  }

  if (zero_dist_count == 0) {
    var all_faces_of_selected_edges1 = new HashTable();
    var all_faces_of_selected_edges2 = new HashTable();
    var new_faces = [];

    fs = uil_get_faces_of_edge(edge_id1);
    if (all_faces_of_selected_edges1.hasItem(fs[0]))
      all_faces_of_selected_edges1.setItem(fs[0], all_faces_of_selected_edges1.getItem(fs[0]) + 1);
    else
      all_faces_of_selected_edges1.setItem(fs[0], 1);
    if (all_faces_of_selected_edges1.hasItem(fs[1]))
      all_faces_of_selected_edges1.setItem(fs[1], all_faces_of_selected_edges1.getItem(fs[1]) + 1);
    else
      all_faces_of_selected_edges1.setItem(fs[1], 1);

    fs = uil_get_faces_of_edge(edge_id2);
    if (all_faces_of_selected_edges2.hasItem(fs[0]))
      all_faces_of_selected_edges2.setItem(fs[0], all_faces_of_selected_edges2.getItem(fs[0]) + 1);
    else
      all_faces_of_selected_edges2.setItem(fs[0], 1);
    if (all_faces_of_selected_edges2.hasItem(fs[1]))
      all_faces_of_selected_edges2.setItem(fs[1], all_faces_of_selected_edges2.getItem(fs[1]) + 1);
    else
      all_faces_of_selected_edges2.setItem(fs[1], 1);

    all_faces_of_selected_edges1.keys().forEach(function(fid) {
      if (all_faces_of_selected_edges2.hasItem(fid))
        push_nodups(fid, new_faces);
    });

    new_faces.forEach(function(fid) {
      var es = uil_get_edges_of_face(fid);
      es.forEach(function(eid){
        var efs = uil_get_faces_of_edge(eid);
        if (efs[0] != fid && uil_is_object_visible(efs[0]) && !all_faces_of_selected_edges1.hasItem(efs[0]) && !all_faces_of_selected_edges2.hasItem(efs[0]))
          uil_make_faces_invisible([efs[0]], false);
        if (efs[1] != fid && uil_is_object_visible(efs[1]) && !all_faces_of_selected_edges1.hasItem(efs[1]) && !all_faces_of_selected_edges2.hasItem(efs[1]))
          uil_make_faces_invisible([efs[1]], false);
      });
    });

    fs = uil_get_faces_of_edge(edge_id1);
    if (!uil_is_object_visible(fs[0]))
      uil_make_faces_visible([fs[0]]);
    if (!uil_is_object_visible(fs[1]))
      uil_make_faces_visible([fs[1]]);

    fs = uil_get_faces_of_edge(edge_id2);
    if (!uil_is_object_visible(fs[0]))
      uil_make_faces_visible([fs[0]]);
    if (!uil_is_object_visible(fs[1]))
      uil_make_faces_visible([fs[1]]);

  }
  else {
    // Identify the faces that are attached to the common edge(s), based on the vertices we kept a list of.
    if (zero_dist_count == 2) {
      var common_edge = 0;
      if (isEdge(edge_id1))
        common_edge = edge_id1;
      else
        common_edge = edge_id2;
      var visible_face1 = uil_get_faces_of_edge(common_edge)[0];
      var visible_face2 = uil_get_faces_of_edge(common_edge)[1];
      var faces_to_make_invisible = [];
      uil_get_vertices_of_edge(common_edge).forEach(function(vid){
        uil_get_edges_of_vertex(vid).forEach(function(eid){
          var fs = uil_get_faces_of_edge(eid);
          if (fs[0] != visible_face1 && fs[0] != visible_face2)
            push_nodups(fs[0], faces_to_make_invisible);
          if (fs[1] != visible_face1 && fs[1] != visible_face2)
            push_nodups(fs[1], faces_to_make_invisible);
        });
      });

      // Remove any faces that were already visible befor bridging
      for (var i = faces_to_make_invisible.length-1; i >= 0; i--) {
        var fid = faces_to_make_invisible[i];
        if (visible_faces_before_bridging.indexOf(fid) != -1)
          faces_to_make_invisible.splice(i,1);
        else {
          var vs = vertices_of_face(fid);
          var face_center_of_mass = new THREE.Vector3();
          vs.forEach(function(vid){
            var v = uid_vertices.getItem(vid).object3d.position;
            face_center_of_mass.add(v);
          });
          face_center_of_mass.multiplyScalar(1/(vs.length));
          var found = false;
          var eps = 1e-4;
          visible_faces_centers_of_mass.forEach(function(center) {
            if (center.distanceTo(face_center_of_mass) < eps)
              found = true;
          });
          if (found)
            faces_to_make_invisible.splice(i,1);
        }
      }

      uil_make_faces_invisible(faces_to_make_invisible, false);
    }
    else if (zero_dist_count == 1) {
      var first_edge = 0;
      if (isEdge(edge_id1))
        first_edge = edge_id1;
      else
        first_edge = edge_id2;
      var fs = uil_get_faces_of_edge(first_edge);
      var new_face = (fs[0] == face_id1 || fs[0] == face_id2) ? fs[1] : fs[0];
      var new_face_edges = uil_get_edges_of_face(new_face);

      var visible_faces = [];
      new_face_edges.forEach(function(eid){
        var fs = uil_get_faces_of_edge(eid);
        fs.forEach(function(fid){
          if (fid != new_face) {
            var vs = vertices_of_face(fid);
            var all_vertices_exist = true;
            vs.forEach(function(vid){
              if (zero_dist_visible_face_vertices.indexOf(vid) == -1)
                all_vertices_exist = false;
            });
            if (all_vertices_exist)
              push_nodups(fid, visible_faces);
          }
        });
      });

      push_nodups(new_face, visible_faces);

      var new_face_vertices = vertices_of_face(new_face);
      var faces_to_make_invisible = [];
      new_face_vertices.forEach(function(vid){
        uil_get_edges_of_vertex(vid).forEach(function(eid){
          var fs = uil_get_faces_of_edge(eid);
          if (visible_faces.indexOf(fs[0]) == -1)
            push_nodups(fs[0], faces_to_make_invisible);
          if (visible_faces.indexOf(fs[1]) == -1)
            push_nodups(fs[1], faces_to_make_invisible);
        });
      });
      // Remove any faces that were already visible befor bridging
      for (var i = faces_to_make_invisible.length-1; i >= 0; i--) {
        var fid = faces_to_make_invisible[i];
        if (visible_faces_before_bridging.indexOf(fid) != -1)
          faces_to_make_invisible.splice(i,1);
        else {
          var vs = vertices_of_face(fid);
          var face_center_of_mass = new THREE.Vector3();
          vs.forEach(function(vid){
            var v = uid_vertices.getItem(vid).object3d.position;
            face_center_of_mass.add(v);
          });
          face_center_of_mass.multiplyScalar(1/(vs.length));
          var found = false;
          var eps = 1e-4;
          visible_faces_centers_of_mass.forEach(function(center) {
            if (center.distanceTo(face_center_of_mass) < eps)
              found = true;

            /*
            // Check that all elements of vs are contained in visible_face_vertex_set
            let check_array_element_membership = (arr, target) => target.every(v => arr.includes(v));
            if (check_array_element_membership(visible_face_vertex_set, vs))
              found = true;
            */
          });
          if (found)
            faces_to_make_invisible.splice(i,1);
        }
      }
      uil_make_faces_invisible(faces_to_make_invisible, false);
    }
  }

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
}

export function uid_bridge_edges_with_invisible_faces_on_them_on_same_body(bid, edge_ids1, edge_ids2) {
  // First check whether either list of edges forms a closed loop, which we currently don't allow
  var listified_edges = [];
  var wv_is_open_list1 = new WrappedValue();
  var wv_is_open_list2 = new WrappedValue();
  uil_listify_edges(edge_ids1, wv_is_open_list1, listified_edges);
  uil_listify_edges(edge_ids2, wv_is_open_list2, listified_edges);
  if (!wv_is_open_list1.v || !wv_is_open_list2.v) {
    display_message_to_user(LOG.WARN, "You currently cannot bridge between closed loops of edges. Please bridge a few edges at a time.");
    return 0;
  }

  // Before starting, we process invisible part of both bodies to split them in triangles at all border vertices.
  var selected_vertices = [... uid_selected_vertices.keys()];

  uid_deselect_all_objects();

  suppress_animate();
  suppress_undo_tracking();

  // XXX Should only unfix hidden elements of bid, not everything!
  uid_set_fixing_of_objects(uid_faces.keys(), false);
  uid_set_fixing_of_objects(uid_edges.keys(), false);
  uid_set_fixing_of_objects(uid_vertices.keys(), false);

  // Assemble all visible and invisible objects
  var visible_faces = [];
  var invisible_faces = [];
  uid_faces.keys().forEach(function(fid){
    if (uil_get_body_of_face(fid) == bid) {
      if (uil_is_object_visible(fid))
        push_nodups(fid, visible_faces);
      else
        push_nodups(fid, invisible_faces);
    }
  });

  // Group invisible objects into connected components
  var invisible_connected_components = uil_get_face_connected_components(invisible_faces);
  if (!invisible_connected_components || invisible_connected_components.length == 0) {
    display_message_to_user(LOG.WARN, "Didn't find any gaps to connect.")
    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    return 0;
  }

  // See which of these components contains all of the edges in edge_ids1 and edge_ids2
  var all_edge_ids_to_connect = [];
  all_edge_ids_to_connect = all_edge_ids_to_connect.concat(edge_ids1);
  all_edge_ids_to_connect = all_edge_ids_to_connect.concat(edge_ids2);
  var invisible_faces_to_merge = [];
  var no_component_has_all_edges = true;
  invisible_connected_components.forEach(function(invisible_faces_component){
    var boundary_edge_ids = new Array();
    var boundary_vertex_ids = new Array();
    var wv_is_one_cc = new WrappedValue();
    var cc_res = uil_get_face_connected_component_info(invisible_faces_component, wv_is_one_cc, boundary_edge_ids, boundary_vertex_ids);
    var all_edges_are_on_this_component = true;
    all_edge_ids_to_connect.forEach(function(eid){
      if (boundary_edge_ids.indexOf(eid) == -1)
        all_edges_are_on_this_component = false;
    });
    if (all_edges_are_on_this_component) {
      invisible_faces_to_merge = invisible_faces_to_merge.concat(invisible_faces_component);
      no_component_has_all_edges = false;
    }
  });

  if (no_component_has_all_edges) {
    // We revert to treating this as 2 separate bodies (kinda)
    // XXX Not sure at all this will work.
    var res = uid_bridge_edges_with_invisible_faces_on_them_on_two_bodies(bid, bid, edge_ids1, edge_ids2);

    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    return res;
  }

  if (invisible_faces_to_merge == []) {
    display_message_to_user(LOG.WARN, "Unable to find a path that connects the two selected borders.")
    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    return 0;
  }

  // Merge all the faces into one
  var new_merged_face_id = 0;
  if (invisible_faces_to_merge.length == 1)
    new_merged_face_id = invisible_faces_to_merge[0];
  else {
    // Assemble all of the invisible faces before the merge
    var invisible_faces_before_merging = [];
    invisible_faces_before_merging = invisible_faces_before_merging.concat(invisible_faces);

    // Do the merging
    var new_merged_face_ids = uil_merge_faces(invisible_faces_to_merge, false, 0, 0);
    if (new_merged_face_ids.length == 0) {
      display_message_to_user(LOG.WARN, "Unable to connect selected borders.");
      unsuppress_undo_tracking_if_ready(false);
      unsuppress_animate_if_ready();
      return 0;
    }
    new_merged_face_id = new_merged_face_ids[0];
  }

  // Find endpoints of edge list
  var vs1 = find_end_vertices_of_edge_list(edge_ids1);
  var vs2 = find_end_vertices_of_edge_list(edge_ids2);

  var v1Aid = vs1[0];
  var v1Bid = vs1[1];
  // Test distances between pairs
  // Case A:
  var v2Aid = vs2[0];
  var v2Bid = vs2[1];
  var d1 = vector3.point_point_distance(uid_vertices.getItem(v1Aid).object3d.position, uid_vertices.getItem(v2Aid).object3d.position) +
           vector3.point_point_distance(uid_vertices.getItem(v1Bid).object3d.position, uid_vertices.getItem(v2Bid).object3d.position);
  // Case B:
  v2Aid = vs2[1];
  v2Bid = vs2[0];
  var d2 = vector3.point_point_distance(uid_vertices.getItem(v1Aid).object3d.position, uid_vertices.getItem(v2Aid).object3d.position) +
           vector3.point_point_distance(uid_vertices.getItem(v1Bid).object3d.position, uid_vertices.getItem(v2Bid).object3d.position);
  if (d1 < d2) {
    v2Aid = vs2[0];
    v2Bid = vs2[1];
  }
  else {
    v2Aid = vs2[1];
    v2Bid = vs2[0];
  }

  // Check if there is already an edge connecting v1Aid + v2Aid and/or v1Bid + v2Bid.
  var id_of_edge_connecting_As = 0;
  var id_of_edge_connecting_Bs = 0;

  uid_edges.keys().forEach(function(eid){
    var vs = uil_get_vertices_of_edge(eid);
    if ((vs[0] == v1Aid && vs[1] == v2Aid) || (vs[0] == v2Aid && vs[1] == v1Aid))
      id_of_edge_connecting_As = eid;
    if ((vs[0] == v1Bid && vs[1] == v2Bid) || (vs[0] == v2Bid && vs[1] == v1Bid))
      id_of_edge_connecting_Bs = eid;
  });

  if (id_of_edge_connecting_As != 0 && id_of_edge_connecting_Bs != 0) {
    // Revert to a fill operation
    uid_fill_faces_with_existing_invisible_faces(all_edge_ids_to_connect);
  }
  else if (id_of_edge_connecting_As != 0) {
    var new_list = [... all_edge_ids_to_connect];
    push_nodups(id_of_edge_connecting_As, new_list);
    uid_connect_edges_with_invisible_faces_on_them(new_list);
  }
  else if (id_of_edge_connecting_Bs != 0) {
    var new_list = [... all_edge_ids_to_connect];
    push_nodups(id_of_edge_connecting_Bs, new_list);
    uid_connect_edges_with_invisible_faces_on_them(new_list);
  }
  else {
    // Before adding edge check if the vertices coincide. If they do, move the one slightly
    let eps = 1e-5;
    if (vertex_distance(v1Aid, v2Aid) < eps) {
      // Select a reasonable edge on v2Aid to use its tangent.
      let move_eid = 0;
      uil_get_edges_of_vertex(v2Aid).forEach(function(eid){
        if (!(edge_ids1.includes(eid) || edge_ids2.includes(eid))) {
          move_eid = eid;
        }
      });
      move_vertex_away_from_coincident_vertex_on_edge(v1Aid, v2Aid, move_eid);
    }
    var e1id = add_edge_between_two_vertices_on_face(v1Aid, v2Aid, new_merged_face_id, null, true);
    if (e1id == 0) {
      display_message_to_user(LOG.WARN, "Failed to create bridge between faces [code E2a]. Perhaps try toggling face visibility instead.");
      unsuppress_undo_tracking_if_ready(false);
      unsuppress_animate_if_ready();
      return;
    }

    var fs = uil_get_faces_of_edge(e1id);
    var vs0 = vertices_of_face(fs[0]);
    var vs1 = vertices_of_face(fs[1]);
    var second_face_id = 0;
    if (vs0.indexOf(v1Bid) != -1)
      second_face_id = fs[0];
    else if (vs1.indexOf(v1Bid) != -1)
      second_face_id = fs[1];
    else
      console.log("Unable to find second face to add edge");

    // Before adding edge check if the vertices coincide. If they do, move the one slightly
    eps = 1e-5;
    if (vertex_distance(v1Bid, v2Bid) < eps) {
      // Select a reasonable edge on v1Aid to use its tangent.
      let move_eid = 0;
      uil_get_edges_of_vertex(v2Bid).forEach(function(eid){
        if (!(edge_ids1.includes(eid) || edge_ids2.includes(eid))) {
          move_eid = eid;
        }
      });
      move_vertex_away_from_coincident_vertex_on_edge(v1Bid, v2Bid, move_eid);
    }

    var e2id = add_edge_between_two_vertices_on_face(v1Bid, v2Bid, second_face_id, null, true);
    if (e2id == 0) {
      // First we delete the 1st edge that was added.
      uil_delete_edges([e1id], shift_pressed());

      display_message_to_user(LOG.WARN, "Failed to create bridge between faces [code E2b].");
      unsuppress_undo_tracking_if_ready(false);
      unsuppress_animate_if_ready();
      return;
    }

    uil_set_linearity_of_edges([e1id, e2id], true);

    // Must make all faces invisible except for the previously visible ones, and the new face.
    var new_all_faces = [];
    uid_faces.keys().forEach(function(fid){
      if (uil_get_body_of_face(fid) == bid)
        new_all_faces.push(fid);
    });
    var faces_to_make_visible = [];
    var faces_to_make_invisible = [];
    new_all_faces.forEach(function(fid) {
      if (!uil_is_object_visible(fid) && visible_faces.indexOf(fid) != -1)
        push_nodups(fid, faces_to_make_visible);
      else if (uil_is_object_visible(fid) && visible_faces.indexOf(fid) == -1)
        push_nodups(fid, faces_to_make_invisible);
      else if (!uil_is_object_visible(fid)) {
        var es = uil_get_edges_of_face(fid);
        var must_make_visible = false;
        edge_ids1.forEach(function(eid){
          if (es.indexOf(eid) != -1)
          must_make_visible = true;
        });
        edge_ids2.forEach(function(eid){
          if (es.indexOf(eid) != -1)
          must_make_visible = true;
        });
        if (must_make_visible)
          push_nodups(fid, faces_to_make_visible);
      }
    });

    uil_make_faces_invisible(faces_to_make_invisible, false);
    uil_make_faces_visible(faces_to_make_visible);
  }

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
}

export function move_vertex_away_from_coincident_vertex_on_edge(v1id, v2id, move_eid) {
  let movedir = new THREE.Vector3();
  movedir.copy(get_inward_looking_edge_tangent_at_vertex(move_eid, v2id)).normalize().multiplyScalar( (-1) * uil_get_edge_length(move_eid) / 50.0);
  let new_position = new THREE.Vector3().copy(uid_vertices.getItem(v1id).object3d.position);
  new_position.add(movedir);
  uil_move_vertex_to_coords(v1id, new_position.x, new_position.y, new_position.z);
}

export function split_invisible_part_of_body(bid, edge_ids) {

  // First let's make sure we have a proper split
  properly_split_invisible_face_connected_components([], true, false);

  var visible_faces = [];
  var invisible_faces = [];

  uid_faces.keys().forEach(function(fid){
    if (uil_get_body_of_face(fid) == bid) {
      if (uil_is_object_visible(fid))
        push_nodups(fid, visible_faces);
      else
        push_nodups(fid, invisible_faces);
    }
  });

  // Group invisible objects into connected components
  var invisible_connected_components = uil_get_face_connected_components(invisible_faces);
  if (!invisible_connected_components || invisible_connected_components.length == 0) {
    console.log("Failed to identify invisible connected components");
    return 0;
  }

  suppress_core_face_tessellation();

  invisible_connected_components.forEach(function(invisible_face_group) {

    // Check if our edges lie on this connected component
    var edges_lie_on_this_connected_component = false;
    var border_edges = [];
    invisible_face_group.forEach(function(fid){
      var es = uil_get_edges_of_face(fid);
      es.forEach(function(eid){
        if (edge_ids.indexOf(eid) != -1)
        edges_lie_on_this_connected_component = true;
        var fs = uil_get_faces_of_edge(eid);
        if (uil_is_object_visible(fs[0]) || uil_is_object_visible(fs[1]))
        push_nodups(eid, border_edges);
      });
    });

    if (edges_lie_on_this_connected_component) {
      merge_face_connected_component(invisible_face_group, false, uid_state.in_thicken_operation? false : true);
    }
    else {
      // Nothing, this connected component is irrelevant, leave it.
    }
  });

  unsuppress_core_face_tessellation_if_ready();

  return 1;
}

export function uid_connect_edges_with_invisible_faces_on_them(edge_ids) {
  var face_ids = [];
  edge_ids.forEach(function(eid){
    var fs = uil_get_faces_of_edge(eid);
    if (!uil_is_object_visible(fs[0]))
      push_nodups(fs[0], face_ids);
    else
      push_nodups(fs[1], face_ids);
  });

  // Check if edge_ids is a closed loop of edges.
  var listified_edge_ids = [];
  var wv_is_open_list = new WrappedValue();
  uil_listify_edges(edge_ids, wv_is_open_list, listified_edge_ids);

  if (!wv_is_open_list.v) {
    // We have a bunch of invisible faces surrounded by a loop of edges,
    // so we simply display them all
    suppress_animate();
    suppress_undo_tracking();
    uil_make_faces_visible(face_ids);
    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    return 0;
  }

  // The edge list to connect is open.
  // Find endpoints of edge list, and merge faces
  var end_vertices = find_end_vertices_of_edge_list(edge_ids);

  // Remove all tangencies between edge_ids
  for (var i = 0; i < listified_edge_ids.length - 1; i++) {
    var e1 = listified_edge_ids[i];
    var e2 = listified_edge_ids[i+1];
    if (uil_is_joint_smooth(e1, e2)) {
      uil_remove_joint_smoothness([[e1, e2]], false, true);
    }
  }

  var new_face_ids;
  new_face_ids = uil_merge_faces(face_ids, false, end_vertices[0], end_vertices[1]);

  if (!new_face_ids || new_face_ids.length == 0) {
    console.log("Failed to merge faces.");
    return 0;
  }

  // Recalculate which faces are attached to our original edges to connnect, and make them visible.
  var all_faces_of_selected_edges = new HashTable();
  var new_face_id = 0;

  edge_ids.forEach(function(eid){
    var fs = uil_get_faces_of_edge(eid);
    if (all_faces_of_selected_edges.hasItem(fs[0]))
      all_faces_of_selected_edges.setItem(fs[0], all_faces_of_selected_edges.getItem(fs[0]) + 1);
    else
      all_faces_of_selected_edges.setItem(fs[0], 1);
    if (all_faces_of_selected_edges.hasItem(fs[1]))
      all_faces_of_selected_edges.setItem(fs[1], all_faces_of_selected_edges.getItem(fs[1]) + 1);
    else
      all_faces_of_selected_edges.setItem(fs[1], 1);
  });

  all_faces_of_selected_edges.keys().forEach(function(fid){
    if (all_faces_of_selected_edges.getItem(fid) == edge_ids.length && !uil_is_object_visible(fid))
      new_face_id = fid;
  });

  if (new_face_id > 0)
    uil_make_faces_visible([new_face_id]);
}

export function uid_fill_all_connected_component_invisible_faces(edge_ids) {
  // Start with the edges in edge_ids, find at least one invisible face on them, form a connected component from it,
  // and make all the faces visible.

  var edges_to_fill = [];
  if (edge_ids && edge_ids.length > 0)
    edges_to_fill = edge_ids;
  else
    edges_to_fill = uid_selected_edges.keys();

  // Before anything else we properly split all invisible connected components, so they will appear good
  suppress_undo_tracking();
  properly_split_invisible_face_connected_components([], false, false);

  // Form all invisible face connected components
  let ifccs = form_invisible_face_connected_components();

  // Filter to keep those that touch any of the edges_to_fill
  let extended_edges = [];
  ifccs.forEach(function(ifcc) {
    let select_this_one = false;
    let ifcc_visible_edges = [];
    ifcc.forEach(function(fid) {
      let es = uil_get_edges_of_face(fid);
      es.filter(eid => uil_is_object_visible(eid)).forEach(function(eid){
        push_nodups(eid, ifcc_visible_edges);
        if (edges_to_fill.includes(eid))
          select_this_one = true;
      })
      if (select_this_one)
        extended_edges = [... ifcc_visible_edges];
    });
  });

  uid_fill_faces_with_existing_invisible_faces(extended_edges);
  unsuppress_undo_tracking_if_ready(false);
}

export function uid_fill_faces_with_existing_invisible_faces(edge_ids) {
  suppress_animate();
  suppress_undo_tracking();
  uil_start_compound_operation();

  var edges_to_fill = [];
  if (edge_ids && edge_ids.length > 0)
    edges_to_fill = edge_ids;
  else
    edges_to_fill = uid_selected_edges.keys();

  let filled_faces = [];
  edges_to_fill.forEach(function(eid) {
    var face_ids = uil_get_faces_of_edge(eid);
    console_log("For edge " + eid + " the faces are: " + face_ids);
    face_ids.forEach(function(fid) {
      if (!uil_is_object_visible(fid)) {
        console_log("Face " + fid + "is not visible");
        var res = uil_change_object_visibility([fid], true, false);
        uid_objects_raynode.add(uid_faces.getItem(fid).object3d);
        push_nodups(fid, filled_faces);
        var edge_ids = uil_get_edges_of_face(fid);
        edge_ids.forEach(function(eid) {
          if (!uil_is_object_visible(eid)) {
            console_log("Edge " + eid + "is not visible");
            var res = uil_change_object_visibility([eid], true, false);
            uid_objects_raynode.add(uid_edges.getItem(eid).object3d);
            uid_visible_edge_mesh_raynode.add(uid_edges.getItem(eid).get_visible_edge_mesh());

            var vertex_ids = uil_get_vertices_of_edge(eid);
            vertex_ids.forEach(function(vid){
              if (!uil_is_object_visible(vid)) {
                console_log("Vertex " + vid + "is not visible");
                var res = uil_change_object_visibility([vid], true, false);
                uid_objects_raynode.add(uid_vertices.getItem(vid).object3d);
              }
            });
          }
        });
      }
      // console_log("   For face " + fid + " the edges are: " + edge_ids);
    });
  });
  unsuppress_animate_if_ready();
  uil_end_compound_operation();

  /*
  // Attempt to merge all the faces
  suppress_ui_messages();
  uil_make_faces_visible(filled_faces);
  var merge_result = uil_merge_faces(faces_to_merge, false, 0, 0);
  unsuppress_ui_messages_if_ready();
  if (!merge_result)
    display_message_to_user(LOG.WARN, "Failed to dissolve remaining faces. May be violating the temporary three vertex limitation.");
  }
  */

  unsuppress_undo_tracking_if_ready(false);
  uid_deselect_all_objects();
  animate();
}

export function form_invisible_face_connected_components() {
  var invisible_faces = [];
  uid_faces.keys().filter(fid => !uil_is_object_visible(fid)).forEach(function(fid){
    push_nodups(fid, invisible_faces);
  });
  if (invisible_faces.length == 0)
    return [];

  var invisible_connected_components = uil_get_face_connected_components(invisible_faces);
  return invisible_connected_components;
}

export function invisible_face_connected_component_is_properly_split(ifcc) {
  // Receives a set of connected invisible faces and checks to see if they are properly split. Properly split means that each
  // face has only one visible edge and only one invisible vertex. Additionally, the invisible vertex is shared by all.
  let common_invisible_vertex_id = 0;
  let properly_split = true;
  ifcc.forEach(function(fid){
    var vs = vertices_of_face(fid);
    if (vs.length != 3) {
      properly_split = false;
    }
    else if (!uil_is_object_visible(vs[0]) && uil_is_object_visible(vs[1]) && uil_is_object_visible(vs[2])) {
      if (common_invisible_vertex_id == 0)
        common_invisible_vertex_id = vs[0];
      else if (common_invisible_vertex_id != vs[0])
        properly_split = false;
    }
    else if (uil_is_object_visible(vs[0]) && !uil_is_object_visible(vs[1]) && uil_is_object_visible(vs[2])) {
      if (common_invisible_vertex_id == 0)
        common_invisible_vertex_id = vs[1];
      else if (common_invisible_vertex_id != vs[1])
        properly_split = false;
    }
    else if (uil_is_object_visible(vs[0]) && uil_is_object_visible(vs[1]) && !uil_is_object_visible(vs[2])) {
      if (common_invisible_vertex_id == 0)
        common_invisible_vertex_id = vs[2];
      else if (common_invisible_vertex_id != vs[2])
        properly_split = false;
    }
    else {
      properly_split = false;
    }
    var es = uil_get_edges_of_face(fid);
    if (es.length != 3) {
      properly_split = false;
    }
    let ves = es.filter(eid => uil_is_object_visible(eid));
    if (ves.length != 1)
        properly_split = false;
  });
  return properly_split;
}

export function recenter_invisible_face_connected_component_center_vertex(ifcc) {
  // Push the center vertex at the center of mass of the set of visible edges around the invisible face set.

  let ifcc_all_edges = [];
  let ifcc_all_vertices = [];
  let ifcc_visible_edges = [];
  let ifcc_invisible_edges = [];
  let middle_vertex_id = 0;
  ifcc.forEach(function(fid) {
    let es = uil_get_edges_of_face(fid);
    es.forEach(function(eid){
      push_nodups(eid, ifcc_all_edges);
      push_nodups(uil_get_vertices_of_edge(eid)[0], ifcc_all_vertices);
      push_nodups(uil_get_vertices_of_edge(eid)[1], ifcc_all_vertices);
    });
    es.forEach(function(eid){
      if (uil_is_object_visible(eid))
        push_nodups(eid, ifcc_visible_edges);
      else
        push_nodups(eid, ifcc_invisible_edges);
    })
  });

  ifcc_all_vertices.forEach(function(vid){
    if (middle_vertex_id == 0) {
      let es = uil_get_edges_of_vertex(vid);
      let found = true;
      es.forEach(function(eid){
        if (uil_is_object_visible(eid))
          found = false;
      });
      if (found)
        middle_vertex_id = vid;
    }
  });

  if (middle_vertex_id > 0) {
    var center = new THREE.Vector3();
    var wv_radius = new WrappedValue(0);
    var characteristic_dir_coords = new THREE.Vector3();
    var normal = new THREE.Vector3();
    uil_get_edge_list_geometry(ifcc_visible_edges, true, center, normal, wv_radius, characteristic_dir_coords);
    uil_move_vertex_to_coords(middle_vertex_id, center.x, center.y, center.z);
    uil_set_linearity_of_edges(ifcc_invisible_edges, true);
  }

}

export function properly_split_invisible_face_connected_component(ifcc, vertices_to_definitely_split = []) {
  // We replace all invisible faces with a set of faces that are all triangular, each one has a single visible edge, and they all share one vertex.
  // We also push the vertex in place at the center of mass of the set of visible edges around the invisible face set.

  // 1. Get the list of edges surrounding the invisible faces, and listify them.
  var boundary_edge_ids = new Array();
  var boundary_vertex_ids = new Array();
  var wv_is_one_cc = new WrappedValue();
  var cc_res = uil_get_face_connected_component_info(ifcc, wv_is_one_cc, boundary_edge_ids, boundary_vertex_ids);
  if (!cc_res || !wv_is_one_cc.v) {
    // Something went wrong, log it and abort
    console.log("Unable to properly split set of invisible faces.");
    return false;
  }
  var wv_is_open_list = new WrappedValue();
  var sorted_boundary_edge_ids = [];
  var l_res = uil_listify_edges(boundary_edge_ids, wv_is_open_list, sorted_boundary_edge_ids);
  if (!l_res || wv_is_open_list.v) {
    // Something went wrong, log it and abort
    console.log("Invisible connected component boundary list was found to be open.");
    return false;
  }

  // Turn off tessellation
  suppress_core_face_tessellation();

  // 2. Remove any tangencies between them, without modifying geometry, keeping a list of removed tangencies (edge pairs)
  let eidsplus = [... sorted_boundary_edge_ids];
  let smooth_joints = [];
  // Add first id at end of array for easier loop
  eidsplus.push(sorted_boundary_edge_ids[0]);
  for (var i = 0 ; i < eidsplus.length-1 ; i++) {
    if (uil_is_joint_smooth(eidsplus[i], eidsplus[i+1])) {
      smooth_joints.push([eidsplus[i], eidsplus[i+1]]);
      uil_remove_joint_smoothness([[eidsplus[i], eidsplus[i+1]]], true, true);
    }
  }

  // 3. Merge all invisible faces in one and make it visible (temporarily)
  uil_set_auto_add_tangencies(false);
  var merged_face_id = uil_directly_delete_faces(ifcc);
  uil_set_auto_add_tangencies(true);
  if (merged_face_id == 0) {
    // Failed, return error
    console.log("Failed to merge invisible face connected component");
    unsuppress_core_face_tessellation_if_ready();
    return false;
  }
  uil_make_faces_visible([merged_face_id]);

  // XXX Fixed objects!

  // 4. Shift-split all invisible faces (probably need to make them visible first)
  let new_faces = split_face_into_triangles(merged_face_id, false /*true*/, true, 0, 0, true, vertices_to_definitely_split);

  if (new_faces && new_faces.length > 0) {
    // 6. Reinstate any removed tangencies
    add_back_removed_tangency_flags(smooth_joints);

    // 7. Move the center vertex in place
    recenter_invisible_face_connected_component_center_vertex(ifcc);

    // 8. Make faces invisible again
    uil_make_faces_invisible(new_faces);
  }

  unsuppress_core_face_tessellation_if_ready();
}

export function properly_split_invisible_face_connected_components(vertices_to_definitely_split = [], dont_move_vertex = false, report_outcome = false) {
  if (App.state.currently_properly_splitting_invisible_face_connected_components)
    return;

  if (properly_splitting_invisible_components_is_suppressed())
    return;

  App.state.currently_properly_splitting_invisible_face_connected_components = true;
  suppress_undo_tracking();
  suppress_animate();
  suppress_core_face_tessellation();

  let ifccs = form_invisible_face_connected_components();
  //display_message_to_user(LOG.INFO, "Identified " + ifccs.length + " invisible gaps.");
  ifccs.forEach(function(ifcc){
    if (!invisible_face_connected_component_is_properly_split(ifcc)) {
      if (report_outcome)
        display_message_to_user(LOG.INFO, "Component NOT properly split - splitting!");
      properly_split_invisible_face_connected_component(ifcc, vertices_to_definitely_split);
    }
    else if (!dont_move_vertex) {
      if (report_outcome)
        display_message_to_user(LOG.INFO, "Component properly split - recentering only.");
      recenter_invisible_face_connected_component_center_vertex(ifcc);
    }
  });

  unsuppress_core_face_tessellation_if_ready();
  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);
  App.state.currently_properly_splitting_invisible_face_connected_components = false;
}

export function can_bridge_edges(eids) {
//  if (eids.length < 2)
//    return false;
  var all_edges_have_an_invisible_face = true;
  eids.forEach(function(eid) {
    var face_ids = uil_get_faces_of_edge(eid);
    if (uil_is_object_visible(face_ids[0]) && uil_is_object_visible(face_ids[1]))
      all_edges_have_an_invisible_face = false;
  })
  return all_edges_have_an_invisible_face;
}


export function uid_splitpatch_face(fid, make_new_edges_linear = false) {

  if (!uil_is_object_visible(fid)) {
    return;
  }

  suppress_ui_messages();

  // Take note of edges that are on border of face being split and were also in symmetry sets.
  var previous_symmetric_edges = [];
  var new_symmetric_edges = [];
  var vertex_is_added_to_symmetry_edge = false;
  var previous_vertices = [... uid_vertices.keys()];

  uil_get_edges_of_face(fid)
  .filter(eid => (symmetry_edges_list_first.includes(eid) || symmetry_edges_list_second.includes(eid)))
  .forEach(eid => push_nodups(eid, previous_symmetric_edges));
  remove_from_symmetry_set([], [fid]);

  const superedge_hash = uil_get_face_superedges(fid);

  const n_super_edges = superedge_hash.keys().length;
  const start_superedge1id = superedge_hash.getKeyAtIndex(0);
  const start_superedge2id = superedge_hash.getKeyAtIndex(Math.floor(n_super_edges/2));

  if (previous_symmetric_edges.some(eid => superedge_hash.getItem(start_superedge1id).includes(eid)))
    vertex_is_added_to_symmetry_edge = true;
    const v1_id = add_or_find_vertex_on_middle_of_superedge(superedge_hash.getItem(start_superedge1id));
  if (v1_id > 0 && vertex_is_added_to_symmetry_edge && !previous_vertices.includes(v1_id)) {
    new_symmetric_edges.push(uil_get_edges_of_vertex(v1_id)[0]);
    new_symmetric_edges.push(uil_get_edges_of_vertex(v1_id)[1]);
    vertex_is_added_to_symmetry_edge = false;
  }

  if (previous_symmetric_edges.some(eid => superedge_hash.getItem(start_superedge2id).includes(eid)))
    vertex_is_added_to_symmetry_edge = true;
    const v2_id = add_or_find_vertex_on_middle_of_superedge(superedge_hash.getItem(start_superedge2id));
  if (v2_id > 0 && vertex_is_added_to_symmetry_edge && !previous_vertices.includes(v2_id)) {
    new_symmetric_edges.push(uil_get_edges_of_vertex(v2_id)[0]);
    new_symmetric_edges.push(uil_get_edges_of_vertex(v2_id)[1]);
    vertex_is_added_to_symmetry_edge = false;
  }

  if (v1_id == 0 || v2_id == 0) {
    unsuppress_ui_messages_if_ready();
    display_message_to_user(LOG.WARN, "Failed to splitpatch face");
    return;
  }
  const eid = add_edge_between_two_vertices_on_face(v1_id, v2_id, fid, null, false);
  if (make_new_edges_linear)
    uil_set_linearity_of_edges([eid], true)

  var vtmp1 = new THREE.Vector3();
  vtmp1.copy(uid_vertices.getItem(v1_id).object3d.position);
  vtmp1.sub(uid_vertices.getItem(v2_id).object3d.position);
  var vtmp2 = new THREE.Vector3(vtmp1.y, vtmp1.z, vtmp1.x); // Shift coordinates to get a non-parallel vector!
  var middle_vertex_id = uil_add_new_vertex( eid, find_edge_midpoint(eid), vtmp2 );

  const new_edges = new Set();
  superedge_hash.keys()
  .filter(seid => seid !== start_superedge1id && seid !== start_superedge2id)
  .forEach(function(seid){
    if (previous_symmetric_edges.some(eid => superedge_hash.getItem(seid).includes(eid)))
      vertex_is_added_to_symmetry_edge = true;
    var vid = add_or_find_vertex_on_middle_of_superedge(superedge_hash.getItem(seid));
    if (vid > 0 && vertex_is_added_to_symmetry_edge && !previous_vertices.includes(vid)) {
      new_symmetric_edges.push(uil_get_edges_of_vertex(vid)[0]);
      new_symmetric_edges.push(uil_get_edges_of_vertex(vid)[1]);
      vertex_is_added_to_symmetry_edge = false;
    }

    if (vid == 0) {
      unsuppress_ui_messages_if_ready();
      display_message_to_user(LOG.WARN, "Failed to splitpatch face");
      return;
    }

    // Find which face contains both vid and middle_vertex_id
    const faces_list_vid = new Set(uil_get_edges_of_vertex(vid).flatMap(uil_get_faces_of_edge));
    const common_fid = uil_get_edges_of_vertex(middle_vertex_id)
        .flatMap(uil_get_faces_of_edge)
        .find(sinside(faces_list_vid))
    const eid = add_edge_between_two_vertices_on_face(vid, middle_vertex_id, common_fid, null, false);
    new_edges.add(eid);

  });

  if (make_new_edges_linear)
    uil_set_linearity_of_edges(Array.from(new_edges), true);

  add_to_symmetry_set(new_symmetric_edges, []);
  uid_unhighlight_all_objects();
  unsuppress_ui_messages_if_ready();
}

export function split_face_into_triangles(face_id, make_invisible = false, make_new_edges_linear = false,
                                   v1id_for_edge = 0, v2id_for_edge = 0, quick_and_dirty = false, vertices_to_definitely_split = []) {
  suppress_undo_tracking();
  suppress_animate();

  uid_deselect_all_objects();

  var border_edges = uil_get_edges_of_face(face_id);

  var new_v = 0;
  var other_v = 0;
  var edge_to_delete = 0;
  if (border_edges.length < 4) {
    var longest_edge_id = 0;
    var max_length = 0;
    border_edges.forEach(function(eid){
      var l = uil_get_edge_length(eid);
      if (l > max_length) {
        max_length = l;
        longest_edge_id = eid;
      }
    });
    new_v = uil_add_vertex_at_param(longest_edge_id, 0.5);
    var vs = vertices_of_face(face_id);
    vs.forEach(function(vid){
      if (!vertices_are_on_same_edge(new_v, vid))
        other_v = vid;
    });
    border_edges = uil_get_edges_of_face(face_id);
  }

  var listified_border_edges = [];
  var wv_is_open_list = new WrappedValue();
  var res = uil_listify_edges(border_edges, wv_is_open_list, listified_border_edges);
  if (!res || wv_is_open_list.v) {
    // Failed to listify border edges, must abort.
    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
    return 0;
  }

  // Maintain list of new faces and new edges created
  var new_faces = [];
  var new_edges = [];

  // Create list of all border vertices
  var border_vertices = [];
  listified_border_edges.forEach(function(eid){
    var es = uil_get_vertices_of_edge(eid);
    push_nodups(es[0], border_vertices);
    push_nodups(es[1], border_vertices);
  });

  var candidate_vertex_pairs = [];
  var dist_v1id = 0;
  var dist_v2id = 0;

  // If we have a newly added vertex new_v and the one across it , then use this one.
  // Otherwise if we have 2 passed vertex ids, use those
  // Else, pick the two most distant ones.
  if (new_v > 0) {
    dist_v1id = new_v;
    dist_v2id = other_v;
  }
  else if (v1id_for_edge > 0 && v2id_for_edge > 0) {
    dist_v1id = v1id_for_edge;
    dist_v2id = v2id_for_edge;
  }
  else {
    // Identify the two most distanct vertices
    var max_dist = 0;
    border_vertices.forEach(function(v1id){
      border_vertices.forEach(function(v2id){
        if (v1id != v2id && !vertices_are_on_same_edge(v1id, v2id)) {
          candidate_vertex_pairs.push([v1id, v2id]);
          if (vector3.point_point_distance(uid_vertices.getItem(v1id).object3d.position, uid_vertices.getItem(v2id).object3d.position) >= max_dist) {
            max_dist = vector3.point_point_distance(uid_vertices.getItem(v1id).object3d.position, uid_vertices.getItem(v2id).object3d.position);
            dist_v1id = v1id;
            dist_v2id = v2id;
          }
        }
      });
    });
  }

  // Add edge between two most distant vertices, and make it linear
  // If this doesn't work, try other candidate pairs
  var middle_edge_id = 0;
  if (quick_and_dirty)
    middle_edge_id = uil_add_new_edge_quickly(face_id, dist_v1id, dist_v2id);
  else
    middle_edge_id = add_edge_between_two_vertices_on_face(dist_v1id, dist_v2id, face_id, null, false);
  if (middle_edge_id == 0) {
    var edge_added = false;
    candidate_vertex_pairs.forEach(function(pair){
      if (middle_edge_id == 0) {
        dist_v1id = pair[0];
        dist_v2id = pair[1];
        if (quick_and_dirty)
          middle_edge_id = uil_add_new_edge_quickly(face_id, dist_v1id, dist_v2id);
        else
          middle_edge_id = add_edge_between_two_vertices_on_face(dist_v1id, dist_v2id, face_id, null, false);
      }
    });
  }

  if (middle_edge_id == 0) {
    display_message_to_user(LOG.WARN, "Unable to split face.")
    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
    return new_faces;
  }

  if (make_invisible || make_new_edges_linear)
    uil_set_linearity_of_edges([middle_edge_id], true)
  new_faces.splice(new_faces.indexOf(face_id), 1);
  push_nodups(uil_get_faces_of_edge(middle_edge_id)[0], new_faces);
  push_nodups(uil_get_faces_of_edge(middle_edge_id)[1], new_faces);
  var middle_vertex_id = uil_add_vertex_at_param(middle_edge_id, 0.5);

  // Position middle vertex at center of mass of face
  const fnormal = new THREE.Vector3();
  const center = new THREE.Vector3();
  const wv_radius = new WrappedValue(0);
  const characteristic_dir_coords = new THREE.Vector3();
  uil_get_edge_list_geometry(listified_border_edges, false, center, fnormal, wv_radius, characteristic_dir_coords);
  uil_move_vertex_to_coords( middle_vertex_id, center.x, center.y, center.z );

  var es = uil_get_edges_of_vertex(middle_vertex_id);
  push_nodups(es[0], new_edges);
  push_nodups(es[1], new_edges);

  if (new_v) {
    if (uil_get_vertices_of_edge(es[0]).indexOf(new_v) != -1)
      edge_to_delete = es[0];
    else
      edge_to_delete = es[1];
  }

  // Connect all other vertices to the middle vertex
  // If quick-and-dirty and there's more than 12 edges in the list of border vertices, only do a few
  let vertices_to_skip = 0;
  if (quick_and_dirty && border_vertices.length > 12) {
    vertices_to_skip = Math.floor(border_vertices.length / 4.0);
  }
  let skipped_vertices = 0;
  console.log("Vertices: "+ border_vertices.length);
  console.log("Vertices to skip: " + vertices_to_skip);
  for (let i=0; i < border_vertices.length; i++) {
    let vid = border_vertices[i];
    if ((vid != dist_v1id && vid != dist_v2id) &&
        (vertices_to_definitely_split.includes(vid) || skipped_vertices >= vertices_to_skip || i ==0)) {
      // Find their common face
      var fid_for_add_edge = 0;
      new_faces.forEach(function(fid){
        var vs = vertices_of_face(fid);
        if (vs.indexOf(vid) != -1 && vs.indexOf(middle_vertex_id != -1))
          fid_for_add_edge = fid;
      });
      var new_edge_id;
      if (quick_and_dirty)
        new_edge_id = uil_add_new_edge_quickly(fid_for_add_edge, vid, middle_vertex_id);
      else
        new_edge_id = add_edge_between_two_vertices_on_face(vid, middle_vertex_id, fid_for_add_edge, null, false);
      if (new_edge_id != 0) {
        push_nodups(new_edge_id, new_edges);
        new_faces.splice(new_faces.indexOf(fid_for_add_edge), 1);
        push_nodups(uil_get_faces_of_edge(new_edge_id)[0], new_faces);
        push_nodups(uil_get_faces_of_edge(new_edge_id)[1], new_faces);
      }
      skipped_vertices = 0;
      //console.log("Added to vertex " + i);
    }
    else {
      skipped_vertices++;
      //console.log("Skipped vertex " + i);
    }
  }

  // Make all new edges linear
  if (make_invisible || make_new_edges_linear)
    uil_set_linearity_of_edges(new_edges, true);

  if (edge_to_delete) {
    uil_delete_edges([edge_to_delete], shift_pressed());
    new_faces = faces_of_vertex(middle_vertex_id);
  }

  // Make all new faces visible/invisible
  if (make_invisible)
    uil_make_faces_invisible(new_faces, false);

  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);

  return new_faces;
}


export function add_or_find_vertex_on_middle_of_superedge(edge_ids) {
  var total_length = 0;
  edge_ids.forEach(function(eid){
    total_length += uil_get_edge_length(eid);
  });

  var total_length_so_far = 0;
  var vertex_id = 0;
  var i = 0;
  while (i < edge_ids.length && vertex_id == 0) {
    total_length_so_far += uil_get_edge_length(edge_ids[i]);
    var vs = uil_get_vertices_of_edge(edge_ids[i]);
    if (maths.proximate(total_length_so_far, total_length/2.0, total_length/20)) {
      // Middle vertex already exists, it's the common one between this edge and the next
      var v = uil_find_edge_shared_vertex(edge_ids[i], edge_ids[i+1]);
      return v;
    }
    else if (total_length_so_far < total_length/2.0) {
      // Keep going
    }
    else {
      // Must add vertex on this edge at point with parameter value u in [0,1]
      var u = (total_length/2.0 - (total_length_so_far - uil_get_edge_length(edge_ids[i])) ) / uil_get_edge_length(edge_ids[i]);
      // Check whether we must invert the u direction
      if (edge_ids.length == 1) {
        u = 0.5;
      }
      else if (i > 0) {
        // We're not sitting on the first edge of this superedge
        var v_common_with_previous = uil_find_edge_shared_vertex(edge_ids[i], edge_ids[i-1])
        if (vs[0] == v_common_with_previous) {
          // We're good, do nothing
        }
        else if (vs[1] == v_common_with_previous) {
          u = 1-u;
        }
        else {
          console_log_xml("Error in finding middle of superedge");
        }
      }
      else {
        // We're sitting on the first edge of this superedge and we have to add the vertex on it.
        var v_common_with_next = uil_find_edge_shared_vertex(edge_ids[i], edge_ids[i+1])
        if (vs[1] == v_common_with_next) {
          // We're good, do nothing
        }
        else if (vs[0] == v_common_with_next) {
          u = 1-u;
        }
        else {
          console_log_xml("Error in finding middle of superedge");
        }
      }
      // Add the vertex at parameter u on this edge

      var v = uil_add_vertex_at_param(edge_ids[i], u);
      return v;
    }
    i++;
  }
}

export function prepare_for_join_bodies_on_vertices() {

  // Find all selected vertices (directly or indirectly through the edges/faces) and sort them from closest
  // Give priority to directly selected vertices.

  if ((uid_selected_vertices.length == 2) || (uid_selected_vertices.length == 4) || (uid_selected_vertices.length == 6)) {
    // Use vertices
    if (uid_selected_vertices.length == 2)
      uid_join_bodies_on_vertices(uid_selected_vertices.keys()[0], 0, 0, uid_selected_vertices.keys()[1], 0, 0);
    else if (uid_selected_vertices.length == 4)
      uid_join_bodies_on_vertices(uid_selected_vertices.keys()[0],uid_selected_vertices.keys()[1], 0,
                                  uid_selected_vertices.keys()[2],uid_selected_vertices.keys()[3], 0);
    else
      uid_join_bodies_on_vertices(uid_selected_vertices.keys()[0],uid_selected_vertices.keys()[1],
                                  uid_selected_vertices.keys()[2],uid_selected_vertices.keys()[3],
                                  uid_selected_vertices.keys()[4],uid_selected_vertices.keys()[5]);
  }
  else if (uid_selected_edges.length == 2) {
    //  2 edges, sort out vertices
    var vs0 = uil_get_vertices_of_edge(uid_selected_edges.keys()[0]);
    var vs1 = uil_get_vertices_of_edge(uid_selected_edges.keys()[1]);
    var d00 = vector3.point_point_distance(uid_vertices.getItem(vs0[0]).object3d.position, uid_vertices.getItem(vs1[0]).object3d.position);
    var d01 = vector3.point_point_distance(uid_vertices.getItem(vs0[0]).object3d.position, uid_vertices.getItem(vs1[1]).object3d.position);
    var d10 = vector3.point_point_distance(uid_vertices.getItem(vs0[1]).object3d.position, uid_vertices.getItem(vs1[0]).object3d.position);
    var d11 = vector3.point_point_distance(uid_vertices.getItem(vs0[1]).object3d.position, uid_vertices.getItem(vs1[1]).object3d.position);

    if (d00 <= d01 && d00 <= d10 && d00 <= d11)
      uid_join_bodies_on_vertices(vs0[0], vs0[1], 0, vs1[0], vs1[1], 0);
    else if (d11 <= d01 && d11 <= d10 && d11 <= d00)
      uid_join_bodies_on_vertices(vs0[1], vs0[0], 0, vs1[1], vs1[0], 0);
    else if (d01 <= d00 && d01 <= d10 && d01 <= d11)
      uid_join_bodies_on_vertices(vs0[0], vs0[1], 0, vs1[1], vs1[0], 0);
    else
      uid_join_bodies_on_vertices(vs0[1], vs0[0], 0, vs1[0], vs1[1], 0);
  }
  else if (uid_selected_edges.length == 4) {
    // 4 edges, sort out vertices
    var vs00 = uil_get_vertices_of_edge(uid_selected_edges.keys()[0]);
    var vs01 = uil_get_vertices_of_edge(uid_selected_edges.keys()[1]);
    var vs10 = uil_get_vertices_of_edge(uid_selected_edges.keys()[2]);
    var vs11 = uil_get_vertices_of_edge(uid_selected_edges.keys()[3]);

    // Find the common vertex on each side
    var v00, v01, v02, v10, v11, v12;
    if (vs00[0] == vs01[0]) {
      v00 = vs00[1];
      v01 = vs00[0];
      v02 = vs01[1];
    }
    else if (vs00[1] == vs01[0]) {
      v00 = vs00[0];
      v01 = vs00[1];
      v02 = vs01[1];
    }
    else if (vs00[1] == vs01[1]) {
      v00 = vs00[0];
      v01 = vs00[1];
      v02 = vs01[0];
    }
    else {
      v00 = vs00[1];
      v01 = vs00[0];
      v02 = vs01[0];
    }

    if (vs10[0] == vs11[0]) {
      v10 = vs10[1];
      v11 = vs10[0];
      v12 = vs11[1];
    }
    else if (vs10[1] == vs11[0]) {
      v10 = vs10[0];
      v11 = vs10[1];
      v12 = vs11[1];
    }
    else if (vs10[1] == vs11[1]) {
      v10 = vs10[0];
      v11 = vs10[1];
      v12 = vs11[0];
    }
    else {
      v10 = vs10[1];
      v11 = vs10[0];
      v12 = vs11[0];
    }

    uid_join_bodies_on_vertices(v01, v00, v02, v11, v10, v12);
  }
  else if (uid_selected_faces.length == 2) {
    var f0 = uid_selected_faces.getKeyAtIndex(0);
    var f1 = uid_selected_faces.getKeyAtIndex(1);
    var vs0 = vertices_of_face(f0);
    var vs1 = vertices_of_face(f1);

    // Find vertex pair between the two faces that is closest
    var mindist = 99999999;
    var minv0 = 0;
    var minv1 = 0;
    vs0.forEach(function(v0id) {
      vs1.forEach(function(v1id) {
        var d = vector3.point_point_distance(uid_vertices.getItem(v0id).object3d.position, uid_vertices.getItem(v1id).object3d.position);
        if (d < mindist) {
          minv0 = v0id;
          minv1 = v1id;
          mindist = d;
        }
      });
    });

    // Find attached pair of edges on the two faces.
    var es0 = [];
    var es1 = [];
    var es0v = uil_get_edges_of_vertex(minv0);
    var es0f = uil_get_edges_of_face(f0);
    es0v.forEach(function(eid){
      if (es0f.indexOf(eid) != -1)
        es0.push(eid);
    });

    var es1v = uil_get_edges_of_vertex(minv1);
    var es1f = uil_get_edges_of_face(f1);
    es1v.forEach(function(eid){
      if (es1f.indexOf(eid) != -1)
        es1.push(eid);
    });

    // Order vertices on the two edge pairs
    var v01 = minv0;
    var vs = uil_get_vertices_of_edge(es0[0]);
    var v00 = vs[0] == v01 ? vs[1] : vs[0];
    var vs = uil_get_vertices_of_edge(es0[1]);
    var v02 = vs[0] == v01 ? vs[1] : vs[0];

    var v11 = minv1;
    var vs = uil_get_vertices_of_edge(es1[0]);
    var v10 = vs[0] == v11 ? vs[1] : vs[0];
    var vs = uil_get_vertices_of_edge(es1[1]);
    var v12 = vs[0] == v11 ? vs[1] : vs[0];

    // Check if the corresponding order is (v00-v01-v02) -> (v10-v11-v12), or (v00-v01-v02) -> (v10-v11-v12)
    if (vector3.point_point_distance(uid_vertices.getItem(v00).object3d.position, uid_vertices.getItem(v10).object3d.position) <
        vector3.point_point_distance(uid_vertices.getItem(v00).object3d.position, uid_vertices.getItem(v12).object3d.position)
       )
      uid_join_bodies_on_vertices(v01, v00, v02, v11, v10, v12);
    else
      uid_join_bodies_on_vertices(v01, v00, v02, v11, v12, v10);
  }
}

export function prepare_for_move_objects_to_location(vid = null, target = null, ignore_shift = false) {
  if (vid && target) {
    // User shift-clicked on a vertex to place it (and possibly other objects with it)
    uid_move_objects_to_location(vid, target, ignore_shift);
    return;
  }
  else {
    // Nothing, shouldn't happen
  }
}

export function uid_move_objects_to_location(vid, target, ignore_shift = false) {
  var bid = uil_get_body_of_vertex(vid);

  // Collect all selected edges AND all edges of the selected faces
  var edges_to_move = [];

  if (ignore_shift || !shift_pressed()) {
    // With shift we consider the selected edges
    uid_selected_edges.keys().forEach(function(eid) {
      push_nodups(eid, edges_to_move);
    });
    uid_selected_faces.keys().forEach(function(fid) {
      var l = uil_get_edges_of_face(fid);
      l.forEach(function(eid) {
        push_nodups(eid, edges_to_move);
      });
    });
  }
  else {
    // Just add all edges of the body
    uid_edges.keys().forEach(function(eid){
      if (uil_get_body_of_edge(eid) == bid)
        edges_to_move.push(eid);
    });
  }

  if (edges_to_move.length > 0) {
    // If the vertex vid is not on any of the selected edges, add it to the list of edges to move
    var must_add_vertex = true;
    edges_to_move.forEach(function(eid){
      if ((uil_get_vertices_of_edge(eid)[0] == vid) || (uil_get_vertices_of_edge(eid)[1] == vid))
        must_add_vertex = false;
    });
    if (must_add_vertex) {
      uil_get_edges_of_vertex(vid).forEach(function(eid){
        push_nodups(eid, edges_to_move);
      });
    }
    // We will be moving a set of edges, step by step
    // Calculate a translation vector based on the vertex selected and the target.
    start_move_edge_list(edges_to_move);

    $( document ).one( "objects_translate_step_finished", { foo: "bar" }, function( event ) {
      $( document ).off( "objects_translate_step_finished" );
      finish_join_bodies(edges_to_move);
    });

    objects_translate_step(edges_to_move, uid_vertices.getItem(vid).object3d.position, target, 5, 1);
  }
  else {
    suppress_animate();
    suppress_undo_tracking();

    var dt = new THREE.Vector3();
    dt.subVectors(target, uid_vertices.getItem(vid).object3d.position);
    uid_select_object(vid); // Select it in case it wasn't.
    uid_selected_vertices.keys().forEach(function(v1id){
      var target1 = new THREE.Vector3();
      target1.copy(uid_vertices.getItem(v1id).object3d.position)
      target1.add(dt);
      uil_move_vertex_to_coords( v1id, target1.x, target1.y, target1.z );
    });

    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    /*
    suppress_undo_tracking();
    var vertices_to_move = [];
    var target_coordinates = [];
    var dt = new THREE.Vector3();
    dt.subVectors(target, uid_vertices.getItem(vid).object3d.position);
    uid_select_object(vid); // Select it in case it wasn't.
    uid_selected_vertices.keys().forEach(function(v1id){
      vertices_to_move.push(v1id);
      var target1 = new THREE.Vector3();
      target1.copy(uid_vertices.getItem(v1id).object3d.position)
      target1.add(dt);
      target_coordinates.push(target1);
    });
    vertices_move_step(vertices_to_move, target_coordinates, 5, 1);
    */
  }
}

export function uid_join_bodies_on_vertices(v11id, v12id, v13id, v21id, v22id, v23id) {
  // See whether we're working with 2, 4 or 6 vertices, and check that they come from the same
  // body
  var n_vertices = 6;
  var b1id = uil_get_body_of_vertex(v11id);
  var b2id = uil_get_body_of_vertex(v21id);
  if (b1id == b2id) {
    display_message_to_user(LOG.WARN, "Cannot join two elements of the same body");
    return;
  }

  if (v13id == 0)
    n_vertices = 4;
  if (v12id == 0)
    n_vertices = 2

  if (n_vertices == 4) {
    if (uil_get_body_of_vertex(v12id) != b1id) {
      display_message_to_user(LOG.WARN, "First pair of elements must belong in the same set");
      return;
    }
    if (uil_get_body_of_vertex(v22id) != b2id) {
      display_message_to_user(LOG.WARN, "Second pair of elements must belong in the same set");
      return;
    }
  }
  else if (n_vertices == 6) {
    if (uil_get_body_of_vertex(v12id) != b1id || uil_get_body_of_vertex(v13id) != b1id) {
      display_message_to_user(LOG.WARN, "First triplet of elements must belong in the same set");
      return;
    }
    if (uil_get_body_of_vertex(v22id) != b2id || uil_get_body_of_vertex(v23id) != b2id) {
      display_message_to_user(LOG.WARN, "Second triplet of elements must belong in the same set");
      return;
    }
  }


  // Collect all selected edges AND all edges of the selected faces of the second body
  var edges_to_move = [];

  if (shift_pressed()) {
    // With shift we consider the selected edges
    uid_selected_edges.keys().forEach(function(eid) {
      if (uil_get_body_of_edge(eid) == b1id)
        edges_to_move.push(eid);
    });
    uid_selected_faces.keys().forEach(function(fid) {
      if (uil_get_body_of_face(fid) == b1id) {
        var l = uil_get_edges_of_face(fid);
        l.forEach(function(eid) {
          edges_to_move.push(eid);
        });
      }
    });

    // Remove duplicate edges.
    var unique = [];
    $.each(edges_to_move, function(i, el) {
      if ($.inArray(el, unique) === -1) unique.push(el);
    });
    edges_to_move = unique.slice(0);
  }
  else {
    // Just add all edges of the first body
      uid_edges.keys().forEach(function(eid){
        if (uil_get_body_of_edge(eid) == b1id)
          edges_to_move.push(eid);
      });
  }

  if (edges_to_move.length > 0) {
    // We will be moving a set of edges, step by step
    // Calculate a translation vector based on the first vertex selected in each set.
    var v1 = uid_vertices.getItem(v11id);
    var v2 = uid_vertices.getItem(v21id);

    start_move_edge_list(edges_to_move);

    $( document ).one( "body_translate_step_finished", { foo: "bar" }, function( event ) {
      $( document ).off( "body_translate_step_finished" );
      if (n_vertices == 4 || n_vertices == 6)
        execute_after_body_translate_step_finished(edges_to_move, v11id, v12id, v21id, v22id );
      else {
        finish_join_bodies(edges_to_move);
      }
    });

    $( document ).one( "first_body_rotate_step_finished", { foo: "bar" }, function( event ) {
      $( document ).off( "first_body_rotate_step_finished" );
      if (n_vertices == 6)
        execute_after_first_body_rotate_step_finished(edges_to_move, v11id, v12id, v13id, v21id, v22id, v23id);
      else {
        finish_join_bodies(edges_to_move);
      }
    });

    $( document ).one( "second_body_rotate_step_finished", { foo: "bar" }, function( event ) {
      $( document ).off( "second_body_rotate_step_finished" );
      finish_join_bodies(edges_to_move);
    });

    body_translate_step(edges_to_move, v1, v2, 5, 1);
  }
  else {
    suppress_undo_tracking();
    var vertices_to_move = [];
    var target_coordinates = [];
    vertices_to_move.push(v11id);
    target_coordinates.push(uid_vertices.getItem(v21id).object3d.position);
    if (v12id != 0) {
      vertices_to_move.push(v12id);
      target_coordinates.push(uid_vertices.getItem(v22id).object3d.position);
    }
    if (v13id != 0) {
      vertices_to_move.push(v13id);
      target_coordinates.push(uid_vertices.getItem(v23id).object3d.position);
    }
    vertices_move_step(vertices_to_move, target_coordinates, 5, 1);
  }
}

export function finish_join_bodies(edges_to_move) {
  end_move_edge_list(edges_to_move);
  uid_deselect_all_objects();
  $( document ).off( "objects_translate_step_finished" );
  $( document ).off( "body_translate_step_finished" );
  $( document ).off( "first_body_rotate_step_finished" );
  $( document ).off( "second_body_rotate_step_finished" );
}

export function execute_after_body_translate_step_finished(edges_to_move, v11id, v12id, v21id, v22id) {

  var v11 = uid_vertices.getItem(v11id);
  var v12 = uid_vertices.getItem(v12id);
  var v21 = uid_vertices.getItem(v21id);
  var v22 = uid_vertices.getItem(v22id);

  var rot_angle = 0;
  var rot_base = new THREE.Vector3(0,0,0);
  var rot_axis = new THREE.Vector3(1,0,0);

  // Calculate a rotation base and axis based on the two pairs of vectors
  var vec1 = new THREE.Vector3();
  vec1.copy(v12.object3d.position);
  vec1.sub(v11.object3d.position).normalize();
  var vec2 = new THREE.Vector3();
  vec2.copy(v22.object3d.position);
  vec2.sub(v21.object3d.position).normalize();

  rot_base.copy(v11.object3d.position);
  rot_axis.crossVectors(vec1, vec2).normalize();

  body_rotate_step(edges_to_move, rot_base, rot_axis, v11, v12, v21, v22, 5, 1);
}

export function execute_after_first_body_rotate_step_finished(edges_to_move, v11id, v12id, v13id, v21id, v22id, v23id) {

  var v11 = uid_vertices.getItem(v11id);
  var v12 = uid_vertices.getItem(v12id);
  var v13 = uid_vertices.getItem(v13id);
  var v21 = uid_vertices.getItem(v21id);
  var v22 = uid_vertices.getItem(v22id);
  var v23 = uid_vertices.getItem(v23id);

  var rot_angle = 0;
  var rot_base = new THREE.Vector3();
  rot_base.copy(v11.object3d.position);

  // Calculate a rotation base and axis based on the two pairs of vectors
  var rot_axis = new THREE.Vector3();
  rot_axis.copy(v12.object3d.position);
  rot_axis.sub(v11.object3d.position).normalize();

  var vec2 = new THREE.Vector3();
  vec2.copy(v23.object3d.position);
  vec2.sub(v21.object3d.position).normalize();
  var normal = new THREE.Vector3();
  normal.crossVectors(rot_axis, vec2);

  var linepoint = vector3.project_point_on_line(v13.object3d.position, rot_base, rot_axis);
  var planepoint = vector3.project_point_on_plane(v13.object3d.position, rot_base, normal);

  body_rotate_onto_plane_step(edges_to_move, rot_base, rot_axis, v11, v12, v13, v21, v22, v23, 5, 1);
}

export function body_translate_step(edges_to_move, initial, final, steps, step) {

  var trans = new THREE.Vector3();
  trans.subVectors(final.object3d.position, initial.object3d.position);
  trans.multiplyScalar(1/(steps-step+1));

  if (step <= steps)
    move_edge_list( edges_to_move, trans, 0, new THREE.Vector3(0,0,0), new THREE.Vector3(1,0,0));

  if (step < steps)
    setTimeout(function() {
      body_translate_step(edges_to_move, initial, final, steps, step+1);
    }, 5);
  else {
    $( document ).trigger( "body_translate_step_finished", [] );
  }
}

export function body_rotate_step(edges_to_move, rot_base, rot_axis, v11, v12, v21, v22, steps, step) {

  var rot_angle = 0;

  // Calculate a rotation base and axis based on the two pairs of vectors
  var vec1 = new THREE.Vector3();
  vec1.copy(v12.object3d.position);
  vec1.sub(v11.object3d.position).normalize();
  var vec2 = new THREE.Vector3();
  vec2.copy(v22.object3d.position);
  vec2.sub(v21.object3d.position).normalize();

  rot_angle = vector3.cc_angle(vec1, vec2, rot_axis);
  var tmp_rot_axis = new THREE.Vector3();
  tmp_rot_axis.copy(rot_axis);
  if (rot_angle > Math.PI) {
    rot_angle = 2*Math.PI - rot_angle;
    tmp_rot_axis.multiplyScalar(-1);
  }
  rot_angle /= (steps-step+1);


  if (step <= steps)
    move_edge_list( edges_to_move, new THREE.Vector3(0,0,0), rot_angle, rot_base, tmp_rot_axis);

  if (step < steps)
    setTimeout(function() {
      body_rotate_step(edges_to_move, rot_base, rot_axis, v11, v12, v21, v22, steps, step+1)
    }, 5);
  else {
    $( document ).trigger( "first_body_rotate_step_finished", [] );
  }
}

export function body_rotate_onto_plane_step(edges_to_move, rot_base, rot_axis, v11, v12, v13, v21, v22, v23, steps, step) {
  var rot_angle = 0;
  var rot_base = new THREE.Vector3();
  rot_base.copy(v11.object3d.position);

  var vec2 = new THREE.Vector3();
  vec2.copy(v23.object3d.position);
  vec2.sub(v21.object3d.position).normalize();
  var normal = new THREE.Vector3();
  normal.crossVectors(rot_axis, vec2);

  var linepoint = vector3.project_point_on_line(v13.object3d.position, rot_base, rot_axis);
  var planepoint = vector3.project_point_on_plane(v13.object3d.position, rot_base, normal);

  // Calculate a rotation base and axis based on the two pairs of vectors
  var vec1 = new THREE.Vector3();
  vec1.copy(v13.object3d.position);
  vec1.sub(linepoint).normalize();
  var vec2 = new THREE.Vector3();
  vec2.copy(planepoint);
  vec2.sub(linepoint).normalize();
  rot_angle = vector3.cc_angle(vec1, vec2, rot_axis);
  var tmp_rot_axis = new THREE.Vector3();
  tmp_rot_axis.copy(rot_axis);
  if (rot_angle > Math.PI) {
    rot_angle = 2*Math.PI - rot_angle;
    tmp_rot_axis.multiplyScalar(-1);
  }
  rot_angle /= (steps-step+1);

  if (step <= steps)
    move_edge_list( edges_to_move, new THREE.Vector3(0,0,0), rot_angle, rot_base, tmp_rot_axis);

  if (step < steps)
    setTimeout(function() {
      body_rotate_onto_plane_step(edges_to_move, rot_base, rot_axis, v11, v12, v13, v21, v22, v23, steps, step+1)
    }, 5);
  else {
    $( document ).trigger( "second_body_rotate_step_finished", [] );
  }
}


export function vertices_move_step(vertices_to_move, target_coordinates, steps, step) {
  suppress_animate();
  for (var i = 0; i < vertices_to_move.length; i++) {
    var v = uid_vertices.getItem(vertices_to_move[i]).object3d.position;
    var t_step = new THREE.Vector3();
    t_step.copy(target_coordinates[i]);
    t_step.sub(v);
    t_step.multiplyScalar(1/(steps-step+1));
    t_step.add(v);
    uil_move_vertex_to_coords( vertices_to_move[i], t_step.x, t_step.y, t_step.z );
  }
  unsuppress_animate_if_ready();

  if (step < steps)
    setTimeout(function() {
      vertices_move_step(vertices_to_move, target_coordinates, steps, step+1);
      console_log(step);
    }, 5);
  else {
    setTimeout(function() {
      uid_deselect_all_objects();
      unsuppress_undo_tracking_if_ready(false);
    }, 5);
  }
}

export function objects_translate_step(edges_to_move, initial_coordinates, final_coordinates, steps, step) {

  var trans = new THREE.Vector3();
  trans.subVectors(final_coordinates, initial_coordinates);
  trans.multiplyScalar(1/(steps-step+1));

  if (step <= steps)
    move_edge_list( edges_to_move, trans, 0, new THREE.Vector3(0,0,0), new THREE.Vector3(1,0,0));

  if (step < steps)
    setTimeout(function() {
      objects_translate_step(edges_to_move, initial_coordinates, final_coordinates, steps, step+1);
    }, 5);
  else {
    $( document ).trigger( "objects_translate_step_finished", [] );
  }
}



export function uid_store_and_deselect_all_objects() {
  currently_selected_objects = [];
  uid_selected_vertices.keys().forEach(function(oid) {
    currently_selected_objects.push(oid);
  });
  uid_selected_edges.keys().forEach(function(oid) {
    currently_selected_objects.push(oid);
  });
  uid_selected_faces.keys().forEach(function(oid) {
    currently_selected_objects.push(oid);
  });
  uid_deselect_all_objects();
  animate();
}

export function uid_reselect_all_stored_objects() {
  suppress_animate();

  // First we select any objects in the list to be selected (unless they are already selected)
  currently_selected_objects.forEach(function(oid){
    if (isVertex(oid)) {
      if (!object_is_selected(oid))
        uid_toggle_select_object(oid, false);
    }
    if (isEdge(oid)) {
      if (!object_is_selected(oid))
        uid_toggle_select_object(oid, false);
    }
    if (isFace(oid)) {
      if (!object_is_selected(oid))
        uid_toggle_select_object(oid, false);
    }
  });

  // Then we deselect any selected objects that are not in the list
  uid_selected_vertices.keys().forEach(function(oid) {
    if (currently_selected_objects.indexOf(oid) == -1)
      uid_toggle_select_object(oid, false);
  });
  uid_selected_edges.keys().forEach(function(oid) {
    if (currently_selected_objects.indexOf(oid) == -1)
      uid_toggle_select_object(oid, false);
  });
  uid_selected_faces.keys().forEach(function(oid) {
    if (currently_selected_objects.indexOf(oid) == -1)
      uid_toggle_select_object(oid, false);
  });

  unsuppress_animate_if_ready();
}

export function uid_deselect_all_objects() {

  // If we are in a zoom state with an active (albeit temporarily removed) rigid move manipulator, don't allow deselecting.
  if (zoom_manipulator_timeout_handle)
    return;

  var oid_array = new Array();
  uid_selected_vertices.keys().forEach(function(o) {
    oid_array.push(o);
  });
  uid_selected_edges.keys().forEach(function(o) {
    oid_array.push(o);
  });
  uid_selected_faces.keys().forEach(function(o) {
    oid_array.push(o);
  });

  suppress_animate();
  oid_array.forEach(function(id) {
    uid_toggle_select_object(id, false);
  });
  unsuppress_animate_if_ready();
}

export function uid_select_all_objects(omit_hidden = false) {
  if (App.state.editing_reference_sketches)
    return;
  suppress_animate();
  uid_faces.keys().forEach(function(o) {
    if (uil_is_object_visible(o) && !face_is_for_sketch(o) && !uid_selected_faces.hasItem(o) && !(omit_hidden && mustNotDisplayFace(o)))
      uid_toggle_select_object(o, false);
  });
  uid_edges.keys().forEach(function(o) {
    if (uil_is_object_visible(o) && !edge_is_for_sketch(o) && !uid_selected_edges.hasItem(o) && !(omit_hidden && mustNotDisplayEdge(o)))
      uid_toggle_select_object(o, false);
  });
  uid_vertices.keys().forEach(function(o) {
    if (uil_is_object_visible(o) && !vertex_is_for_sketch(o) && !uid_selected_vertices.hasItem(o) && !(omit_hidden && mustNotDisplayVertex(o)))
      uid_toggle_select_object(o, false);
  });
  unsuppress_animate_if_ready();
}

export function uid_deselect_all_faces() {
  if (App.state.editing_reference_sketches)
    return;

  var oid_array = new Array();
  uid_selected_faces.keys().forEach(function(o) {
    oid_array.push(o);
  });
  suppress_animate();
  oid_array.forEach(function(id) {
    uid_toggle_select_object(id, false);
  });
  unsuppress_animate_if_ready();
}

export function uid_deselect_all_vertices() {
  var oid_array = new Array();
  uid_selected_vertices.keys().forEach(function(o) {
    oid_array.push(o);
  });
  suppress_animate();
  oid_array.forEach(function(id) {
    uid_toggle_select_object(id, false);
  });
  unsuppress_animate_if_ready();
}

export function uid_deselect_all_edges() {
  var oid_array = new Array();
  uid_selected_edges.keys().forEach(function(o) {
    oid_array.push(o);
  });
  suppress_animate();
  oid_array.forEach(function(id) {
    uid_toggle_select_object(id, false);
  });
  unsuppress_animate_if_ready();
}

export function rotateAroundObjectAxis(objectGeometry, axis, radians) {
  var rotObjectMatrix = new THREE.Matrix4();
  rotObjectMatrix.makeRotationAxis(axis.normalize(), radians);
  objectGeometry.applyMatrix(rotObjectMatrix);
}

export function rotateAroundWorldAxis(object, axis, radians) {
  var rotWorldMatrix = new THREE.Matrix4();
  rotWorldMatrix.makeRotationAxis(axis.normalize(), radians);
  rotWorldMatrix.multiply(object.matrix); // pre-multiply
  object.matrix = rotWorldMatrix;
  object.rotation.setFromRotationMatrix(object.matrix);
}

export function uid_undo() {
  if (uid_in_dragging_mode)
    // Don't allow undo while in dragging mode
    return;

  handle_escape(true, false, false);

  // Show sketches
  var must_hide_sketches_after = false;
  var must_edit_sketches_after = false;

  // Move any reference sketch objects into the default uid_objects_raynode
  if (App.state.imported_sketches.length > 0) {
    if (!App.state.showing_reference_sketches) {
      show_sketches();
      must_hide_sketches_after = true;
    }
    if (App.state.editing_reference_sketches) {
      stop_editing_sketches();
      must_edit_sketches_after = true;
    }

    for (var i = uid_sketch_objects_raynode.children.length - 1; i >= 0; i--) {
      var n = uid_sketch_objects_raynode.children[i];
      uid_objects_raynode.add(n);
    }
  }

  uil_undo();
  display_toggle_edge_visibility(true);
  display_toggle_vertex_visibility(true, false, true);
  display_toggle_face_visibility(false, true);

  // Make sure we're not left with orphaned sketch images
  App.state.imported_sketches.forEach(function(e){
    if (!uid_faces.hasItem(e.face_id))
      delete_sketch_of_face(e.face_id);
  });

  // Also make sure that if any sketches became un-orphaned, we mark them as non-deleted
  App.state.imported_sketches.forEach(function(e){
    if (uid_faces.hasItem(e.face_id))
      undelete_sketch_of_face(e.face_id);
  });

  move_sketch_objects_out_of_objects_raynode();

  if (must_hide_sketches_after)
    hide_sketches();

  if (must_edit_sketches_after)
    start_editing_sketches();
}

export function uid_redo() {
  if (uid_in_dragging_mode)
    // Don't redo while in dragging mode
    return;

  handle_escape(true, false, false);

  var must_edit_sketches_after = false;
  var must_hide_sketches_after = false;

  // Move any reference sketch objects into the default uid_objects_raynode
  if (App.state.imported_sketches.length > 0) {
    // Show sketches
    if (!App.state.showing_reference_sketches) {
      show_sketches();
      must_hide_sketches_after = true;
    }

    if (App.state.editing_reference_sketches) {
      stop_editing_sketches();
      must_edit_sketches_after = true;
    }

    for (var i = uid_sketch_objects_raynode.children.length - 1; i >= 0; i--) {
      var n = uid_sketch_objects_raynode.children[i];
      uid_objects_raynode.add(n);
    }
  }

  uil_redo();
  display_toggle_edge_visibility(true);
  display_toggle_vertex_visibility(true, false, true);
  display_toggle_face_visibility(false, true);

  // Make sure we're not left with orphaned sketch images
  App.state.imported_sketches.forEach(function(e){
    if (!uid_faces.hasItem(e.face_id))
      delete_sketch_of_face(e.face_id);
  });

  // Also make sure that if any sketches became un-orphaned, we mark them as non-deleted
  App.state.imported_sketches.forEach(function(e){
    if (uid_faces.hasItem(e.face_id))
      undelete_sketch_of_face(e.face_id);
  });

  move_sketch_objects_out_of_objects_raynode();

  if (must_hide_sketches_after)
    hide_sketches();

  if (must_edit_sketches_after)
    start_editing_sketches();
}

export function start_snap_to_object() {
  if (App.state.snapping_to_object)
    return;

  if (App.state.imported_partstudio_reference_models.length > 0)
    show_all_reference_partstudio_vertices();
  if (App.state.imported_stl_models.length > 0)
    show_all_reference_stl_vertices();

  App.state.snapping_to_object = true;
  uid_raynode = uid_geometries_raynode;

  update_dom_text_values_and_coordinates();

  request_setting_button_states();
  animate();
}

export function abort_snap_to_object() {
  if (!App.state.snapping_to_object)
    return;

  hide_all_reference_partstudio_vertices();
  hide_all_reference_stl_vertices();
  App.state.snapping_to_object = false;
  uid_raynode = uid_objects_raynode;
  uid_manip_raynode.remove(uid_axes_planes_raynode);
  if (App.state.axes_visible)
    uid_objects_raynode.add(uid_axes_planes_raynode);

  request_setting_button_states();
  animate();
}

export async function finish_snap_to_object(intersected_obj) {
  var o3d = intersected_obj.object;

  var target_click_location = new THREE.Vector3();
  target_click_location.copy(intersected_obj.point);

  // First make a note of the currently selected Phi objects
  var phi_vertices_to_snap = [... uid_selected_vertices.keys()];
  var phi_edges_to_snap = [... uid_selected_edges.keys()];
  var phi_faces_to_snap = [... uid_selected_faces.keys()];

  // Check whether shift is pressed
  var shift_modifier = shift_pressed();

  // Collect target information
  var target_uido = uid_object3dIDToUid_object.getItem(o3d.id);

  // If for any reason they didn't click on a regular object (phi or reference) don't proceed.
  if (!target_uido)
    return;

  // Handle different snap cases
  if (phi_vertices_to_snap.length == 1 && phi_edges_to_snap.length == 0 && phi_faces_to_snap.length == 0) {
    // Snapping a vertex onto a target
    if (target_uido.type == "vertex") {
      if (!shift_modifier)
        finish_snap_vertex_to_vertex(phi_vertices_to_snap[0], o3d);
      else
        finish_snap_vertex_to_vertex_rigidly(phi_vertices_to_snap[0], o3d);
    }
    else if (target_uido.type == "edge") {
      if (!shift_modifier)
        finish_snap_vertex_to_edge(phi_vertices_to_snap[0], o3d, target_click_location);
      else
        finish_snap_vertex_to_edge_rigidly(phi_vertices_to_snap[0], o3d, target_click_location);
    }
    else if (target_uido.type == "face") {
      display_message_to_user(LOG.WARN, "Cannot snap to face, try projecting instead.");
      // XXX Or actually call the projection interface
    }
  }
  else if (phi_vertices_to_snap.length == 0 && phi_edges_to_snap.length == 1 && phi_faces_to_snap.length == 0) {
    // Snapping an edge onto a target
    if (target_uido.type == "edge") {
      if (!shift_modifier)
        await finish_snap_edge_to_entire_edge(phi_edges_to_snap[0], o3d);
      else
        finish_snap_edge_to_part_of_edge(phi_edges_to_snap[0], o3d);
    }
    else if (target_uido.type == "face") {
      display_message_to_user(LOG.WARN, "Cannot snap to face, try projecting instead.");
      // XXX Or actually call the projection interface
    }
    else {
      // Can't do this, ignore it.
      return;
    }
  }
  else if (phi_vertices_to_snap.length == 0 && phi_edges_to_snap.length == 0 && phi_faces_to_snap.length == 1) {
    // Snapping a face onto a face
    if (target_uido.type == "face") {
      finish_snap_face_to_entire_face(phi_faces_to_snap[0], o3d);
    }
    else {
      // Can't do this, ignore it.
      return;
    }
  }
  else {
    // Mistaken click, abort
    abort_snap_to_object();
  }

  uid_deselect_all_objects();
  uid_unhighlight_all_objects();
  hide_all_reference_partstudio_vertices();
  hide_all_reference_stl_vertices();
  App.state.snapping_to_object = false;
  uid_raynode = uid_objects_raynode;
  uid_manip_raynode.remove(uid_axes_planes_raynode);
  if (App.state.axes_visible)
    uid_objects_raynode.add(uid_axes_planes_raynode);
  animate();
}

export function finish_snap_vertex_to_vertex(vid, o3d) {
  // Move selected vertex to coordinates of vertex object o3d
  var vtarget = o3d.position;
  uil_move_vertex_to_coords( vid, vtarget.x, vtarget.y, vtarget.z );
}

export function finish_snap_vertex_to_vertex_rigidly(vid, o3d) {
  // Move body of selected vertex so that selected vertex falls at coordinates of vertex object o3d

  // Collect all edges of body to move
  var edges_to_move = [];
  var bid = uil_get_body_of_vertex(vid);
  uid_edges.keys().forEach(function(eid){
    if (uil_get_body_of_edge(eid) == bid)
      push_nodups(eid, edges_to_move);
  });

  // Calculate a translation vector based on the first vertex selected in each set.
  var vtarget = o3d.position;
  var vorigin = uid_vertices.getItem(vid).object3d.position;
  var trans = new THREE.Vector3();
  trans.subVectors(vtarget, vorigin);

  start_move_edge_list(edges_to_move);
  move_edge_list( edges_to_move, trans, 0, new THREE.Vector3(0,0,0), new THREE.Vector3(1,0,0));
  end_move_edge_list(edges_to_move);
}

export function finish_snap_vertex_to_edge(vid, o3d, vtarget) {
  // Move selected vertex onto edge
  //var vtarget = find_target_for_projecting_vertex_to_edge_object(uid_vertices.getItem(vid), o3d);
  uil_move_vertex_to_coords( vid, vtarget.x, vtarget.y, vtarget.z );
}

export function finish_snap_vertex_to_edge_rigidly(vid, o3d, vtarget) {
  // Move body of selected vertex so that selected vertex falls on edge

  // Collect all edges of body to move
  var edges_to_move = [];
  var bid = uil_get_body_of_vertex(vid);
  uid_edges.keys().forEach(function(eid){
    if (uil_get_body_of_edge(eid) == bid)
      push_nodups(eid, edges_to_move);
  });

  // Calculate a translation vector based on the first vertex selected in each set.
  //var vtarget = find_target_for_projecting_vertex_to_edge_object(uid_vertices.getItem(vid), o3d);
  var vorigin = uid_vertices.getItem(vid).object3d.position;
  var trans = new THREE.Vector3();
  trans.subVectors(vtarget, vorigin);

  start_move_edge_list(edges_to_move);
  move_edge_list( edges_to_move, trans, 0, new THREE.Vector3(0,0,0), new THREE.Vector3(1,0,0));
  end_move_edge_list(edges_to_move);
}

export function finish_snap_edge_to_part_of_edge(source_edge_id, target_edge_o3d) {

  suppress_animate();
  suppress_undo_tracking();

  var v_array = uil_get_vertices_of_edge(source_edge_id);
  var v1 = uid_vertices.getItem(v_array[0]);
  var v2 = uid_vertices.getItem(v_array[1]);

  // Find target edge point that is closest to each phi edge vertex.
  var target1 = find_target_for_projecting_vertex_to_edge_object(v1, target_edge_o3d);
  var target2 = find_target_for_projecting_vertex_to_edge_object(v2, target_edge_o3d);

  // If the two end targets are too close, we have to abort
  if (target1.distanceTo(target2) < 1e-3) {
    display_message_to_user(LOG.WARN, "Unable to proceed, please place the edge closer to the target and try again.");
    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    return;
  }

  // If necessary flip reference points array
  var target_points = [... target_edge_o3d.geometry.vertices];
  if (target1.distanceTo(target_points[0]) > target2.distanceTo(target_points[0]))
    target_points.reverse();

  // Make list of target edge samples that are between target1 and target2 (or target2 and target1)
  var target_edge_points_to_snap_phi_edge_on = [];
  var hit_first_target = false;
  var hit_second_target = false;
  var eps = 1e-5;

  // Add first target to list of points
  target_edge_points_to_snap_phi_edge_on.push(target1);

  // Add all intermediate reference points
  for (var i = 0; i < target_points.length; i++) {
    var p = target_points[i];
    var p_prev = i > 0 ? target_points[i-1] : null;

    var dont_add_this_point = false;
    if (maths.proximate(target1.distanceTo(p), 0, eps)) {
      // We hit a point that coincides with a target. Set the flags, but don't add it to the array
      // since we're separately adding the targets.
      if (!hit_first_target)
        hit_first_target = true;
      dont_add_this_point = true;
    }
    if (maths.proximate(target2.distanceTo(p), 0, eps)) {
      // We hit a point that coincides with a target. Set the flags, but don't add it to the array
      // since we're separately adding the targets.
      if (!hit_second_target)
        hit_second_target = true;
      dont_add_this_point = true;
    }
    if (p_prev && point_is_projected_on_segment_between_endpoints(target1, p_prev, p)) {
      // We hit a point that is a transition across the first target
      if (!hit_first_target)
        hit_first_target = true;
    }
    if (p_prev && point_is_projected_on_segment_between_endpoints(target2, p_prev, p)) {
      // We hit a point that is a transition across the second target
      if (!hit_second_target)
        hit_second_target = true;
    }

    if (hit_first_target && !hit_second_target && !dont_add_this_point)
      target_edge_points_to_snap_phi_edge_on.push(p);
  }
  // Add second target to list of points
  target_edge_points_to_snap_phi_edge_on.push(target2);

  // Then move the vertices, and deform edge
  uil_move_vertex_to_coords( v1.id, target1.x, target1.y, target1.z );
  uil_move_vertex_to_coords( v2.id, target2.x, target2.y, target2.z );
  deform_edge_to_match_samples(uid_edges.getItem(source_edge_id), target_edge_points_to_snap_phi_edge_on, true, true);

  // We now need to do some smoothing. We'll follow some heuristic based on how many samples we have available

  /* XXX Use:
  int phi_get_ray_edge_intersection( int eid,
                  float rbx, float rby, float rbz,
                  float rdx, float rdy, float rdz, int high_res,
                  float *p_coords, float *t );

  * Intersects a ray with an edge and returns the intersection point or the
  * edge point closest to the ray if no intersection.
  * Input:
  *      eid: Id of the edge.
  *      (rbx,rby,rbz): coordinates of ray base vector.
  *      (rdx,rdy,rdz): coordinates of ray direction vector.
  *      high_res: If set then a higher accuracy is used for approximation.
  * Output:
  *      (*pz,*py,*pz): will contain
  *      p_coords: A 3 element array of floats that on output will contain
  *                the (x,y,z) coordinates for the point on edge closest to ray.
  *      *t: will contain the curve parameter (0 <= t <= 1) corresponding
  *          to (px,py,pz). Can be NULL for no such output.
  *      Returns success/failure (1,0)
*/

  //if (target_edge_points_to_snap_phi_edge_on.length == 2) {
  if (true) {
    // We only have the two endpoint samples, we'll have to use the midpoint of the edge
    var new_vertex_id = uil_add_vertex_at_param( source_edge_id, 0.5 );
    uil_fix_objects([new_vertex_id]);
    var es = uil_get_edges_of_vertex(new_vertex_id);
    uil_smoothen_edges(es, REGULAR_STRENGTH);
    uil_unfix_objects([new_vertex_id]);
    uil_delete_vertices([new_vertex_id], true);
  }
  else if (target_edge_points_to_snap_phi_edge_on.length == 3) {
    // Use the middle sample
  }
  else if (target_edge_points_to_snap_phi_edge_on.length == 4) {
    // Use the two samples
  }
  else if (target_edge_points_to_snap_phi_edge_on.length > 4) {
    // Use the sample that is closest to the middle
  }

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
}

export const finish_snap_edge_to_entire_edge = withSpinner('Snapping', async function finish_snap_edge_to_entire_edge(source_edge_id, target_edge_o3d) {
  suppress_animate();
  suppress_undo_tracking();

  var target_v1 = new THREE.Vector3();
  var target_v2 = new THREE.Vector3();
  target_v1.copy(target_edge_o3d.geometry.vertices[0]);
  target_v2.copy(target_edge_o3d.geometry.vertices[target_edge_o3d.geometry.vertices.length-1]);

  var v_array = uil_get_vertices_of_edge(source_edge_id);
  var v1 = uid_vertices.getItem(v_array[0]);
  var v2 = uid_vertices.getItem(v_array[1]);

  var d11 = vector3.point_point_distance(v1.object3d.position, target_v1);
  var d12 = vector3.point_point_distance(v1.object3d.position, target_v2);
  var d21 = vector3.point_point_distance(v2.object3d.position, target_v1);
  var d22 = vector3.point_point_distance(v2.object3d.position, target_v2);

  var vecV = new THREE.Vector3();
  vecV.copy(v1.object3d.position);
  vecV.sub(v2.object3d.position);
  var vectV = new THREE.Vector3();
  vectV.copy(target_v1);
  vectV.sub(target_v2);
  var dot = vecV.dot(vectV);

  if (dot > 0) {
    uil_move_vertex_to_coords( v1.id, target_v1.x, target_v1.y, target_v1.z );
    uil_move_vertex_to_coords( v2.id, target_v2.x, target_v2.y, target_v2.z );
  }
  else {
    uil_move_vertex_to_coords( v1.id, target_v2.x, target_v2.y, target_v2.z );
    uil_move_vertex_to_coords( v2.id, target_v1.x, target_v1.y, target_v1.z );
  }

  // Also deform edge to match reference edge
  deform_edge_to_match_samples(uid_edges.getItem(source_edge_id), target_edge_o3d.geometry.vertices, true, true);

  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);
});

export function find_target_for_projecting_vertex_to_edge_object(v, edge_o3d) {

  // Find the two closest edge points to our vertex v. Do a linear interpolation
  // between them.

  var min1 = new THREE.Vector3();
  var min2 = new THREE.Vector3();
  var o = new THREE.Vector3();
  o.copy(v.object3d.position);

  var min1dist = 99999;
  var min2dist = 99999;

  edge_o3d.geometry.vertices.forEach(function(p){
    var d = p.distanceTo(o);
    if (d < min1dist) {
      min2dist = min1dist;
      min2.copy(min1);
      min1dist = d;
      min1.copy(p);
    }
    else if (d < min2dist) {
      min2dist = d;
      min2.copy(p);
    }
  });

  var target = new THREE.Vector3();

  // Check whether the vertex will project onto the segment between the two endpoints or not
  if (!point_is_projected_on_segment_between_endpoints(o, min1, min2)) {
    // Go directly to the closest minimum distance.
    target.copy(min1);
  }
  else {
    // Do a projection of the vertex onto the segment between m1 and m2.
    var d = new THREE.Vector3();
    d.subVectors(min2, min1).normalize();
    target = vector3.project_point_on_line(o, min1, d);
  }
  return target;
}

export function point_is_projected_on_segment_between_endpoints(pt, endpoint1, endpoint2) {

  var d = new THREE.Vector3();
  var d1 = new THREE.Vector3();
  var d2 = new THREE.Vector3();
  d.subVectors(endpoint2, endpoint1).normalize();
  d1.subVectors(endpoint1, pt);
  d2.subVectors(endpoint2, pt);
  var dd1 = d1.dot(d);
  var dd2 = d2.dot(d);

  // If the two dot products have the same sign, we're outside the segment range, otherwise we project
  // onto the segment. Check for same sign by multiplying.
  return (dd1 * dd2 < 0);
}

export function point_is_projected_on_triangle(pt, p1, p2, p3) {
  // For now bypass this mechanism
  return false;
}

export const start_convert_reference_to_phi = withSpinner("Converting", function start_convert_reference_to_phi(modifier = false) {
  if (App.state.editing_reference_partstudio || App.state.editing_reference_stl)
    return;
  if (!App.state.converting_reference_to_phi) {
    suppress_undo_tracking();
    show_reference_partstudio_model();
    App.state.converting_reference_to_phi = true;
    uid_raynode = uid_ref_partstudio_faces_raynode;
    request_setting_button_states();
    if (modifier)
      make_sandwich_converted_face = true;
    else
      make_sandwich_converted_face = false;
  }
  else {
    if (!make_sandwich_converted_face && modifier)
      make_sandwich_converted_face = true;
      finish_convert_reference_faces_to_phi(uid_selected_reference_faces);
  }

  animate();
}, () => uid_selected_reference_faces.length > 7);

export function deform_edge_to_match_samples(e, points, smoothen_after_deformation = false, reapply_tangencies_after_deformation = false) {
  suppress_undo_tracking();
  suppress_animate();
  suppress_core_face_tessellation();

  var v_array = uil_get_vertices_of_edge(e.id);
  var v1 = uid_vertices.getItem(v_array[0]);
  var v2 = uid_vertices.getItem(v_array[1]);

  var p_array = [ ...points ];

  for (var i = 0; i < p_array.length; i++) {
    if (i > 0 && p_array[i].distanceTo(p_array[i-1]) < 1e-6)
      p_array.splice(i, 1);
  }

  // Remove any sample that is very near its previous sample
  var local_epsilon = 1e-4;
  var done_here = false;
  while (!done_here) {
    done_here = true;
    for (var i = p_array.length-1; i >= 1; i--) {
      if (p_array[i] && p_array[i-1] && maths.proximate(vector3.point_point_distance(p_array[i], p_array[i-1]), 0, local_epsilon)) {
        p_array.splice(i, 1);
        done_here = false;
      }
    }
  }

  // Fill in with more samples if we don't have enough
  done_here = false;
  while (p_array.length < 22 && !done_here) {
    var length_before = p_array.length;
    p_array = fill_up_sample_array(p_array);
    if (p_array.length == length_before)
      // Failed to add more samples, we're done here
      done_here = true;
  }

  // Check that the samples go in order from V1 to V2, otherwise reverse
  var d1 = vector3.point_point_distance(v1.object3d.position, p_array[0]);
  var d2 = vector3.point_point_distance(v1.object3d.position, p_array[p_array.length-1]);
  if (d1 > d2)
    p_array.reverse();

  // Find neighbours of this edge, we'll need them
  var neids = get_neighbouring_edges_of_edge(e.id);

  // Take note of any tangencies on this edge
  if (reapply_tangencies_after_deformation) {
    var smooth_edge_neighbours = [];
    neids.forEach(function(neid){
      if (uil_is_joint_smooth(e.id, neid))
        push_nodups(neid, smooth_edge_neighbours);
    });
  }

  // Modify the edge geometry
  var res = uil_modify_edge_geometry(e.id, v1.id, v2.id, p_array);

  // Use new variable to hold the edge id (it may change as we smoothen with added vertices)
  var modified_edge_id = e.id;

  if (smoothen_after_deformation) {
    // Also smoothen edge, but first add and fix 5 vertices on it
    var new_vertex_id1 = uil_add_vertex_at_param( modified_edge_id, 0.5 );
    var e1 = uil_get_edges_of_vertex(new_vertex_id1)[0];
    var e2 = uil_get_edges_of_vertex(new_vertex_id1)[1];
    var new_vertex_id2 = uil_add_vertex_at_param( e1, 0.5 );
    var new_vertex_id3 = uil_add_vertex_at_param( e2, 0.5 );
    var e3 = uil_get_edges_of_vertex(new_vertex_id2)[0];
    var e4 = uil_get_edges_of_vertex(new_vertex_id2)[1];
    var e5 = uil_get_edges_of_vertex(new_vertex_id3)[0];
    var e6 = uil_get_edges_of_vertex(new_vertex_id3)[1];
    var new_vertex_id4 = uil_add_vertex_at_param( e3, 0.5 );
    var new_vertex_id5 = uil_add_vertex_at_param( e4, 0.5 );
    var new_vertex_id6 = uil_add_vertex_at_param( e5, 0.5 );
    var new_vertex_id7 = uil_add_vertex_at_param( e6, 0.5 );
    var e7 = uil_get_edges_of_vertex(new_vertex_id4)[0];
    var e8 = uil_get_edges_of_vertex(new_vertex_id4)[1];
    var e9 = uil_get_edges_of_vertex(new_vertex_id5)[0];
    var e10 = uil_get_edges_of_vertex(new_vertex_id5)[1];
    var e11 = uil_get_edges_of_vertex(new_vertex_id6)[0];
    var e12 = uil_get_edges_of_vertex(new_vertex_id6)[1];
    var e13 = uil_get_edges_of_vertex(new_vertex_id7)[0];
    var e14 = uil_get_edges_of_vertex(new_vertex_id7)[1];

    uil_fix_objects([new_vertex_id1]);
    uil_fix_objects([new_vertex_id2]);
    uil_fix_objects([new_vertex_id3]);
    uil_fix_objects([new_vertex_id4]);
    uil_fix_objects([new_vertex_id5]);
    uil_fix_objects([new_vertex_id6]);
    uil_fix_objects([new_vertex_id7]);
    uil_smoothen_edges([e7, e8, e9, e10, e11, e12, e13, e14], FAINT_STRENGTH);
    uil_unfix_objects([new_vertex_id1]);
    uil_unfix_objects([new_vertex_id2]);
    uil_unfix_objects([new_vertex_id3]);
    uil_unfix_objects([new_vertex_id4]);
    uil_unfix_objects([new_vertex_id5]);
    uil_unfix_objects([new_vertex_id6]);
    uil_unfix_objects([new_vertex_id7]);
    uil_delete_vertices([new_vertex_id1, new_vertex_id2, new_vertex_id3, new_vertex_id4, new_vertex_id5, new_vertex_id6, new_vertex_id7], true);

    // Find new modified_edge_id from its neighbours
    const common_elements = pipe(
        neids,
        map(compose(get_neighbouring_edges_of_edge, set)),
        foldl1(intersect, new Set()),
        array,
    );
    console.log(common_elements);
    if (common_elements.length === 1)
      modified_edge_id = common_elements[0];
  }

  //Re-apply tangencies after fixing the modified edge
  if (reapply_tangencies_after_deformation && smooth_edge_neighbours.length > 0) {
    let pairs = [];
    let fxv1 = uil_get_vertices_of_edge(modified_edge_id)[0];
    let fxv2 = uil_get_vertices_of_edge(modified_edge_id)[1];
    uil_fix_objects([fxv1, fxv2]);
    smooth_edge_neighbours.forEach(function(neid){
      pairs.push([modified_edge_id, neid]);
    });
    uil_add_joint_smoothness(pairs, FAINT_STRENGTH, true, true);
    uil_unfix_objects([fxv1, fxv2]);
  }

  unsuppress_core_face_tessellation_if_ready();
  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready(false);

  if (!res) {
    console.log("ERROR: Unable to modify edge " + modified_edge_id + "with vertices " + v1.id + " and " + v2.id + " and samples:");
    console.log(p_array);
  }
}

export function fill_up_sample_array(ar) {
  var tmp_array = [];
  var local_epsilon = 1e-4;

  for (var i = 0; i < ar.length - 1 ; i ++) {
    tmp_array.push(ar[i]);
    // Add a new intermediate sample if there is enough "space" between this and the next sample
    if (ar[i] && ar[i+1] && vector3.point_point_distance(ar[i], ar[i+1]) >  2.5 * local_epsilon ) {
      var new_sample = new THREE.Vector3();
      new_sample.copy(ar[i]);
      new_sample.add(ar[i+1]);
      new_sample.multiplyScalar(0.5);
      tmp_array.push(new_sample);
    }
  }
  tmp_array.push(ar[ar.length-1]);
  ar = [ ...tmp_array];
  tmp_array = [];
  for (var i = ar.length-1; i >= 0 ; i--) {
    if (!ar[i] || isNaN(ar[i].x) || isNaN(ar[i].y) || isNaN(ar[i].z))
      ar.splice(i, 1);
  }

  return ar;
}

export function finish_snap_face_to_entire_face(fid, target_face_o3d) {

  var target_face_uido = uid_object3dIDToUid_object.getItem(target_face_o3d.id)
  var target_is_reference = true;
  if (uid_faces.hasItem(target_face_uido.id))
    target_is_reference = false;

  if (target_is_reference) {
    var target_v_array = [];
    var target_face_ref = uid_reference_faces.getItem(target_face_uido.id);
    target_face_ref.edges.forEach(function(eid){
      var e = uid_reference_edges.getItem(eid);
      e.vertices.forEach(function(vid){
        if (target_v_array.indexOf(vid) == -1)
          target_v_array.push(vid);
      });
    });
  }
  else {
    var target_v_array = [];
    var target_edges = uil_get_edges_of_face(target_face_uido.id);
    target_edges.forEach(function(eid){
      var e = uid_edges.getItem(eid);
      var vs = uil_get_vertices_of_edge(eid);
      vs.forEach(function(vid){
        if (target_v_array.indexOf(vid) == -1)
          target_v_array.push(vid);
      });
    });
  }

  var v_array = [];
  uil_get_edges_of_face(fid).forEach(function(eid){
    uil_get_vertices_of_edge(eid).forEach(function(vid){
      if (v_array.indexOf(vid) == -1)
        v_array.push(vid);
    });
  });

  if (target_v_array.length > v_array.length) {
    display_message_to_user(LOG.INFO, "Target face has more edges than Phi face, unable to proceed");
    return;
  }
  else if (target_v_array.length < v_array.length) {
    display_message_to_user(LOG.INFO, "Target face has fewer edges than Phi face, unable to proceed");
    return;
  }

  // Do the actual snapping

  suppress_undo_tracking();
  suppress_animate();

  // Create two sorted lists of arrays
  var sorted_vertices = sort_face_vertices(fid);
  var sorted_target_vertices;
  if (target_is_reference)
    sorted_target_vertices = sort_reference_face_vertices(target_face_uido.id);
  else
    sorted_target_vertices = sort_face_vertices(target_face_uido.id);

  var n_vertices = sorted_vertices.length;
  var v_index = 0;
  var rv_index;
  var best_match_value = -1;
  var best_match_rv_index = 0;
  var best_match_rotation = 0;
  for (rv_index = 0; rv_index < n_vertices; rv_index ++) {
    // Match v_index=0 with rv_index
    var loop_index;
    var d = 0.0001;
    var G = 0;
    for (loop_index = 0; loop_index < n_vertices; loop_index ++) {
      var v = uid_vertices.getItem(sorted_vertices[(v_index+loop_index) % n_vertices]);
      var rv;
      if (target_is_reference) {
        rv = uid_reference_vertices.getItem(sorted_target_vertices[(rv_index+loop_index) % n_vertices]);
        G += 1/(vector3.point_point_distance(v.object3d.position, rv.uid_object.object3d.position)+d);
      }
      else{
        rv = uid_vertices.getItem(sorted_target_vertices[(rv_index+loop_index) % n_vertices]);
        G += 1/(vector3.point_point_distance(v.object3d.position, rv.object3d.position)+d);
      }
    }
    if (G > best_match_value) {
      best_match_value = G;
      best_match_rv_index = rv_index;
      best_match_rotation = 1;
    }
    var G = 0;
    for (loop_index = 0; loop_index < n_vertices; loop_index ++) {
      var v = uid_vertices.getItem(sorted_vertices[(v_index+loop_index) % n_vertices]);
      var rv;
      if (target_is_reference) {
        rv = uid_reference_vertices.getItem(sorted_target_vertices[(rv_index-loop_index+n_vertices) % n_vertices]);
        G += 1/(vector3.point_point_distance(v.object3d.position, rv.uid_object.object3d.position)+d);
      }
      else {
        rv = uid_vertices.getItem(sorted_target_vertices[(rv_index-loop_index+n_vertices) % n_vertices]);
        G += 1/(vector3.point_point_distance(v.object3d.position, rv.object3d.position)+d);
      }
    }
    if (G > best_match_value) {
      best_match_value = G;
      best_match_rv_index = rv_index;
      best_match_rotation = -1;
    }
  }
  rv_index = best_match_rv_index;

  for (loop_index = 0; loop_index < n_vertices; loop_index ++) {
    var v = uid_vertices.getItem(sorted_vertices[(v_index+loop_index) % n_vertices]);
    if (best_match_rotation == 1) {
      var rv;
      if (target_is_reference)
        rv = uid_reference_vertices.getItem(sorted_target_vertices[(rv_index+loop_index) % n_vertices]);
      else
        rv = uid_vertices.getItem(sorted_target_vertices[(rv_index+loop_index) % n_vertices]);
    }
    else {
      var rv;
      if (target_is_reference)
        rv = uid_reference_vertices.getItem(sorted_target_vertices[(rv_index-loop_index+n_vertices) % n_vertices]);
      else
        rv = uid_vertices.getItem(sorted_target_vertices[(rv_index-loop_index+n_vertices) % n_vertices]);
    }
    if (target_is_reference)
      uil_move_vertex_to_coords( v.id, rv.uid_object.object3d.position.x, rv.uid_object.object3d.position.y, rv.uid_object.object3d.position.z, uid_get_ball_size(), uid_get_ball_hardness() );
    else
      uil_move_vertex_to_coords( v.id, rv.object3d.position.x, rv.object3d.position.y, rv.object3d.position.z, uid_get_ball_size(), uid_get_ball_hardness() );
  }

  // Repeat loop to deform edges
  for (loop_index = 0; loop_index < n_vertices; loop_index ++) {
    var v = uid_vertices.getItem(sorted_vertices[(v_index+loop_index) % n_vertices]);
    var v_next = uid_vertices.getItem(sorted_vertices[(v_index+loop_index+1) % n_vertices]);
    var rv;
    var rv_next;
    if (best_match_rotation == 1) {
      if (target_is_reference) {
        rv = uid_reference_vertices.getItem(sorted_target_vertices[(rv_index+loop_index) % n_vertices]);
        rv_next = uid_reference_vertices.getItem(sorted_target_vertices[(rv_index+loop_index+1) % n_vertices]);
      }
      else {
        rv = uid_vertices.getItem(sorted_target_vertices[(rv_index+loop_index) % n_vertices]);
        rv_next = uid_vertices.getItem(sorted_target_vertices[(rv_index+loop_index+1) % n_vertices]);
      }
    }
    else {
      if (target_is_reference) {
        rv = uid_reference_vertices.getItem(sorted_target_vertices[(rv_index-loop_index+n_vertices) % n_vertices]);
        rv_next = uid_reference_vertices.getItem(sorted_target_vertices[(rv_index-(loop_index+1)+n_vertices) % n_vertices]);
      }
      else {
        rv = uid_vertices.getItem(sorted_target_vertices[(rv_index-loop_index+n_vertices) % n_vertices]);
        rv_next = uid_vertices.getItem(sorted_target_vertices[(rv_index-(loop_index+1)+n_vertices) % n_vertices]);
      }
    }

    // Find edge and reference edge that share vertices (v, v_next) and (rv, rv_next)
    var common_edge = null;
    var common_target_edge = null;
    // First for regular edge
    var edges = uil_get_edges_of_vertex(v.id);
    edges.forEach(function(eid){
      if (uil_is_vertex_of_edge(v_next.id, eid))
        common_edge = uid_edges.getItem(eid);
    });
    // Then for reference edge
    if (target_is_reference) {
      rv.edges.forEach(function(eid){
        var e = uid_reference_edges.getItem(eid);
        if (e.faces.includes(target_face_uido.id)) {
          e.vertices.forEach(function(vid) {
            if (vid == rv_next.uid_object.id)
            common_target_edge = e;
          });
        }
      });
    }
    else {
      var es = uil_get_edges_of_vertex(rv.id);
      es.forEach(function(eid){
        var e = uid_edges.getItem(eid);
        var vs = uil_get_vertices_of_edge(e.id);
        vs.forEach(function(vid) {
          if (vid == rv_next.id)
          common_target_edge = e;
        });
      });
    }
    if (target_is_reference)
      deform_edge_to_match_samples(common_edge, common_target_edge.uid_object.object3d.geometry.vertices, false, false);
    else
      deform_edge_to_match_samples(common_edge, common_target_edge.object3d.geometry.vertices, false, false);
  }
  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
}

export async function finish_convert_reference_faces_to_phi(reference_faces) {

  suppress_animate();

  // We assume that at least one reference face has been selected.
  if (uid_selected_reference_faces.length == 0) {
    abort_convert_reference_faces_to_phi();
    return;
  }

  var error_messages = [];
  for (const rf of uid_selected_reference_faces)
    await convert_single_reference_face_to_phi(rf, error_messages);

  handle_missing_tangencies();
  wrap_up_finish_convert_single_reference_face_to_phi(error_messages);
}

export async function convert_single_reference_face_to_phi(rf, error_messages) {
  var tmp_reference_bodies = [];
  var tmp_reference_faces = [];
  var tmp_reference_edges = [];
  var tmp_reference_vertices = [];

  // Create a bogus body
  var bid = get_global_id();
  tmp_reference_bodies.push(bid);
  uid_reference_bodies.setItem(bid, bid);

  var ref_uido = uid_object3dIDToUid_object.getItem(rf.id);
  var uid_face = uid_reference_faces.getItem(ref_uido.id);

  // Make face belong to this body
  const previous_body_id = uid_face.onshape_body_id;
  uid_face.onshape_body_id = bid;

  tmp_reference_faces.push(ref_uido.id);

  uid_face.edges.forEach(function(eid){
    tmp_reference_edges.push(eid);
    var e = uid_reference_edges.getItem(eid);
    tmp_reference_vertices.push(e.vertices[0]);
    tmp_reference_vertices.push(e.vertices[1]);
  });

  // Remove duplicate vertices
  tmp_reference_vertices = [... new Set(tmp_reference_vertices)];

  // Duplicate the face (a quick and dirty trick that will probably not work)
  tmp_reference_faces.push(tmp_reference_faces[0]);

  const res = await load_reference_objects_to_phi(tmp_reference_vertices, tmp_reference_edges, tmp_reference_faces, tmp_reference_bodies, true, error_messages);

  // Restore previous body id
  uid_face.onshape_body_id = previous_body_id;

  // Also make inner face invisible
  if (error_messages.length == 0 && !make_sandwich_converted_face && inward_looking_converted_face_id > 0) {
    uid_deselect_all_objects();
    uil_make_faces_invisible([inward_looking_converted_face_id], false);
    inward_looking_converted_face_id = 0;
    outward_looking_converted_face_id = 0;
    App.state.converting_reference_to_phi = false;
  }

  return;
}

export function wrap_up_finish_convert_single_reference_face_to_phi(error_messages) {
  uid_deselect_all_objects();
  // Deselect all reference faces
  uid_selected_reference_faces = [];
  uid_reference_faces.keys().forEach(function(fid){
    set_default_face_color(fid);
  });
  App.state.converting_reference_to_phi = false;
  uid_raynode = uid_objects_raynode;
  uid_manip_raynode.remove(uid_axes_planes_raynode);
  if (App.state.axes_visible)
    uid_objects_raynode.add(uid_axes_planes_raynode);

  uil_end_compound_operation();
  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  set_button_states();

  if (error_messages.length > 0) {
    display_message_to_user(LOG.WARN, "Converting face didn't complete correctly. Possible reason is missing or incomplete Onshape face tessellation information.")
  }
}

export function abort_convert_reference_faces_to_phi() {
  uid_deselect_all_objects();
  uil_end_compound_operation();
  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();

  // Deselect all reference faces
  uid_selected_reference_faces = [];
  uid_reference_faces.keys().forEach(function(fid){
    set_default_face_color(fid);
  });
  App.state.converting_reference_to_phi = false;
  uid_raynode = uid_objects_raynode;
  uid_manip_raynode.remove(uid_axes_planes_raynode);
  if (App.state.axes_visible)
    uid_objects_raynode.add(uid_axes_planes_raynode);

  set_button_states();
}

export function stitch_edges_of_open_faces_on_same_body(e1id, e2id, f1id, f2id) {
  // Here we attempt to stitch two edges that:
  // - are on the same body
  // - are on faces that are visible only on one side of the edge
  // - share one common vertex

  // First of all, if we were called with faces instead of edges, make sure all is good and find the correct edges
  if (e1id == 0 && e2id == 0 && f1id > 0 && f2id > 0) {
    // Make sure edges are on the same body
    if (uil_get_body_of_edge(e1id) != uil_get_body_of_edge(e2id)) {
      display_message_to_user(LOG.WARN, "To stitch edges of the same body, they must share one vertex.");
      return;
    }

    // Make sure the two faces share one vertex (only)
    let vcommon = 0;
    let common_found = 0;
    vertices_of_face(f1id).forEach(function(v1id){
      vertices_of_face(f2id).forEach(function(v2id){
        if (v1id == v2id) {
          vcommon = v1id;
          common_found++;
        }
      });
    });

    if (vcommon == 0 || common_found != 1) {
      display_message_to_user(LOG.WARN, "You must select two faces containing two edges that share a vertex and are across from each other.");
      return;
    }

    let edges_found = 0;
    uil_get_edges_of_vertex(vcommon).forEach(function(eid){
      if ((uil_is_object_visible(uil_get_faces_of_edge(eid)[0]) && !uil_is_object_visible(uil_get_faces_of_edge(eid)[1])) ||
          (uil_is_object_visible(uil_get_faces_of_edge(eid)[1]) && !uil_is_object_visible(uil_get_faces_of_edge(eid)[0]))) {
        if (edges_found == 0) {
          e1id = eid;
          edges_found++;
        }
        else if (edges_found == 1) {
          e2id = eid;
          edges_found++;
        }
        else {
          // Too many edges satisfy criteria, can't proceed
          e1id = 0;
          e2id = 0;
        }
      }
    });

    if (e1id == 0 || e2id == 0) {
      display_message_to_user(LOG.WARN, "Unable to identify two edges to stich on the selected faces.");
      return;
    }

  }



  // Make sure edges are on the same body
  if (uil_get_body_of_edge(e1id) != uil_get_body_of_edge(e2id)) {
    display_message_to_user(LOG.WARN, "To stitch edges of the same body, they must share one vertex.");
    return;
  }

  // Make sure only one of the two faces of each edge is visible
  if (
    ((uil_is_object_visible(uil_get_faces_of_edge(e1id)[0]) &&
      uil_is_object_visible(uil_get_faces_of_edge(e1id)[1])) ||
     (!uil_is_object_visible(uil_get_faces_of_edge(e1id)[1]) &&
      !uil_is_object_visible(uil_get_faces_of_edge(e1id)[0])))
    &&
    ((uil_is_object_visible(uil_get_faces_of_edge(e2id)[0]) &&
      uil_is_object_visible(uil_get_faces_of_edge(e2id)[1])) ||
     (!uil_is_object_visible(uil_get_faces_of_edge(e2id)[1]) &&
      !uil_is_object_visible(uil_get_faces_of_edge(e2id)[0])))
  ) {
    display_message_to_user(LOG.WARN, "To stitch edges of the same body, they must share one vertex.");
    return;
  }

  // Make sure the two edges share one vertex
  let v1a = uil_get_vertices_of_edge(e1id)[0];
  let v1b = uil_get_vertices_of_edge(e1id)[1];
  let v2a = uil_get_vertices_of_edge(e2id)[0];
  let v2b = uil_get_vertices_of_edge(e2id)[1];

  let vshared = 0;
  let v1distant = 0;
  let v2distant = 0;
  if (v1a == v2a) {
    vshared = v1a;
    v1distant = v1b;
    v2distant = v2b;
  }
  else if (v1a == v2b) {
    vshared = v1a;
    v1distant = v1b;
    v2distant = v2a;
  }
  else if (v1b == v2b) {
    vshared = v1b;
    v1distant = v1a;
    v2distant = v2a;
  }
  else if (v1b == v2a) {
    vshared = v1b;
    v1distant = v1a;
    v2distant = v2b;
  }
  else {
    display_message_to_user(LOG.WARN, "To stitch edges of the same body, they must share one vertex.");
    return;
  }

  // Collect the visible and invisible faces of the two edges, and make sure they are not the same.
  let f1vis = 0;
  let f1invis = 0;
  let f2vis = 0;
  let f2invis = 0;

  if ((uil_is_object_visible(uil_get_faces_of_edge(e1id)[0]))) {
    f1vis = uil_get_faces_of_edge(e1id)[0];
    f1invis = uil_get_faces_of_edge(e1id)[1];
  }
  else {
    f1vis = uil_get_faces_of_edge(e1id)[1];
    f1invis = uil_get_faces_of_edge(e1id)[0];
  }

  if ((uil_is_object_visible(uil_get_faces_of_edge(e2id)[0]))) {
    f2vis = uil_get_faces_of_edge(e2id)[0];
    f2invis = uil_get_faces_of_edge(e2id)[1];
  }
  else {
    f2vis = uil_get_faces_of_edge(e2id)[1];
    f2invis = uil_get_faces_of_edge(e2id)[0];
  }

  if (f1vis == f2vis) {
    display_message_to_user(LOG.WARN, "Cannot stitch edges of the same face.");
    return;
  }

  // Check which of the two edges is longer and use that one first
  if (vector3.point_point_distance(uid_vertices.getItem(vshared).object3d.position, uid_vertices.getItem(v1distant).object3d.position) <
      vector3.point_point_distance(uid_vertices.getItem(vshared).object3d.position, uid_vertices.getItem(v2distant).object3d.position)) {
      let etmp = e1id;
      e1id = e2id;
      e2id = etmp;
  }

  suppress_animate();
  suppress_undo_tracking();

  // Properly split invisible faces
  properly_split_invisible_face_connected_components([], false, false);

  // Make an open bridge between the two edges
  uid_connect_edges_with_invisible_faces_on_them([e1id, e2id]);

  // Remove one of the two edges. I'll pick the longest of the two. Also remove its distant vertex
  uil_delete_edges([e1id], false);

  if (uil_get_edges_of_vertex(v1distant).filter(eid => uil_is_object_visible(eid)).length == 2)
    uil_delete_vertices([v1distant]);

  unsuppress_animate_if_ready();
  unsuppress_undo_tracking_if_ready();
}


export function merge_bodies_on_edge_selecting_edges_or_faces(e1id, e2id, f1id, f2id) {

  if (e1id > 0 && e2id > 0) {
    var b1id = uil_get_body_of_edge(e1id);
    var b2id = uil_get_body_of_edge(e2id);
    if (b1id==b2id) {
      display_message_to_user(LOG.WARN, "Select two edges of two different bodies to stitch them.")
      return;
    }
  }
  else if (f1id > 0 && f2id > 0) {
    var b1id = uil_get_body_of_face(f1id);
    var b2id = uil_get_body_of_face(f2id);
    if (b1id==b2id) {
      display_message_to_user(LOG.WARN, "Select two faces of two different bodies to stitch their coincident edges.")
      return;
    }
    // Identify the two coincident edges.
    var es1 = uil_get_edges_of_face(f1id);
    var es2 = uil_get_edges_of_face(f2id);
    var coincident_pairs = [];
    es1.forEach(function(ef1id){
      es2.forEach(function(ef2id){
        if (edges_match_based_on_vertices(ef1id, ef2id))
          coincident_pairs.push([ef1id, ef2id]);
      });
    });
    if (coincident_pairs.length == 0) {
      display_message_to_user(LOG.WARN, "No coincident edges were identified on the two selected faces. Unable to proceed with stitching.")
      return;
    }
    else if (coincident_pairs.length > 1) {
      display_message_to_user(LOG.WARN, "More than one coincident edge pairs were identified on the two selected faces. Please select the edges you want to stitch.")
      return;
    }

    // We identified two edges to use for stitching, so we can proceed.
    e1id = coincident_pairs[0][0];
    e2id = coincident_pairs[0][1];
  }
  else {
    display_message_to_user(LOG.WARN, "Unable to stitch bodies based on selected objects.")
    return;
  }

  //Check that we don't have anything fixed.
  if (uil_is_object_fixed(e1id) || uil_is_object_fixed(e2id) ||
      uil_is_object_fixed(uil_get_vertices_of_edge(e1id)[0]) ||
      uil_is_object_fixed(uil_get_vertices_of_edge(e1id)[1]) ||
      uil_is_object_fixed(uil_get_vertices_of_edge(e2id)[0]) ||
      uil_is_object_fixed(uil_get_vertices_of_edge(e2id)[1])) {
    display_message_to_user(LOG.WARN, "Edges or their vertices are fixed, please unfix them before stitching.")
    return;
  }

  /* No we don't want this check after all
  // Check that both edges have visible faces on either side
  if (!is_object_visible(uil_get_faces_of_edge(e1id)[0]) ||
      !is_object_visible(uil_get_faces_of_edge(e1id)[1]) ||
      !is_object_visible(uil_get_faces_of_edge(e2id)[0]) ||
      !is_object_visible(uil_get_faces_of_edge(e2id)[1])) {
    display_message_to_user(LOG.WARN, "You can only stitch two edges that have two visible faces on them.")
    return;
  }
  */

  // Find vertices to match. We will use how parallel the two vectors joining the pairs of vertices are.
  let va1 = uil_get_vertices_of_edge(e1id)[0];
  let va2 = uil_get_vertices_of_edge(e1id)[1];
  let vb1 = uil_get_vertices_of_edge(e2id)[0];
  let vb2 = uil_get_vertices_of_edge(e2id)[1];
  let fa1 = uil_get_faces_of_edge(e1id)[0];
  let fa2 = uil_get_faces_of_edge(e1id)[1];
  let fb1 = uil_get_faces_of_edge(e2id)[0];
  let fb2 = uil_get_faces_of_edge(e2id)[1];

  let v1id = 0;
  let v2id = 0;
  let faid = 0;
  let fbid = 0;

  // If one vertex on each side is selected, use them as guides for how to stitch
  if (object_is_selected(va1) && !object_is_selected(va2) && object_is_selected(vb1) && !object_is_selected(vb2)) {
    v1id = va1;
    v2id = vb1;
  }
  if (object_is_selected(va1) && !object_is_selected(va2) && !object_is_selected(vb1) && object_is_selected(vb2)) {
    v1id = va1;
    v2id = vb2;
  }
  else if (!object_is_selected(va1) && object_is_selected(va2) && object_is_selected(vb1) && !object_is_selected(vb2)) {
    v1id = va2;
    v2id = vb1;
  }
  else if (object_is_selected(va1) && !object_is_selected(va2) && !object_is_selected(vb1) && object_is_selected(vb2)) {
    v1id = va2;
    v2id = vb2;
  }
  // Otherwise, if each edge has one visible and one invisible face on them, order the vertices so that the
  // visible will be stitched with the visible
  else if (uil_is_object_visible(fa1) && !uil_is_object_visible(fa2) && uil_is_object_visible(fb1) && !uil_is_object_visible(fb2)) {
    faid = fa1;
    fbid = fb1;
  }
  else if (uil_is_object_visible(fa1) && !uil_is_object_visible(fa2) && !uil_is_object_visible(fb1) && uil_is_object_visible(fb2)) {
    faid = fa1;
    fbid = fb2;
  }
  else if (!uil_is_object_visible(fa1) && uil_is_object_visible(fa2) && uil_is_object_visible(fb1) && !uil_is_object_visible(fb2)) {
    faid = fa2;
    fbid = fb1;
  }
  else if (!uil_is_object_visible(fa1) && uil_is_object_visible(fa2) && !uil_is_object_visible(fb1) && uil_is_object_visible(fb2)) {
    faid = fa2;
    fbid = fb2;
  }

  // Do the geometric matching. We'll either use it as our final result, or just to warn the user we have
  // used the face normals instead.
  let va1b1 = new THREE.Vector3().subVectors(uid_vertices.getItem(vb1).object3d.position, uid_vertices.getItem(va1).object3d.position).normalize();
  let va1b2 = new THREE.Vector3().subVectors(uid_vertices.getItem(vb2).object3d.position, uid_vertices.getItem(va1).object3d.position).normalize();
  let va2b1 = new THREE.Vector3().subVectors(uid_vertices.getItem(vb1).object3d.position, uid_vertices.getItem(va2).object3d.position).normalize();
  let va2b2 = new THREE.Vector3().subVectors(uid_vertices.getItem(vb2).object3d.position, uid_vertices.getItem(va2).object3d.position).normalize();

  let angle_a1b1a2b2 = vector3.angle(va1b1, va2b2);
  let angle_a1b2a2b1 = vector3.angle(va1b2, va2b1);

  let v1_tmp_id = va1;
  let v2_tmp_id = vb1;
  if (angle_a1b1a2b2 > angle_a1b2a2b1) {
    v1_tmp_id = va1;
    v2_tmp_id = vb2;
  }

  if (v1id == 0 && v2id == 0 && faid != 0 && fbid != 0) {
    // We haven't identified the vertices, but we have identified face pairs to help us out
    var listified_edges_a = uil_get_edges_of_face(faid);
    var listified_edges_b = uil_get_edges_of_face(fbid);
    var edge_pairs_a = find_all_pairs_in_listified_edge_list(listified_edges_a, false);
    var edge_pairs_b = find_all_pairs_in_listified_edge_list(listified_edges_b, false);

    // Get the vertices in order
    var vs_a = [];
    edge_pairs_a.forEach(function(ep){
      let v = uil_find_edge_shared_vertex(ep[0], ep[1]);
      vs_a.push(v);
    });

    var vs_b = [];
    edge_pairs_b.forEach(function(ep){
      let v = uil_find_edge_shared_vertex(ep[0], ep[1]);
      vs_b.push(v);
    });

    var iva1 = vs_a.indexOf(va1);
    var iva2 = vs_a.indexOf(va2);
    var ivb1 = vs_b.indexOf(vb1);
    var ivb2 = vs_b.indexOf(vb2);

    let a_incremental = false;
    let b_incremental = false;
    if (iva2 == iva1 + 1 || (iva1 == vs_a.length - 1 && iva2 == 0 ))
      a_incremental = true;
    if (ivb2 == ivb1 + 1 || (ivb1 == vs_b.length - 1 && ivb2 == 0 ))
      b_incremental = true;

    if ((a_incremental && b_incremental) || (!a_incremental && !b_incremental)) {
      v1id = va1;
      v2id = vb2;
    }
    else {
      v1id = va1;
      v2id = vb1;
    }

    // Also warn the user if we're not providing the expected geometric matching
    if ((v1_tmp_id == v1id && v2_tmp_id != v2id) || (v1_tmp_id != v1id && v2_tmp_id == v2id))
      display_message_to_user(LOG.WARN, "Not the result you expected? Stitched components might have twisted to face the same direction. Feel free to undo, and reposition or Flip.");
  }

  if (v1id == 0 && v2id == 0) {
    // Use the geometric match we have already caclulated above.
    v1id = v1_tmp_id;
    v2id = v2_tmp_id;
  }

  uid_deselect_all_objects();

  return uil_merge_bodies_on_edge(b1id, b2id, e1id, e2id, v1id, v2id);
}

export function edges_match_based_on_vertices(e1id, e2id) {
  // Checks whether two edges "match" by simply checking whether a pair of their vertices are closer than a certain epsilon
  // from each other. Lazy and fast, but good enough for some operations.
  let vs0 = uil_get_vertices_of_edge(e1id);
  let vs1 = uil_get_vertices_of_edge(e2id);
  let eps = 1e-04;
  let d00 = uid_vertices.getItem(vs0[0]).object3d.position.distanceTo(uid_vertices.getItem(vs1[0]).object3d.position);
  let d01 = uid_vertices.getItem(vs0[0]).object3d.position.distanceTo(uid_vertices.getItem(vs1[1]).object3d.position);
  let d10 = uid_vertices.getItem(vs0[1]).object3d.position.distanceTo(uid_vertices.getItem(vs1[0]).object3d.position);
  let d11 = uid_vertices.getItem(vs0[1]).object3d.position.distanceTo(uid_vertices.getItem(vs1[1]).object3d.position);
  return ((d00 < eps && d11 < eps) || (d01 < eps && d10 < eps))
}

export function prepare_project_edges_onto_faces() {

  if (App.state.projecting_edges) {
    abort_projecting_edges_onto_faces();
    return;
  }

  if (uid_selected_edges.length == 0 && uid_selected_faces.length == 0 && uid_selected_vertices.length == 0 ) {
    display_message_to_user(LOG.WARN, "No objects selected.");
    return;
  }

  App.state.projecting_edges = true;

  show_modal("projectEdgesOffset");
}

export async function complete_or_abort_projecting_edges_onto_faces() {
  if (App.state.projecting_edges == false)
    return;

  // Make note of currently not displayed objects
  let vertices_to_hide_again = [];
  let edges_to_hide_again = [];
  let faces_to_hide_again = [];
  uid_vertices.keys()
    .filter(x => uil_is_object_visible(x) && mustNotDisplayVertex(x))
    .forEach(x => {vertices_to_hide_again.push(x);});
  uid_edges.keys()
    .filter(x => uil_is_object_visible(x) && mustNotDisplayEdge(x))
    .forEach(x => {edges_to_hide_again.push(x);});
  uid_faces.keys()
    .filter(x => uil_is_object_visible(x) && mustNotDisplayFace(x))
    .forEach(x => {faces_to_hide_again.push(x);});

  // Get rid of clipping planes if there are any
  let clipping_was_on = false;
  if (clipping_model_editable.value) {
    // Exit modes
    toggle_clipping();
    clipping_was_on = true;
  }

  if (global_edge_projection_data.projection_type == 'face') {
    if (uid_selected_faces.length == 0)
      abort_projecting_edges_onto_faces();
    else
      await project_edges_onto_faces();
  }
  else if (global_edge_projection_data.projection_type == 'plane' || global_edge_projection_data.projection_type == 'edge') {
    if (global_edge_projection_data.direction.length() == 0)
      abort_projecting_edges_onto_faces();
    else
      await project_edges_onto_faces();
  }
  else if (global_edge_projection_data.projection_type == 'view') {
    global_edge_projection_data.direction = get_camera_look_vector(uid_camera).normalize();
    await project_edges_onto_faces();
  }

  App.state.projecting_edges = false;
  global_edge_projection_data.direction.set(0,0,0);

  // Make everything visible
  uid_deselect_all_objects();
  if (App.state.currently_hiding_edges)
    display_toggle_edge_visibility();
  if (App.state.currently_hiding_vertices)
    display_toggle_vertex_visibility();
  if (App.state.currently_hiding_faces)
    display_toggle_face_visibility();

  // Hide elements that were originally hidden
  if (faces_to_hide_again.length > 0) {
    faces_to_hide_again.forEach(function (x){
      uid_select_object(x);
    });
    display_toggle_face_visibility();
  }
  if (edges_to_hide_again.length > 0) {
    edges_to_hide_again.forEach(function (x){
      uid_select_object(x);
    });
    display_toggle_edge_visibility();
  }
  if (vertices_to_hide_again.length > 0) {
    vertices_to_hide_again.forEach(function (x){
      uid_select_object(x);
    });
    display_toggle_vertex_visibility();
  }
  uid_deselect_all_objects();

  // Bring back clipping if necessry
  if (clipping_was_on) {
    toggle_clipping_edit_model();
  }

  set_button_states();
}

export function abort_projecting_edges_onto_faces() {
    App.state.projecting_edges = false;
    set_button_states();
    hide_modal("projectEdgesOffset")
}

export function process_modal_for_project_edges_onto_faces() {
  global_edge_projection_data = new EdgeProjectionData();
  global_edge_projection_data.offset = get_unitless_value_from_display_unit(
        Number($("#project-edges-offset").val()) ?
        Number($("#project-edges-offset").val()) :
        0);
  global_edge_projection_data.projection_type = $("#project-edges-type").val();

  hide_modal("projectEdgesOffset")

  start_project_edges_onto_faces();
}

export async function start_project_edges_onto_faces() {

  uid_selected_vertices.keys().forEach(function(vid){
    push_nodups(vid, global_edge_projection_data.vertex_list);
  });

  uid_selected_edges.keys().forEach(function(eid){
    push_nodups(eid, global_edge_projection_data.edge_list);
  });

  uid_selected_faces.keys().forEach(function(fid){
    var es = uil_get_edges_of_face(fid);
    es.forEach(function(eid){
      push_nodups(eid, global_edge_projection_data.edge_list);
    });
  });

  // Remove from vertex_list any vertices that lie on edges of edge_list
  for (var i = global_edge_projection_data.vertex_list.length - 1 ; i >= 0 ; i-- ) {
    var vid = global_edge_projection_data.vertex_list[i];
    var es = uil_get_visible_edges_of_vertex(vid);
    var edge_found = false;
    es.forEach(function(eid){
      if (global_edge_projection_data.edge_list.indexOf(eid) != -1)
        global_edge_projection_data.vertex_list.splice(i,1);
    });
  }

  if (global_edge_projection_data.edge_list.length == 0 && global_edge_projection_data.vertex_list.length == 0) {
    display_message_to_user(LOG.WARN, "No edges, faces or vertices selected.");
    return;
  }

  App.state.projecting_edges = true;

  // Calculate orientation direction of selected objects
  global_edge_projection_data.object_orientation_direction = new THREE.Vector3();
  if (uid_selected_faces.length > 0) {
    // Use orientation of all faces.
    uid_selected_faces.keys().forEach(function(fid){
      var center = new THREE.Vector3();
      var wv_radius = new WrappedValue(0);
      var wv_flatness = new WrappedValue(0);
      var characteristic_dir_coords = new THREE.Vector3();
      var normal = new THREE.Vector3();
      uil_get_face_geometry(fid, center, normal, wv_radius, characteristic_dir_coords, wv_flatness);
      global_edge_projection_data.object_orientation_direction.add(normal);
    });
    global_edge_projection_data.object_orientation_direction.normalize();
  }
  else {
    // Use any faces that have more than one selected edge on them.
    var face_edge_count_hash = new HashTable();
    uid_selected_edges.keys().forEach(function(eid){
      var fs = uil_get_faces_of_edge(eid);
      if (face_edge_count_hash.hasItem(fs[0]))
        face_edge_count_hash.setItem(fs[0], face_edge_count_hash.getItem(fs[0]) + 1);
      else
        face_edge_count_hash.setItem(fs[0], 1);
      if (face_edge_count_hash.hasItem(fs[1]))
        face_edge_count_hash.setItem(fs[1], face_edge_count_hash.getItem(fs[1]) + 1);
      else
        face_edge_count_hash.setItem(fs[1], 1);
    });

    var faces_to_consider = [];
    face_edge_count_hash.keys().forEach(function(fid){
      if (face_edge_count_hash.getItem(fid) > 1)
        push_nodups(fid, faces_to_consider);
    });

    if (faces_to_consider.length == 0) {
      // Use view direction
      global_edge_projection_data.object_orientation_direction.copy(get_camera_look_vector(uid_camera).normalize());
      global_edge_projection_data.used_view_direction = true;
    }
    else {
      faces_to_consider.forEach(function(fid){
        var center = new THREE.Vector3();
        var wv_radius = new WrappedValue(0);
        var wv_flatness = new WrappedValue(0);
        var characteristic_dir_coords = new THREE.Vector3();
        var normal = new THREE.Vector3();
        uil_get_face_geometry(fid, center, normal, wv_radius, characteristic_dir_coords, wv_flatness);
        global_edge_projection_data.object_orientation_direction.add(normal);
      });
      global_edge_projection_data.object_orientation_direction.normalize();
    }
  }

  if (global_edge_projection_data.projection_type == "view") {
    // Do nothing
  }
  else if (global_edge_projection_data.projection_type == "plane") {
    if (!App.state.axes_visible)
      display_axes();
  }
  else if (global_edge_projection_data.projection_type == "face") {
    uid_deselect_all_objects();
  }
  else if (global_edge_projection_data.projection_type == "edge") {
    uid_deselect_all_objects();
  }
  update_dom_text_values_and_coordinates();
}

async function project_edges_onto_faces() {
  await next_frame();
  hide_axes();
  await next_frame();
  suppress_animate();
  suppress_undo_tracking();

  // If we're projection onto faces, we first need to find a projection direction based on the selected faces
  if (global_edge_projection_data.projection_type == 'face') {
    const center = new THREE.Vector3();
    const wv_radius = new WrappedValue(0);
    const wv_flatness = new WrappedValue(0);
    const characteristic_dir_coords = new THREE.Vector3();
    const normal = new THREE.Vector3();
    uid_selected_faces.keys().forEach(function(fid){
      const n = new THREE.Vector3();
      uil_get_face_geometry(fid, center, n, wv_radius, characteristic_dir_coords, wv_flatness);
      normal.add(n);
    });
    normal.multiplyScalar(1/uid_selected_faces.length).normalize();
    global_edge_projection_data.set_direction(normal);
  }

  // If we're projecting onto faces, hide all objects except the face
  if (global_edge_projection_data.projection_type == 'face') {

    uid_selected_faces.keys().forEach(function(fid){
      push_nodups(fid, global_edge_projection_data.projection_faces);
    });
    var faces_hidden_before_operation = [];

    // Hide all vertices and edges, and momentarily show all faces
    uid_deselect_all_objects();
    if (!App.state.currently_hiding_edges)
      display_toggle_edge_visibility();
    if (!App.state.currently_hiding_vertices)
      display_toggle_vertex_visibility();
    if (App.state.currently_hiding_faces)
      display_toggle_face_visibility();

    // Then hide faces as appropriate
    uid_faces.keys().forEach(function(fid) {
      if (uil_is_object_visible(fid) && mustNotDisplayFace(fid))
        push_nodups(fid, faces_hidden_before_operation);
      uid_select_object(fid);
    });
    global_edge_projection_data.projection_faces.forEach(function(fid){
      uid_deselect_object(fid);
    });
    display_toggle_face_visibility();
  }

  // Create a list of all the smooth edge pairs
  var smooth_edge_pairs = [];
  global_edge_projection_data.edge_list.forEach(function(eid){
    var es = get_neighbouring_edges_of_edge(eid);
    es.forEach(function(e1id){
      if (find_edge_shared_vertex(eid, e1id) > 0 && uil_is_joint_smooth(eid, e1id)) {
        var pair = (eid > e1id) ? [eid, e1id] : [e1id, eid];
        smooth_edge_pairs.push(pair);
      }
    });
  });

  await actually_project_edges_onto_faces();

  // If we're projecting onto faces, show again the faces we just hid
  if (global_edge_projection_data.projection_type == 'face') {
    uid_deselect_all_objects();
    uid_faces.keys().forEach(function(fid) {
      if (faces_hidden_before_operation.includes(fid))
        uid_select_object(fid);
    });
    display_toggle_face_visibility();
  }

  App.state.projecting_edges = false;

  // Switch back to objects raynode
  uid_raynode = uid_objects_raynode;

  // We also need to smoothen all edge pairs that may be almost smooth, within the projected edges
  var edge_pairs_to_smoothen = [];
  smooth_edge_pairs.forEach(function(edge_pair){
    const e1id = edge_pair[0];
    const e2id = edge_pair[1];
    if (!uil_is_joint_smooth(e1id, e2id)) {
      var vid = uil_find_edge_shared_vertex(e1id, e2id);
      if (vid) {
        var v1aid = uil_get_vertices_of_edge(e1id)[0];
        var v1bid = uil_get_vertices_of_edge(e1id)[1];
        var t1s = uil_get_edge_end_tangents(e1id);
        var t1;
        if (vid == v1aid)
          t1 = t1s[0];
        else
          t1 = t1s[1];
        var v2aid = uil_get_vertices_of_edge(e2id)[0];
        var v2bid = uil_get_vertices_of_edge(e2id)[1];
        var t2s = uil_get_edge_end_tangents(e2id);
        var t2;
        if (vid == v2aid)
          t2 = t2s[0];
        else
          t2 = t2s[1];

        t1.normalize();
        t2.normalize();
        var a = vector3.angle(t1, t2);
        a = Math.min(a, Math.PI-a);
        var angle_limit = 45.0 * Math.PI/180.0; // 5 degrees
        if ((a < angle_limit && a > (-1) * angle_limit) ||
            (a > Math.PI - angle_limit && a < Math.PI + angle_limit))
          edge_pairs_to_smoothen.push([e1id, e2id]);
      }
    }
  });

  if (edge_pairs_to_smoothen.length > 0) {
    // First we introduce an undo step
    unsuppress_undo_tracking_if_ready(false);
    suppress_undo_tracking();
  }
  let vertices_to_unfix = [];
  edge_pairs_to_smoothen.forEach(function(edge_pair) {
    if (!uil_is_joint_smooth(edge_pair[0], edge_pair[1])) {
      let vid = find_edge_shared_vertex(edge_pair[0], edge_pair[1]);
      if (vid > 0) {
        if (!uil_is_object_fixed(vid)) {
          uil_fix_objects([vid]);
          vertices_to_unfix.push(vid);
        }
        uil_add_joint_smoothness([edge_pair], FAINT_STRENGTH, true, true);
      }
    }
  });
  uil_unfix_objects(vertices_to_unfix);
  if (edge_pairs_to_smoothen.length > 0)
    display_message_to_user(LOG.INFO, " " + edge_pairs_to_smoothen.length + " edge joints were smoothened. You can undo this smoothening.");

  unsuppress_undo_tracking_if_ready(false);
  unsuppress_animate_if_ready();
  set_button_states();
}

export async function actually_project_edges_onto_faces() {

  var vertex_projections = [];
  var edge_projections = [];
  var rejected_edges = [];
  var rejected_vertices = [];

  // Then project all vertices. The ones in vertex_list and the ones on edges of edge_list.
  var vertex_ids = [];
  global_edge_projection_data.vertex_list.forEach(function(vid){
    push_nodups(vid, vertex_ids);
  });
  global_edge_projection_data.edge_list.forEach(function(eid){
    push_nodups(uil_get_vertices_of_edge(eid)[0], vertex_ids);
    push_nodups(uil_get_vertices_of_edge(eid)[1], vertex_ids);
  });

  var bb = uid_calculate_global_bounding_box(true, true, false);

  // Create a variable that will carry the projection direction, but will also allow it to be flipped and stay flipped in the process.
  var prevailing_projection_direction = new WrappedValue(global_edge_projection_data.direction);

  await withProgress("[1/3] Calculating vertex placements", (vid) => {
    var proj = calculate_projection_of_vertex_onto_faces(vid, global_edge_projection_data.offset, prevailing_projection_direction,
      global_edge_projection_data.projection_type, bb);
    if (proj)
      vertex_projections.push(proj);
    else
      rejected_vertices.push(vid);
  })(vertex_ids);


  // Then project all edges

  await withProgress("[2/3] Calculating edge shapes placements", (eid) => {
    var proj = calculate_projection_of_edge_onto_faces(eid, global_edge_projection_data.offset, prevailing_projection_direction,
      global_edge_projection_data.projection_type, bb);
    if (proj)
      edge_projections.push(proj);
    else
      rejected_edges.push(eid);
  })(global_edge_projection_data.edge_list);



  if (edge_projections.length == 0 && vertex_projections.length == 0 && global_edge_projection_data.projection_type != "face") {
    if (!global_edge_projection_data.flipped_view_direction) {
      // Reverse direction and try new attempt
      global_edge_projection_data.direction.multiplyScalar(-1);
      global_edge_projection_data.flipped_view_direction = true;
      await actually_project_edges_onto_faces();
    }
    else {
      display_message_to_user(LOG.WARN, "Projected objects didn't hit any faces, even after flipping the view direction.");
    }
  }
  else {
    var at_least_one_projection_happened = false;
    vertex_projections.forEach(function(projection){
      // Only keep vertex projections that are on edges that will be projected, or that were
      // independently added in vertex_list.
      var vertex_found = false;
      if (global_edge_projection_data.vertex_list.indexOf(projection.vertex_id) != -1)
        vertex_found = true;
      else {
        edge_projections.forEach(function(ep){
          if ((uil_get_vertices_of_edge(ep.edge_id)[0] == projection.vertex_id) ||
              (uil_get_vertices_of_edge(ep.edge_id)[1] == projection.vertex_id))
            vertex_found = true;
        });
      }
      if (vertex_found) {
        uil_move_vertex_to_coords(projection.vertex_id,
              projection.coordinates.x, projection.coordinates.y, projection.coordinates.z);
        at_least_one_projection_happened = true;
        }
    });

    await withProgress("[3/3] Projecting edges", (projection) => {
      deform_edge_to_match_samples(uid_edges.getItem(projection.edge_id), projection.samples, false, false);
      uil_smoothen_edges([projection.edge_id], FAINT_STRENGTH);
      at_least_one_projection_happened = true;
    })(edge_projections);


    // Make linear any unprojected edges that had at least one projected vertex
    var edges_to_make_linear = [];
    rejected_edges.forEach(function(eid){
      var vs = uil_get_vertices_of_edge(eid);
      if ( (rejected_vertices.indexOf(vs[0]) == -1 && rejected_vertices.indexOf(vs[1]) != -1  ) ||
            (rejected_vertices.indexOf(vs[0]) != -1 && rejected_vertices.indexOf(vs[1]) == -1  ) )
        push_nodups(eid, edges_to_make_linear);
    });
    uil_set_linearity_of_edges(edges_to_make_linear, true, true);

    if (global_edge_projection_data.used_view_direction &&
        global_edge_projection_data.projection_type != "view" &&
        global_edge_projection_data.projection_type != "face")
      display_message_to_user(LOG.INFO, "We used your current camera viewpoint as a hint for the direction you want " +
                              "to project the edges along. If you wanted to project along the opposite direction, " +
                              "please rotate your camera viewpoint and try again.");
    if (global_edge_projection_data.flipped_view_direction)
      display_message_to_user(LOG.INFO, "The view direction was flipped in order to hit some faces.");
    if (!at_least_one_projection_happened)
      display_message_to_user(LOG.WARN, "We were not able to project the selected objects onto any surface. Perhaps try again with a different direction.");
  }
}

export function calculate_projection_of_vertex_onto_faces(vid, offset, wrapped_dir, type, bb) {
  var proj;
  if (type == "face") {
    proj = calculate_projection_of_vertex_onto_faces_with_final_dir(vid, offset, wrapped_dir.v, bb, false);
    if (proj == null) {
      wrapped_dir.v.multiplyScalar(-1);
      proj = calculate_projection_of_vertex_onto_faces_with_final_dir(vid, offset, wrapped_dir.v, bb, false);
    }
  }
  else {
    proj = calculate_projection_of_vertex_onto_faces_with_final_dir(vid, offset, wrapped_dir.v, bb, true);
  }
  return proj;
}

export function calculate_projection_of_vertex_onto_faces_with_final_dir(vid, offset, dir, bb, shift_point) {

  if (uil_is_object_fixed(vid)) {
    display_message_to_user(LOG.WARN, "Cannot proceed to due fixed objects.");
    return;
  }

  // Trace edge and project each sample along the camera view
  var p = new THREE.Vector3();
  var p_new = new THREE.Vector3();
  p.copy(uid_vertices.getItem(vid).object3d.position);
  p_new.copy(p);

  var dir_off = new THREE.Vector3();
  dir_off.copy(dir);
  dir_off.multiplyScalar(offset);

  // If we're not doing face projection, slightly shift the vertex to avoid the raycasting hitting its own faces
  if (shift_point)
    p.addScaledVector(dir, bb.dimension/300.0);
  uid_raycaster.set(p, dir);

  uid_raynode = uid_geometries_raynode;
  var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
  var intersection = find_intersection_face_object(intersects);
  var found_target = false;
  uid_raynode = uid_objects_raynode;

  if (intersection) {
    found_target = true;
    p_new.copy(intersection.point)
    p_new.add(dir_off);
  }

  var projection = null;
  if (found_target)
    projection = {"vertex_id": vid, "coordinates": p_new};
  return projection;
}

export function calculate_projection_of_edge_onto_faces(eid, offset, wrapped_dir, type, bb) {

  if (uil_is_object_fixed(eid)) {
    display_message_to_user(LOG.WARN, "Cannot proceed to due fixed objects.");
    return;
  }

  // Trace edge and project each sample along the camera view
  var samples = [];
  var new_samples = [];
  uid_edges.getItem(eid).object3d.geometry.vertices.forEach(function(v){
    var p = new THREE.Vector3();
    p.copy(v);
    samples.push(p)
    new_samples.push(p);
  });

  var edge_succeeded = false;

  uid_raynode = uid_geometries_raynode;

  for (var i = 0; i < samples.length; i++) {
    var p = samples[i];
    var s = calculate_projection_of_sample_onto_faces(p, offset, wrapped_dir, type, bb);
    if (s) {
      new_samples[i] = s;
      edge_succeeded = true;
    }
    else {
      new_samples[i] = p;
    }
  }

  uid_raynode = uid_objects_raynode;

  var projection = null;
  if (edge_succeeded)
    projection = {"edge_id": eid, "samples": new_samples};
  return projection;
}

export function calculate_projection_of_sample_onto_faces(p, offset, wrapped_dir, type, bb) {
  var proj;
  if (type == "face") {
    proj = calculate_projection_of_sample_onto_faces_with_final_dir(p, offset, wrapped_dir.v, bb, false);
    if (proj == null) {
      wrapped_dir.v.multiplyScalar(-1);
      proj = calculate_projection_of_sample_onto_faces_with_final_dir(p, offset, wrapped_dir.v, bb, false);
    }
  }
  else {
    proj = calculate_projection_of_sample_onto_faces_with_final_dir(p, offset, wrapped_dir.v, bb, true);
  }
  return proj;
}

export function calculate_projection_of_sample_onto_faces_with_final_dir(p, offset, dir, bb, shift_point) {
  var dir_off = new THREE.Vector3();
  dir_off.copy(dir);
  dir_off.multiplyScalar(offset);
  if (shift_point)
    p.addScaledVector(dir, bb.dimension/300.0);
  uid_raycaster.set(p, dir);
  var intersects = raycaster_intersect_objects(uid_raycaster, uid_raynode.children);
  var intersection = find_intersection_face_object(intersects);
  var found_target = false;
  var p_new = null;
  if (intersection) {
    found_target = true;
    p_new = new THREE.Vector3();
    p_new.copy(intersection.point)
    p_new.add(dir_off);
  }
  return p_new;
}

export function flip_body_faces_inside_out() {
  if (uid_state.rigid_move_manipulator_is_shown) {
    uid_hide_rigid_move_manipulator(false, 0, false);
  }

  if (uid_selected_faces.length > 0) {
    var bodies_to_flip = [];
    uid_selected_faces.keys().forEach(function(fid){
      push_nodups(uil_get_body_of_face(fid), bodies_to_flip)
    });
    bodies_to_flip.forEach(function(bid){
      uil_flip_body_inside_out(bid);
    });
    uid_deselect_all_objects();
    uid_unhighlight_all_objects();
  }
  else
    display_message_to_user(LOG.WARN, "No bodies selected");
}

// ------------ Callbacks for updating the display data structure

export function display_add_vertex(id, x, y, z) {
  var uidv = new Uid_vertex(id, color_r_vertex, color_g_vertex, color_b_vertex);
  uid_vertices.setItem(uidv.id, uidv);
  uid_add_vertex_geometry(uidv, x, y, z, false, false, "");
  animate();
}

export function display_add_edge(id, points) {
  var uide = new Uid_edge(id, color_r_edge, color_g_edge, color_b_edge);
  uid_edges.setItem(uide.id, uide);
  uide.set_linear_flag(uil_is_edge_linear(id));
  uid_add_edge_geometry(uide, points, false, "");
  animate();
}

export function display_add_face(id, points, triangles, colorhex, material_name) {
  if (uid_face_colors.hasItem(id)) {
    var colorhex = uid_face_colors.getItem(id);
    var uidf = new Uid_face(id, colorhex);
  }
  else if (colorhex) {
    var uidf = new Uid_face(id, colorhex);
    if (App.state.dragging_type == "none") {
      suppress_undo_tracking()
      uil_set_face_color([id], colorhex);
      unsuppress_undo_tracking_if_ready(true);
    }
  }
  else if (uil_get_face_color(id) != null) {
    var file_colorhex = uil_get_face_color(id);
    var uidf = new Uid_face(id, file_colorhex);
  }
  else {
    var uidf = new Uid_face(id, rgbToHex(color_r_face, color_g_face, color_b_face));
  }

  if (uid_face_materials.hasItem(id))
    uidf.material_name = uid_face_materials.getItem(id);
  else if (material_name) {
    uidf.material_name = material_name;
    if (App.state.dragging_type == "none") {
      suppress_undo_tracking();
      uil_set_face_material([id], material_name);
      unsuppress_undo_tracking_if_ready(true);
    }
  }

  uid_faces.setItem(uidf.id, uidf);
  uid_add_face_geometry(uidf, points, triangles, false, face_is_for_sketch(id), "");
  /*
  // To add glossy look to all objects:
  uid_add_face_gloss_geometry(uidf, points, triangles);
  */
  free_array(points);
  free_array(triangles);

  // Check if this face is a sketch face
  if (App.state.imported_sketches.length > 0 && uil_is_object_visible(id) && face_is_for_sketch(id))
    must_redraw_sketches = true;

  animate();
}

export function display_modify_vertex(id, x, y, z) {
  if (isVertex(id)) {
    var v = uid_vertices.getItem(id);
    v.object3d.position.set(x, y, z);
    v.object3d.geometry.verticesNeedUpdate = true;
    v.object3d.geometry.normalsNeedUpdate = true;
    //v.object3d.geometry.computeFaceNormals();
    //v.object3d.geometry.computeVertexNormals();
    v.object3d.geometry.computeBoundingSphere();
  }
  else {
    display_message_to_user(LOG.WARN, "Error: Callback trying to modify vertex with id " + id + " which doesn't exist!" );
    console.trace();
  }
}

export function display_modify_edge(id, newpoints) {
  if (isEdge(id)) {
    var e = uid_edges.getItem(id);

    // Check if the linearity has changed
    var linearity_has_changed = false;
    var new_linearity_flag = uil_is_edge_linear(id);
    if ((e.get_linearity_flag() && !new_linearity_flag) || (!e.get_linearity_flag() && new_linearity_flag)) {
      e.set_linear_flag(new_linearity_flag);
      linearity_has_changed = true;
    }

    // If the incoming array of points has the same number of vertices as the current
    // edge geometry then replace coordinates. Otherwise, regenerate array with new points.
    // Do the same for both the interactive (invisible) edge, and the visible one.

    //if (false) {
    if (e.object3d.geometry.vertices.length == newpoints.length && !linearity_has_changed) {
      // Modifying existing edge

      // For the interactive edge:
      for (var i = 0; i < newpoints.length; i++) {
        e.object3d.geometry.vertices[i].set(newpoints[i].x, newpoints[i].y, newpoints[i].z);
      }
      e.object3d.geometry.verticesNeedUpdate = true;
      e.object3d.geometry.normalsNeedUpdate = true;
      e.object3d.geometry.computeBoundingSphere();

      // For the visible edge:

      // Set default edge color
      set_default_edge_color(id);

      var visible_edge_mesh = e.get_visible_edge_mesh();

      var positions = [];
      e.object3d.geometry.vertices.forEach(function(v){
        positions.push(v.x);
        positions.push(v.y);
        positions.push(v.z);
      });
      visible_edge_mesh.geometry.setPositions( positions );
      visible_edge_mesh.computeLineDistances();

      visible_edge_mesh.geometry.attributes.position.needsUpdate = true;
      visible_edge_mesh.geometry.normalsNeedUpdate = true;
      visible_edge_mesh.geometry.computeBoundingSphere();

    }
    else {
      // Removing and adding back edge
      uid_object3dIDToUid_object.removeItem(e.o3d_id);
      uid_objects_raynode.remove(e.object3d); // XXX
      e.remove_visible_edge_mesh(false);
      uid_add_edge_geometry(e, newpoints, false, "");
    }
  }
  else {
    display_message_to_user(LOG.WARN, "Error: Callback trying to modify edge with id " + id + " which doesn't exist!" );
    console.trace();
  }

  animate();
}

export function display_modify_face(id, newpoints) {
  if (isFace(id)) {
    var f = uid_faces.getItem(id);
    var v = f.object3d.geometry.attributes.position.array;

    var n = new THREE.Vector3(0,0,0);
    if (zero_thickness_faces_hash.hasItem(id))
        n.copy(zero_thickness_faces_hash.getItem(id));

    // newpoints is a flat array of coordinates
    for (var i = 0; i < newpoints.length / 3.0; i++) {
      //v[i].set(newpoints[i * 3], newpoints[i * 3 + 1], newpoints[i * 3 + 2]);
      f.object3d.geometry.attributes.position.array[ i * 3 ]  = newpoints[i * 3] + n.x;
      f.object3d.geometry.attributes.position.array[ i * 3 + 1 ]  = newpoints[i * 3 + 1] + n.y;
      f.object3d.geometry.attributes.position.array[ i * 3 + 2 ]  = newpoints[i * 3 + 2] + n.z;
    }

    // And once more for the mirror
    if (should_display_mirror_side()) {
      var offset = newpoints.length / 3.0;
      for (var i = 0; i < newpoints.length / 3.0; i++) {
        var mv = new THREE.Vector3(newpoints[i * 3], newpoints[i * 3 + 1], newpoints[i * 3 + 2]);
        var mirroredv = mirror_point(mv, mirror_plane_base, mirror_plane_normal);
        f.object3d.geometry.attributes.position.array[ (i + offset) * 3 ]  = mirroredv.x + n.x;
        f.object3d.geometry.attributes.position.array[ (i + offset) * 3 + 1 ]  = mirroredv.y + n.y;
        f.object3d.geometry.attributes.position.array[ (i + offset) * 3 + 2 ]  = mirroredv.z + n.z;
      }
    }

    f.object3d.geometry.attributes.position.needsUpdate = true;

    // Check if this face is a sketch face
    if (App.state.imported_sketches.length > 0 && uil_is_object_visible(id) && face_is_for_sketch(id))
      must_redraw_sketches = true;
  }
  else {
    display_message_to_user(LOG.WARN, "Error: Callback trying to modify face with id " + id + " which doesn't exist!" );
    console.trace();
  }
}

export function redisplay_face(id, also_recreate_material = false) {
  var f = uid_faces.getItem(id);

  var wv_ncoords = new WrappedValue();
  var wv_ntriangles = new WrappedValue();
  uil_get_face_triangulation_info(id, wv_ncoords, wv_ntriangles);

  var coords = new Array();
  var triangles = new Array();
  uil_get_face_triangulation(id, wv_ncoords.v, wv_ntriangles.v, coords, triangles);

  if (also_recreate_material) {
    if (isFace(id)) {
      f.object3d.material = create_materials_for_face(f, false);
      set_default_face_color(id);
      f.object3d.geometry.colorsneedsUpdate = true;
    }
    else if (uid_reference_faces.hasItem(id)) {
      var ms = create_materials_for_face(f, true);
      f.uid_object.object3d.material = ms[0];
    }

  }
  display_modify_face(id, coords);
}

export function display_vertex_distance_modal() {
  var v1, v2;
  if (uid_selected_vertices.length == 2) {
    v1 = uid_selected_vertices.getItemAtIndex(0);
    v2 = uid_selected_vertices.getItemAtIndex(1);
  }
  else if (uid_selected_edges.length == 1) {
    var eid = uid_selected_edges.getKeyAtIndex(0);
    v1 = uid_vertices.getItem(uil_get_vertices_of_edge(eid)[0]);
    v2 = uid_vertices.getItem(uil_get_vertices_of_edge(eid)[1]);
  }
  else
    return;
  uid_deselect_all_objects();
  uid_select_object(v1.id);
  uid_select_object(v2.id);
  var d = vector3.point_point_distance(v1.object3d.position, v2.object3d.position )
  d = get_display_unit_from_unitless_value(d);
  $("#vertexDistValue").val(d.toFixed(App.settings.coordinate_decimals));
  var s = "New distance (" + App.state.display_units + "):";
  $("#vertexDistanceModalValueTitle").html(s);
  $("#vertexDistEntireBodyRadio").prop("checked", true);
  $("#vertexDistProportionalRadio").prop("checked", false);
  $("#vertexDistFixFirstVertexRadio").prop("checked", false);

  show_modal("vertexDistanceModal");
}

export function vertex_distance_set_entire_body_status() {
  if ($("#vertexDistProportionalRadio").is(':checked'))
    $("#vertexDistEntireBodyRadio").prop("checked", true);
}

export function vertex_distance_dont_scale_proportionately() {

  if (!$("#vertexDistEntireBodyRadio").is(':checked'))
    $("#vertexDistProportionalRadio").prop("checked", false);
}

export function vertex_distance_lock_first_vertex() {

  if (!$("#vertexDistFixFirstVertexRadio").is(':checked'))
    $("#vertexDistFixFirstVertexRadio").prop("checked", false);
}

export function display_set_vertex_distance() {
  if (uid_selected_vertices.length != 2)
    return;

  var v1 = uid_selected_vertices.getItemAtIndex(0);
  var v2 = uid_selected_vertices.getItemAtIndex(1);
  var bid;
  bid = uil_get_body_of_vertex(v1.id);
  if (uil_get_body_of_vertex(v2.id) != bid) {
    display_message_to_user("Vertices do not belong to the same body. Please try again.");
    return;
  }
  var edges_to_scale = [];

  // First check if the vertexDistEntireBodyRadio is set, to decide whether to do a regular scale or a vertex_distance, and select
  // the corresponding objects.
  var do_vertex_distance = $("#vertexDistEntireBodyRadio").is(':checked') ? false : true;
  if (!do_vertex_distance) {
    uid_edges.keys().forEach(function(eid) {
      if (uil_get_body_of_edge(eid) == bid)
        edges_to_scale.push(eid);
    });
  }

  // Check if we should scale proportionately
  var do_proportional = $("#vertexDistProportionalRadio").is(':checked');

  // Check if we should fix the first vertex
  var fix_first_vertex = $("#vertexDistFixFirstVertexRadio").is(':checked');

  // Then we calculate the scale factor
  var factor;
  var p1 = uid_selected_vertices.getItemAtIndex(0).object3d.position;
  var p2 = uid_selected_vertices.getItemAtIndex(1).object3d.position;

  var dbefore = vector3.point_point_distance(p1, p2)
  let du = Number($("#vertexDistValue").val());
  var dafter = get_unitless_value_from_display_unit(du);

  factor = dafter/dbefore;
  if (factor > 200 || factor < 1/200) {
    display_message_to_user(LOG.WARN, "The requested change is too big. Please try again.");
    return;
  }

  // Then calculate a scale axis
  var axis = null;
  if (!do_proportional) {
    axis = new THREE.Vector3();
    axis.subVectors(p1,p2).normalize();
  }

  if (do_vertex_distance) {
    var v1 = uid_selected_vertices.getKeyAtIndex(0);
    var v2 = uid_selected_vertices.getKeyAtIndex(1);
    move_pair_of_vertices_for_scaling(v1, v2, factor, "d", fix_first_vertex);
  }
  else {
    suppress_animate();
    suppress_ui_messages();
    edges_to_scale.forEach(function(eid){
      uid_select_object(eid);
    });
    uid_start_scale_objects(fix_first_vertex);
    uid_scale_objects(factor, axis, fix_first_vertex);
    uid_end_scale_objects();
    edges_to_scale.forEach(function(eid){
      uid_deselect_object(eid);
    });
    uid_select_object(v1.id);
    uid_select_object(v2.id);
    unsuppress_ui_messages_if_ready();
    unsuppress_animate_if_ready();
  }
  hide_modal("vertexDistanceModal");
}

export function closeSTLImportModal() {
  hide_modal('ImportRefStlModelModal');
}

export function display_setVertexXYZ_modal() {
  var cannot_proceed_due_to_fixed_objects = false;
  uid_selected_vertices.keys().forEach(function(vid){
    if (uil_is_object_fixed(vid))
      cannot_proceed_due_to_fixed_objects = true;
  });

  if (cannot_proceed_due_to_fixed_objects) {
    display_message_to_user(LOG.WARN, "Cannot change coordinates of a fixed vertex.");
    return;
  }

  show_modal("setVertexXYZModal");
}

export function display_booleans_modal() {
  let model_bodies = uil_get_model_bodies();
  model_bodies.forEach(function(bid){

  });
  $('#modelProportionally').onchange = rescale_proportionately_changed;
  show_modal("booleanOpsModal");
}

export function display_read_coords_modal(vid) {
    const vx = get_display_unit_from_unitless_value(uid_vertices.getItem(vid).object3d.position.x);
    const vy = get_display_unit_from_unitless_value(uid_vertices.getItem(vid).object3d.position.y);
    const vz = get_display_unit_from_unitless_value(uid_vertices.getItem(vid).object3d.position.z);

    // Show modal near mouse
    const { x, y } = from_normalized_to_actual_screen_coordinates(pointer_normalised);
    const left = Math.min(x, window.innerWidth - 250);
    const top = Math.min(y, window.innerHeight - 340);

    show_read_coords(left, top, vx, vy, vz, vid);
}

export function processReadCoordsModal(x, y, z, vid) {
  const vec = new THREE.Vector3(
    get_unitless_value_from_display_unit(x),
    get_unitless_value_from_display_unit(y),
    get_unitless_value_from_display_unit(z),
  );
  prepare_for_move_objects_to_location(vid, vec, true);
}

export async function processReadValueModal(val, type) {
  // Allow manipulator to be removed within scene
  uid_state.rigid_move_manipulator_shown_after_copy_body = false;

  switch (type) {
    case "ringx" :
      // Rotate around X axis by "val" degrees
      if (!App.state.editing_edge_tangent_mode) {
        var normal_along_view_direction = new THREE.Vector3();
        normal_along_view_direction.copy(uid_rigid_move_manipulator.local_x_direction);
        uid_rigid_move_manipulator.set_has_operated();
        await uid_rotate_edge_list_around_axis(uid_rigid_move_manipulator.rigid_move_edge_object_ids,
            uid_rigid_move_manipulator.center, normal_along_view_direction, val * Math.PI/180.0);
      }
      else {
        if (uid_selected_edges.length == 0 || uid_selected_edge_click_point <= 0)
          return;
        const eid = uid_selected_edges.firstKey();
        const vid = find_edge_closest_vertex_to_coordinates(eid, uid_selected_edge_click_point);
        const d = new THREE.Vector3().copy(uid_rigid_move_manipulator.local_x_direction);
        // Find current edge tangent
        const t = uil_get_edge_tangent_at_vertex(eid, vid);
        // Apply angle
        const t_new = vector3.rotate_vector_around_axis(t, d, val * Math.PI/180.0);
        uid_rigid_move_manipulator.set_has_operated();
        uil_set_edge_tangent_at_vertex(eid, vid, t_new);
      }
    break;
    case "ringy" :
      // Rotate around Y axis by "val" degrees
      if (!App.state.editing_edge_tangent_mode) {
        var normal_along_view_direction = new THREE.Vector3();
        normal_along_view_direction.copy(uid_rigid_move_manipulator.local_y_direction);
        uid_rigid_move_manipulator.set_has_operated();
        await uid_rotate_edge_list_around_axis(uid_rigid_move_manipulator.rigid_move_edge_object_ids,
            uid_rigid_move_manipulator.center, normal_along_view_direction, val * Math.PI/180.0);
      }
      else {
        if (uid_selected_edges.length == 0 || uid_selected_edge_click_point <= 0)
          return;
        const eid = uid_selected_edges.firstKey();
        const vid = find_edge_closest_vertex_to_coordinates(eid, uid_selected_edge_click_point);
        const d = new THREE.Vector3().copy(uid_rigid_move_manipulator.local_y_direction);
        // Find current edge tangent
        const t = uil_get_edge_tangent_at_vertex(eid, vid);
        // Apply angle
        uid_rigid_move_manipulator.set_has_operated();
        const t_new = vector3.rotate_vector_around_axis(t, d, val * Math.PI/180.0);
        uil_set_edge_tangent_at_vertex(eid, vid, t_new);
      }
    break;
    case "ringz" :
      // Rotate around Z axis by "val" degrees
      if (!App.state.editing_edge_tangent_mode) {
        var normal_along_view_direction = new THREE.Vector3();
        normal_along_view_direction.copy(uid_rigid_move_manipulator.local_z_direction);
        uid_rigid_move_manipulator.set_has_operated();
        await uid_rotate_edge_list_around_axis(uid_rigid_move_manipulator.rigid_move_edge_object_ids,
            uid_rigid_move_manipulator.center, normal_along_view_direction, val * Math.PI/180.0);
      }
      else {
        if (uid_selected_edges.length == 0 || uid_selected_edge_click_point <= 0)
          return;
        const eid = uid_selected_edges.firstKey();
        const vid = find_edge_closest_vertex_to_coordinates(eid, uid_selected_edge_click_point);
        const d = new THREE.Vector3().copy(uid_rigid_move_manipulator.local_z_direction);
        // Find current edge tangent
        const t = uil_get_edge_tangent_at_vertex(eid, vid);
        // Apply angle
        const t_new = vector3.rotate_vector_around_axis(t, d, val * Math.PI/180.0);
        uid_rigid_move_manipulator.set_has_operated();
        uil_set_edge_tangent_at_vertex(eid, vid, t_new);
      }
    break;
    case "rodx" :
    case "rody" :
    case "rodz" :
    case "tube" :
      val = get_unitless_value_from_display_unit(val);
      var local_move_direction = new THREE.Vector3();
      var perpendicular_direction = new THREE.Vector3();
      if (type == "rodx") {
        local_move_direction.copy(uid_rigid_move_manipulator.local_x_direction);
        perpendicular_direction.copy(uid_rigid_move_manipulator.local_y_direction);

        // Remove from val the amount in pop_amount_value, which is the amount we already moved from a pop.
        // If no pop has happened just now, the value will be 0 anyway.
        val -= pop_amount_value;

      }
      else if (type == "rody") {
        local_move_direction.copy(uid_rigid_move_manipulator.local_y_direction);
        perpendicular_direction.copy(uid_rigid_move_manipulator.local_z_direction);
      }
      else if (type == "rodz") {
        local_move_direction.copy(uid_rigid_move_manipulator.local_z_direction);
        perpendicular_direction.copy(uid_rigid_move_manipulator.local_x_direction);
      }
      else if (type == "coorddecimals") {
        var coordinate_decimals = Math.round(val);
      }
      else {
        // Fence tube
      }

      var initial_point = new THREE.Vector3();
      var target_point = new THREE.Vector3();

      // Two different types of movement depending on whether this is an edge list or just one vertex
      if (!uid_rigid_move_manipulator.rigid_move_face_object_id && uid_rigid_move_manipulator.rigid_move_edge_object_ids.length == 0) {
        // Moving just a vertex

        initial_point.copy(uid_vertices.getItem(uid_rigid_move_manipulator.indicated_center_object_id).object3d.position);
        target_point.copy(initial_point);
        target_point.addScaledVector(local_move_direction, val);

        uid_additional_drag_vertices = [];
        uid_additional_drag_vertices.push(uid_rigid_move_manipulator.indicated_center_object_id);
        uid_selected_vertices.keys().forEach(function(vid){
          if (vid != uid_rigid_move_manipulator.indicated_center_object_id) {
            uid_additional_drag_vertices.push(vid);
          }
        });

        uid_rigid_move_manipulator.set_has_operated();
        await uid_move_vertices(uid_additional_drag_vertices, target_point, perpendicular_direction);
      }
      else {
        if (uid_rigid_move_manipulator.is_for_fence && uid_selected_manipulator_type == "rigid_move_manip_fence_tube") {
          // Do nothing for now XXX
        }
        else {
          // Moving an edge list

          initial_point.copy(uid_rigid_move_manipulator.center);
          target_point.copy(initial_point);
          target_point.addScaledVector(local_move_direction, val);

          uid_rigid_move_manipulator.set_has_operated();
          await uid_translate_edge_list(uid_rigid_move_manipulator.rigid_move_edge_object_ids, initial_point, perpendicular_direction, target_point, perpendicular_direction);
        }
      }
      // Reset the pop value as it's no longer applicable
      pop_amount_value = 0;

    break;
  }

  // Reset manipulator colors
  uid_rigid_move_manipulator.reset_colors();
  uid_rigid_move_manipulator.attach_to_objects(uid_rigid_move_manipulator.use_local_coordinate_system, 0.5);
  animate();
}

export function display_setModelScale_modal() {
  $("#display_units_select").val(App.state.display_units);
  show_modal("setModelScaleModal");
}

export function display_edge_freedom_modal() {
  suppress_undo_tracking();
  let slider_data = $("#edgefreedom_range").data("ionRangeSlider");
  var max_freedom = 0;
  uid_selected_edges.keys().forEach(function(eid){
    var f = uil_get_edge_freedom(eid);
    if (f > max_freedom)
      max_freedom = f;
  })

  slider_data.update({from: max_freedom});
  show_modal("setEdgeFreedomModal");
}

export function display_set_edge_freedoms(f) {
  var edge_ids = [];
  uid_selected_edges.keys().forEach(function(eid){
    edge_ids.push(eid);
  });
  uil_set_edge_freedoms(edge_ids, f);
}

export function display_end_set_edge_freedoms() {
  unsuppress_undo_tracking_if_ready(false);
  uid_deselect_all_objects();
  hide_modal("setEdgeFreedomModal");
}

export function display_show_set_ballsize_modal() {
  show_modal("setBallsizeModal");
}

export function display_show_set_gridsize_modal() {
  if (!App.state.grid_visible)
    toggle_grid();

  if (App.state.display_units == "cm") {
    $("#gridsizebtnA").html("0.01");
    $("#gridsizebtnB").html("0.05");
    $("#gridsizebtnC").html("0.1");
    $("#gridsizebtnD").html("0.5");
    $("#gridsizebtnE").html("1");

    if (uid_grid_step == 0.01)
      setgridbuttonactive("A");
    else if (uid_grid_step == 0.05)
      setgridbuttonactive("B");
    else if (uid_grid_step == 0.1)
      setgridbuttonactive("C");
    else if (uid_grid_step == 0.5)
      setgridbuttonactive("D");
    else if (uid_grid_step == 0.1)
      setgridbuttonactive("E");
    else
      setgridbuttonactive("");
  }
  else if (App.state.display_units == "m") {
    $("#gridsizebtnA").html("0.01");
    $("#gridsizebtnB").html("0.05");
    $("#gridsizebtnC").html("0.1");
    $("#gridsizebtnD").html("0.5");
    $("#gridsizebtnE").html("1");

    if (uid_grid_step == 0.01)
      setgridbuttonactive("A");
    else if (uid_grid_step == 0.05)
      setgridbuttonactive("B");
    else if (uid_grid_step == 0.1)
      setgridbuttonactive("C");
    else if (uid_grid_step == 0.5)
      setgridbuttonactive("D");
    else if (uid_grid_step == 0.1)
      setgridbuttonactive("E");
    else
      setgridbuttonactive("");
  }
  else if (App.state.display_units == "mm") {
    $("#gridsizebtnA").html("0.01");
    $("#gridsizebtnB").html("0.05");
    $("#gridsizebtnC").html("0.1");
    $("#gridsizebtnD").html("0.5");
    $("#gridsizebtnE").html("1");

    if (uid_grid_step == 0.01)
      setgridbuttonactive("A");
    else if (uid_grid_step == 0.05)
      setgridbuttonactive("B");
    else if (uid_grid_step == 0.1)
      setgridbuttonactive("C");
    else if (uid_grid_step == 0.5)
      setgridbuttonactive("D");
    else if (uid_grid_step == 0.1)
      setgridbuttonactive("E");
    else
      setgridbuttonactive("");
  }
  else if (App.state.display_units == "ft") {
    $("#gridsizebtnA").html("1/16");
    $("#gridsizebtnB").html("1/12");
    $("#gridsizebtnC").html("1/8");
    $("#gridsizebtnD").html("1/4");
    $("#gridsizebtnE").html("1/2");

    if (uid_grid_step == 1/16)
      setgridbuttonactive("A");
    else if (uid_grid_step == 1/12)
      setgridbuttonactive("B");
    else if (uid_grid_step == 1/8)
      setgridbuttonactive("C");
    else if (uid_grid_step == 1/4)
      setgridbuttonactive("D");
    else if (uid_grid_step == 1/2)
      setgridbuttonactive("E");
    else
      setgridbuttonactive("");
  }
  else if (App.state.display_units == "in") {
    $("#gridsizebtnA").html("1/32");
    $("#gridsizebtnB").html("1/16");
    $("#gridsizebtnC").html("1/8");
    $("#gridsizebtnD").html("1/4");
    $("#gridsizebtnE").html("1/2");

    if (uid_grid_step == 1/32)
      setgridbuttonactive("A");
    else if (uid_grid_step == 1/16)
      setgridbuttonactive("B");
    else if (uid_grid_step == 1/8)
      setgridbuttonactive("C");
    else if (uid_grid_step == 1/4)
      setgridbuttonactive("D");
    else if (uid_grid_step == 1/2)
      setgridbuttonactive("E");
    else
      setgridbuttonactive("");
  }
  else if (App.state.display_units == "yd") {
    $("#gridsizebtnA").html("1/36");
    $("#gridsizebtnB").html("1/16");
    $("#gridsizebtnC").html("1/4");
    $("#gridsizebtnD").html("1/3");
    $("#gridsizebtnE").html("1/2");

    if (uid_grid_step == 1/36)
      setgridbuttonactive("A");
    else if (uid_grid_step == 1/16)
      setgridbuttonactive("B");
    else if (uid_grid_step == 1/4)
      setgridbuttonactive("C");
    else if (uid_grid_step == 1/3)
      setgridbuttonactive("D");
    else if (uid_grid_step == 1/2)
      setgridbuttonactive("E");
    else
      setgridbuttonactive("");
  }

  if (uid_selected_vertices.length == 1)
    $('#gridCenteredAtVertex').removeAttr('disabled');
  else {
    $('#gridCenteredAtVertex').attr('disabled','disabled');
    document.getElementById("gridCenteredAtVertex").checked = false;
  }

  $("#gridCenteredAtVertex").on("change", function() {
    // Simply invoke the function to refresh grid.
    set_grid_size_and_adjust_grid(currently_selected_grid_button);
  });

  show_modal("setGridsizeModal");
}

export function display_show_materials_modal() {
  show_modal('materialsModal');
}

export function display_triangles_modal() {
  var n = uil_get_display_face_triangulation_res();
  $("#display_no_of_triangles").val(n);
  //console_log("No of triangles got:", n);
  show_modal("displayTrianglesModal");
}

export function display_show_curvature_for_all_faces() {
  // Clear a hash that will map each face id to an array of curvature (or color) values
  face_color_hash = {};

  // Call the core to get the curvature values for each face. They will end up in the above hash
  uid_faces.keys().forEach(function(oid) {
    if (!mustNotDisplayFace(oid)) get_face_curvatures(oid);
  });

  // Normalize all array values for all faces and render them.
  var overall_max = 0;
  var overall_min = 0;
  uid_faces.keys().forEach(function(oid) {
    if (!mustNotDisplayFace(oid)) {
      var fmax = Math.max.apply(null, face_color_hash[oid]);
      var fmin = Math.min.apply(null, face_color_hash[oid]);
      overall_min = fmin < overall_min ? fmin : overall_min;
      overall_max = fmax > overall_max ? fmax : overall_max;
    }
  });

  var max_curv = Math.max(Math.abs(overall_max), Math.abs(overall_min));

  // In order to find the 99% point, we merge all arrays into one:
  var all_colors_array = [];
  uid_faces.keys().forEach(function(oid) {
    if (!mustNotDisplayFace(oid))
      all_colors_array = all_colors_array.concat(face_color_hash[oid]);
  });
  // Then take the absolute value of the array
  all_colors_array = all_colors_array.map(function(e) {
    e = Math.abs(e);
    return e;
  });
  // Then sort the big array
  all_colors_array.sort(function(a, b) {
    return a - b;
  });
  // Then find the element at index 99% of length
  var overall_99pc = all_colors_array[Math.round(all_colors_array.length * 0.99)];

  $("#99pc-curv-val").text(Math.round(overall_99pc * 100) / 100);
  $("#max-curv-val").text(Math.round(max_curv * 100) / 100);

  // If the max-curvature box is unset (i.e. contains ""), then we set the curvature_coloring_factor to 1, and
  // we write the max value in the box.
  // If the max-curvature box is set, then we don't change it and we use it to calculate a curvature_coloring_factor.
  if ($("#max-curvature").val() == "-") {
    $("#max-curvature").val(Math.round(max_curv * 100) / 100);
    curvature_coloring_factor = 1.0;
  } else {
    curvature_coloring_factor = max_curv / $("#max-curvature").val();
  }

  uid_faces.keys().forEach(function(oid) {
    if (!mustNotDisplayFace(oid)) {
      if (!curvature_debug_flag) {
        // Normally, we want to normalize the curvatures like so:
        face_color_hash[oid] = face_color_hash[oid].map(function(e) {
          e = (curvature_coloring_factor * e) / max_curv;
          return e;
        });
      } else {
        // INSTEAD, to debug we will use some thresholds to apply different colors:
        face_color_hash[oid] = face_color_hash[oid].map(function(e) {
          if (Math.abs(e) > 200) {
            //console_log("Large curvature:", e);
            e = 1;
          } else if (Math.abs(e) > 50) {
            //console_log("Large curvature:", e);
            e = -1;
          } else e = 0;
          return e;
        });
      }

      display_set_face_mesh_vertex_colors_for_curvature(
        oid,
        face_color_hash[oid]
      );

      var f = uid_faces.getItem(oid);
      f.object3d.geometry.verticesNeedUpdate = true;
      f.object3d.geometry.normalsNeedUpdate = true;
      f.object3d.geometry.colorsNeedUpdate = true;
      f.object3d.geometry.computeFaceNormals();
      f.object3d.geometry.computeVertexNormals();
      f.object3d.geometry.computeBoundingSphere();
    }
  });
  animate();
}

export function display_show_isophotes_for_all_faces() {
  // Clear a hash that will map each face id to an array of normal vectors
  face_normals_hash = {};

  // Call the core to get the curvature values for each face. They will end up in the above hash
  uid_faces.keys().forEach(function(oid) {
    var normals = [];
    if (!mustNotDisplayFace(oid)) get_face_normals(oid);
  });

  // Find view direction
  //Here is how to determine the direction in which the camera is looking -- assuming the camera either has no parent (other than the scene).
  //The camera is looking down it's internal negative z-axis, so create a vector pointing down the negative z-axis:
  var isophote_view_direction = new THREE.Vector3(0, 0, -1);
  //Now, apply the same rotation to the vector that is applied to the camera:
  isophote_view_direction.applyQuaternion(uid_camera.quaternion);
  isophote_view_direction.normalize();

  // First determine the max and min angles found.
  var max_angle = 0;
  var min_angle = 360;
  uid_faces.keys().forEach(function(oid) {
    if (!mustNotDisplayFace(oid)) {
      for (var i = 0; i < face_normals_hash[oid].length; i++) {
        var n = face_normals_hash[oid][i];
        var angle = Math.abs(
          (n.angleTo(isophote_view_direction) * 180) / Math.PI
        );
        max_angle = angle > max_angle ? angle : max_angle;
        min_angle = angle < min_angle ? angle : min_angle;
      }
    }
  });

  var no_of_isophotes = $("#isophote-stripes").val();
  var stripe_width = (max_angle - min_angle) / no_of_isophotes;

  uid_faces.keys().forEach(function(oid) {
    if (!mustNotDisplayFace(oid)) {
      var face_colors = [];
      for (var i = 0; i < face_normals_hash[oid].length; i++) {
        var n = face_normals_hash[oid][i];
        var angle = Math.abs(
          (n.angleTo(isophote_view_direction) * 180) / Math.PI
        );

        var x = Math.trunc(angle / stripe_width);
        if (Math.trunc(angle / stripe_width) % 2 == 0) face_colors[i] = 0;
        else face_colors[i] = 1;
      }

      display_set_face_mesh_vertex_colors_for_isophotes(oid, face_colors);

      var f = uid_faces.getItem(oid);
      f.object3d.geometry.verticesNeedUpdate = true;
      f.object3d.geometry.normalsNeedUpdate = true;
      f.object3d.geometry.colorsNeedUpdate = true;
      f.object3d.geometry.computeFaceNormals();
      f.object3d.geometry.computeVertexNormals();
      f.object3d.geometry.computeBoundingSphere();
    }
  });

  animate();
}

export function display_toggle_vertex_visibility(
    render_again_current_selection = false,
    hide_all_vertices = false,
    set_button_states = true,
    modifier = false,
) {

  if (App.state.editing_reference_sketches)
    return display_toggle_vertex_visibility_for_sketches(render_again_current_selection, hide_all_vertices);

  suppress_animate();

  // Remove and add back all vertices
  var no_vertices_are_displayed = true;
  for (var i = uid_objects_raynode.children.length - 1; i >= 0; i--) {
    var o3d = uid_objects_raynode.children[i];
    var uido = uid_object3dIDToUid_object.getItem(o3d.id);
    if (uido && uido.type == "vertex" && !vertex_is_for_sketch(uido.id)) {
      uid_objects_raynode.remove(uid_objects_raynode.children[i]);
      no_vertices_are_displayed = false;
    }
  }
  if (no_vertices_are_displayed && hide_all_vertices) {
    unsuppress_animate_if_ready(set_button_states);
    return;
  }

  if (render_again_current_selection) {
    // Do nothing
  }
  else if (hide_all_vertices) {
    // Hide all vertices directly, don't do anything with the singled_out_vertices_to_hide list
  }
  else if (uid_selected_vertices.length == 0 && App.state.currently_hiding_vertices) {
    // Redisplay all vertices, don't put anything in singled_out_vertices_to_hide
    singled_out_vertices_to_hide = [];
  }
  else if (uid_selected_vertices.length == 0 && !App.state.currently_hiding_vertices) {
    // Hide all vertices
    uid_vertices.keys().forEach(function(vid) {
      if (uil_is_object_visible(vid) && !vertex_is_for_sketch(vid))
        push_nodups(vid, singled_out_vertices_to_hide);
    });
  }
  else if (uid_selected_vertices.length > 0) {
    // Hide selected vertices (or rest of them, if shift if pressed)
    if (!modifier) {
      uid_selected_vertices.keys().forEach(function(vid){
        if (!vertex_is_for_sketch(vid))
          push_nodups(vid, singled_out_vertices_to_hide);
      });
    }
    else {
      uid_vertices.keys().forEach(function(vid) {
        if (uil_is_object_visible(vid) && !vertex_is_for_sketch(vid) && uid_selected_vertices.keys().indexOf(vid) == -1)
          push_nodups(vid, singled_out_vertices_to_hide);
      });
    }
  }

  if (hide_all_vertices) {
    App.state.currently_hiding_vertices = true;
  } else if (singled_out_vertices_to_hide.length == 0) {
    App.state.currently_hiding_vertices = false;
  } else if (!App.state.currently_hiding_vertices) {
    App.state.currently_hiding_vertices = true;
  }

  if (!render_again_current_selection)
    uid_deselect_all_vertices();

  // Add back vertices accordingly.
  uid_vertices.keys().forEach(function(vid) {
    if (!mustNotDisplayVertex(vid) && !hide_all_vertices) {
      var v = uid_vertices.getItem(vid);
      if (!vertex_is_for_sketch(vid))
        uid_objects_raynode.add(v.object3d);
    }
  });

  if (App.state.editing_edge_tangent_mode && everything_is_hidden())
    exit_editing_edge_tangent_mode();

  resize_all_vertices_for_zoom(0, viewSize, 0);

  unsuppress_animate_if_ready();
}

export function display_toggle_edge_visibility(render_again_current_selection = false, modifier = false) {

  if (App.state.editing_reference_sketches)
    return display_toggle_edge_visibility_for_sketches(render_again_current_selection);

  suppress_animate();

  // Remove all edges
  uid_edges.values()
    .filter(x => !edge_is_for_sketch(x.id))
    .forEach(e => {
        uid_objects_raynode.remove(e.object3d);
        uid_visible_edge_mesh_raynode.remove(e.get_visible_edge_mesh());
    });

  if (render_again_current_selection) {
    // Do nothing
  }
  else if (!App.state.currently_hiding_edges) {
    singled_out_edges_to_hide = [];
    if (uid_selected_edges.length == 0) {
      uid_edges.keys().forEach(function(eid){
        if (uil_is_object_visible(eid) && !edge_is_for_sketch(eid))
          push_nodups(eid, singled_out_edges_to_hide);
      });
    }
    else if (modifier) {
      uid_edges.keys().forEach(function(eid){
        if (uil_is_object_visible(eid) && !uid_selected_edges.hasItem(eid) && !edge_is_for_sketch(eid))
          push_nodups(eid, singled_out_edges_to_hide);
      });
    }
    else {
      uid_selected_edges.keys().forEach(function(eid){
        if (uil_is_object_visible(eid) && !edge_is_for_sketch(eid))
          push_nodups(eid, singled_out_edges_to_hide);
      });
    }
    App.state.currently_hiding_edges = true;
  }
  else {
    if (uid_selected_edges.length == 0 && !modifier) {
      singled_out_edges_to_hide = [];
      App.state.currently_hiding_edges = false;
    }
    else if (uid_selected_edges.length == 0 && modifier) {
      // Do nothing
    }
    else if (uid_selected_edges.length > 0 && !modifier) {
      uid_selected_edges.keys().forEach(function(eid){
        if (uil_is_object_visible(eid) && !edge_is_for_sketch(eid))
          push_nodups(eid, singled_out_edges_to_hide);
      });
    }
    else if (uid_selected_edges.length > 0 && modifier) {
      uid_edges.keys().forEach(function(eid){
        if (uil_is_object_visible(eid) && !uid_selected_edges.hasItem(eid) && edge_is_for_sketch(eid))
          push_nodups(eid, singled_out_edges_to_hide);
      });
    }
  }

  // Add back edges as appropriate
  uid_edges.values().forEach(function(e) {
    if (!mustNotDisplayEdge(e.id) && !edge_is_for_sketch(e.id)) {
      uid_objects_raynode.add(e.object3d);
      uid_visible_edge_mesh_raynode.add(e.get_visible_edge_mesh());
    }
  });

  if (!render_again_current_selection)
    uid_deselect_all_edges();

  if (App.state.editing_edge_tangent_mode && everything_is_hidden())
    exit_editing_edge_tangent_mode();

  unsuppress_animate_if_ready();
}

export function display_toggle_face_visibility(ignore_shift = false, render_again_current_selection = false, modifier = false) {
  // User clicked on the hide faces button, with or without the shift key pressed.

  if (App.state.editing_reference_sketches)
    return display_toggle_face_visibility_for_sketches(ignore_shift, render_again_current_selection);

  suppress_animate();

  var shift_effectively_pressed = modifier;
  if (ignore_shift)
    shift_effectively_pressed = false;

  // First we redisplay any currently hidden faces
  uid_faces.keys().forEach(function(fid) {
    if (!face_is_for_sketch(fid)) {
      if (mustNotDisplayFace(fid) && uil_is_object_visible(fid)) {
        var f = uid_faces.getItem(fid);
        uid_objects_raynode.add(f.object3d);

        f.object3d.geometry.verticesNeedUpdate = true;
        f.object3d.geometry.normalsNeedUpdate = true;
        f.object3d.geometry.computeFaceNormals();
        f.object3d.geometry.computeVertexNormals();
        f.object3d.geometry.computeBoundingSphere();
      }
    }
  });

  // Then depending on whether shift is effectively pressed or not, assemble the new faces to be hidden
  var new_faces_to_hide = [];
  if (!App.state.currently_hiding_faces &&
      uid_selected_faces.length == 0 &&
      singled_out_faces_to_hide.length == 0 &&
      !render_again_current_selection) {
    // Nothing is hidden or selected, make all faces ready to be hidden
    uid_faces.keys().forEach(function(fid) {
      if (uil_is_object_visible(fid) && !face_is_for_sketch(fid))
        push_nodups(fid, new_faces_to_hide);
    });
  }
  else {
    uid_faces.keys().forEach(function(fid) {
      if (uil_is_object_visible(fid) && !face_is_for_sketch(fid)) {
        if (!shift_effectively_pressed) {
          if (uid_selected_faces.hasItem(fid))
            push_nodups(fid, new_faces_to_hide);
        }
        else {
          if (!uid_selected_faces.hasItem(fid))
            push_nodups(fid, new_faces_to_hide);
        }
      }
    });
  }

  if (!App.state.currently_hiding_faces && render_again_current_selection) {
    // Do nothing
  }
  else if (App.state.currently_hiding_faces && new_faces_to_hide.length == 0 && !render_again_current_selection) {
    // Just unclick the button and reset to all faces visible.
    singled_out_faces_to_hide = [];
    App.state.currently_hiding_faces = false;
  }
  else {
    // Hide any faces that we need to hide
    App.state.currently_hiding_faces = true;
    new_faces_to_hide.forEach(function(fid){
      push_nodups(fid, singled_out_faces_to_hide);
    });

    // Remove faces accordingly.
    uid_faces.keys().forEach(function(fid) {
      if (mustNotDisplayFace(fid)) {
        var f = uid_faces.getItem(fid);
        uid_objects_raynode.remove(f.object3d);
      }
    });

    uid_deselect_all_faces();
  }

  if (App.state.editing_edge_tangent_mode && everything_is_hidden())
    exit_editing_edge_tangent_mode();

  if (App.state.shadows_visible) {
    hide_shadows();
    show_shadows();
  }

  unsuppress_animate_if_ready();
}

export function display_toggle_vertex_visibility_for_sketches(render_again_current_selection = false, hide_all_vertices = false) {
  // Just resize
  resize_all_vertices_for_zoom(0, viewSize, 0);
}

export function display_toggle_edge_visibility_for_sketches(render_again_current_selection = false) {

}

export function display_toggle_face_visibility_for_sketches(ignore_shift = false, render_again_current_selection = false) {
  // User clicked on the hide faces button, with or without the shift key pressed.

  suppress_animate();

  var shift_effectively_pressed = shift_pressed();
  if (ignore_shift)
    shift_effectively_pressed = false;

  // First we redisplay any currently hidden faces
  uid_faces.keys().forEach(function(fid) {
    if (!face_is_for_sketch(fid)) {
      if (mustNotDisplayFace(fid) && uil_is_object_visible(fid)) {
        var f = uid_faces.getItem(fid);
        if (face_is_for_sketch(f.id))
          current_scene_raynode_for_sketch_objects().add(f.object3d);
        else
          uid_objects_raynode.add(f.object3d);

        f.object3d.geometry.verticesNeedUpdate = true;
        f.object3d.geometry.normalsNeedUpdate = true;
        f.object3d.geometry.computeFaceNormals();
        f.object3d.geometry.computeVertexNormals();
        f.object3d.geometry.computeBoundingSphere();
      }
    }
  });

  // Also display all sketches
  for (var i = uid_sketch_plane_raynode.children.length - 1; i >= 0; i--) {
    var m = uid_sketch_plane_raynode.children[i];
    uid_sketch_plane_raynode.remove(m);
  }

  // Then depending on whether shift is effectively pressed or not, assemble the new faces to be hidden
  var new_faces_to_hide = [];
  if (uid_selected_faces.length == 0 && singled_out_faces_to_hide.length == 0 && !render_again_current_selection) {
    // Nothing is hidden or selected, make all faces ready to be hidden
    uid_faces.keys().forEach(function(fid) {
      if (uil_is_object_visible(fid))
        push_nodups(fid, new_faces_to_hide);
    });
  }
  else {
    uid_faces.keys().forEach(function(fid) {
      if (uil_is_object_visible(fid)) {
        if (!shift_effectively_pressed) {
          if (uid_selected_faces.hasItem(fid))
            push_nodups(fid, new_faces_to_hide);
        }
        else {
          if (!uid_selected_faces.hasItem(fid))
            push_nodups(fid, new_faces_to_hide);
        }
      }
    });
  }

  if (!App.state.currently_hiding_faces && render_again_current_selection) {
    // Do nothing
  }
  else if (App.state.currently_hiding_faces && new_faces_to_hide.length == 0 && !render_again_current_selection) {
    // Just unclick the button and reset to all faces visible.
    singled_out_faces_to_hide = [];
    App.state.currently_hiding_faces = false;
  }
  else {
    // Hide any faces that we need to hide
    App.state.currently_hiding_faces = true;
    new_faces_to_hide.forEach(function(fid){
      push_nodups(fid, singled_out_faces_to_hide);
    });

    // Remove faces accordingly.
    uid_faces.keys().forEach(function(fid) {
      if (mustNotDisplayFace(fid)) {
        var f = uid_faces.getItem(fid);
        uid_objects_raynode.remove(f.object3d);
      }
    });

    uid_deselect_all_faces();
  }

  if (App.state.shadows_visible) {
    hide_shadows();
    show_shadows();
  }

  non_deleted_reference_sketches().forEach(function(loaded_sketch_element) {
    if (!mustNotDisplayFace(loaded_sketch_element.face_id))
      uid_sketch_plane_raynode.add(loaded_sketch_element.mesh);
  });

  unsuppress_animate_if_ready();
}

export function display_toggle_show_wireframe() {
  App.state.wireframe_visible = !App.state.wireframe_visible;
}

export function display_show_hidden_edges() {
  uid_edges.values().forEach(function(e) {
    e.visible_edge_mesh.renderOrder = 998;
  });

  // Make hidden vertices visible
  uid_vertices.values().forEach(function(v){
    v.object3d.renderOrder = 998;
  });
  make_all_faces_semitransparent();
  animate();
}

export function display_hide_hidden_edges() {
  uid_edges.values().forEach(function(e){
    e.object3d.onBeforeRender = function( renderer ) { };
    e.visible_edge_mesh.onBeforeRender = function( renderer ) { };
  });

  // Make hidden vertices invisible
  uid_vertices.values().forEach(function(v){
    v.object3d.onBeforeRender = function( renderer ) { };
  });

  make_all_faces_opaque();
  animate();
}

export function display_set_face_curvatures(id, colors_array) {
  if (mustNotDisplayFace(id)) return;
  var f = uid_faces.getItem(id);
  face_color_hash[id] = colors_array;
}

export function display_set_face_normals(id, normals_array) {
  if (mustNotDisplayFace(id)) return;

  var f = uid_faces.getItem(id);
  face_normals_hash[id] = normals_array;
}

export function display_set_face_mesh_vertex_colors_for_curvature(id, colors_array) {
  if (mustNotDisplayFace(id)) return;

  var f = uid_faces.getItem(id);

  // Go over all faces and set default colors for all vertices of each
  for (var i = 0; i < f.object3d.geometry.faces.length; i++) {
    var face = f.object3d.geometry.faces[i];

    // If the color is < 0, then we want to reduce the blue and green by as much, leaving the red at 1.0.
    // If the color is > 0, then we want to reduce the red and green by as much, leaving the blue at 1.0.
    if (colors_array[face.a] < 0)
      face.vertexColors[0].setRGB(
        1.0,
        1.0 - Math.abs(colors_array[face.a]),
        1.0 - Math.abs(colors_array[face.a])
      );
    else
      face.vertexColors[0].setRGB(
        1.0 - Math.abs(colors_array[face.a]),
        1.0 - Math.abs(colors_array[face.a]),
        1.0
      );

    if (colors_array[face.b] < 0)
      face.vertexColors[1].setRGB(
        1.0,
        1.0 - Math.abs(colors_array[face.b]),
        1.0 - Math.abs(colors_array[face.b])
      );
    else
      face.vertexColors[1].setRGB(
        1.0 - Math.abs(colors_array[face.b]),
        1.0 - Math.abs(colors_array[face.b]),
        1.0
      );

    if (colors_array[face.c] < 0)
      face.vertexColors[2].setRGB(
        1.0,
        1.0 - Math.abs(colors_array[face.c]),
        1.0 - Math.abs(colors_array[face.c])
      );
    else
      face.vertexColors[2].setRGB(
        1.0 - Math.abs(colors_array[face.c]),
        1.0 - Math.abs(colors_array[face.c]),
        1.0
      );
  }
}

export function display_set_face_mesh_vertex_colors_for_isophotes(id, colors_array) {
  if (mustNotDisplayFace(id)) return;

  var f = uid_faces.getItem(id);

  // Go over all faces and set default colors for all vertices of each
  for (var i = 0; i < f.object3d.geometry.faces.length; i++) {
    var face = f.object3d.geometry.faces[i];

    face.vertexColors[0].setRGB(
      colors_array[face.a],
      colors_array[face.a],
      colors_array[face.a]
    );
    face.vertexColors[1].setRGB(
      colors_array[face.b],
      colors_array[face.b],
      colors_array[face.b]
    );
    face.vertexColors[2].setRGB(
      colors_array[face.c],
      colors_array[face.c],
      colors_array[face.c]
    );
  }
}

export function display_set_vertex_xyz(xstr, ystr, zstr, xchoice, ychoice, zchoice) {
  const x = xchoice === 'set' ? get_unitless_value_from_display_unit(Number(xstr)) : undefined;
  const y = ychoice === 'set' ? get_unitless_value_from_display_unit(Number(ystr)) : undefined;
  const z = zchoice === 'set' ? get_unitless_value_from_display_unit(Number(zstr)) : undefined;

  suppress_undo_tracking();
  for (const vid of uid_selected_vertices.keys()) {
    const v = uid_vertices.getItem(vid);

    let newx = x === undefined || xchoice == "leave" ? v.object3d.position.x : x;
    let newy = y === undefined || ychoice == "leave" ? v.object3d.position.y : y;
    let newz = z === undefined || zchoice == "leave" ? v.object3d.position.z : z;

    if (xchoice == "first" || ychoice == "first" || zchoice == "first") {
      const vfirst = uid_selected_vertices.firstValue();
      if (xchoice == "first") newx = vfirst.object3d.position.x;
      if (ychoice == "first") newy = vfirst.object3d.position.y;
      if (zchoice == "first") newz = vfirst.object3d.position.z;
    }
    uil_move_vertex_to_coords(vid, newx, newy, newz);
  }
  unsuppress_undo_tracking_if_ready(false);
  uid_deselect_all_objects();
}

export function start_ui_side_compound_operation() {
  suppress_animate();
  currently_in_ui_side_compound_operation = true;
}

export function in_ui_side_compound_operation() {
  return currently_in_ui_side_compound_operation;
}

export function end_ui_side_compound_operation() {
  currently_in_ui_side_compound_operation = false;
  unsuppress_animate_if_ready();
}

export function display_set_model_scale() {
  App.state.display_units = $("#display_units_select").val();
  hide_modal("setModelScaleModal");
}

export function display_delete_object(oid) {
  var ui_object;

  if (isVertex(oid))
    ui_object = uid_vertices.getItem(oid);
  else if (isEdge(oid))
    ui_object = uid_edges.getItem(oid);
  else if (isFace(oid))
    ui_object = uid_faces.getItem(oid);
  else {
    // UI-side object is not found, it may be invisible, no need to delete it (again).
    animate();
    return;
  }

  // If it's one of the selected faces, do not remove it from the scene.
  // if (isFace(oid) && mustNotDisplayFace(oid)) return; XXX XXX

  // We cannot determine with certainty if the face is for sketch because the face
  // has already been removed on the core side. So we try to remove it from all
  // locations, and one will work.
  /*
  if (isFace(oid) && face_is_for_sketch(ui_object.id)) {
    if (!mustNotDisplayFace(ui_object.id))
      current_scene_raynode_for_sketch_objects().remove(ui_object.object3d);
    else
      uid_hidden_sketch_objects_raynode.remove(ui_object.object3d);
  }
  else
    uid_objects_raynode.remove(ui_object.object3d);
  */
  current_scene_raynode_for_sketch_objects().remove(ui_object.object3d);
  uid_hidden_sketch_objects_raynode.remove(ui_object.object3d);
  uid_objects_raynode.remove(ui_object.object3d);

  if (isVertex(oid))
    uid_vertices.removeItem(oid);
  else if (isEdge(oid))
    uid_edges.removeItem(oid);
  else if (isFace(oid))
    uid_faces.removeItem(oid);

  uid_object3dIDToUid_object.removeItem(ui_object.o3d_id);

  // Remove from scene
  if (ui_object.type == "face") {
    ui_object.freeFace();
  }
  else if (ui_object.type == "edge") {
    ui_object.remove_visible_edge_mesh(false);

    ui_object.object3d.geometry.dispose();
    ui_object.object3d.geometry = null;
    ui_object.object3d.material.dispose();
    ui_object.object3d.material = null;
  }
  else if (ui_object.type == "vertex") {
    ui_object.object3d.geometry.dispose();
    ui_object.object3d.geometry = null;
    ui_object.object3d.material.dispose();
    ui_object.object3d.material = null;
  }
  ui_object.object3d = null;
  ui_object = null;

  uid_highlighted_object_o3d = null;

  animate();
}

export function display_clear_model() {
  // Clears the entire model.

  uid_deselect_all_objects();

  // Move any reference sketch objects into the default uid_objects_raynode
  if (App.state.imported_sketches.length > 0) {
    if (App.state.editing_reference_sketches)
      stop_editing_sketches();

    for (var i = uid_sketch_objects_raynode.children.length - 1; i >= 0; i--) {
      uid_sketch_objects_raynode.children.splice(i,1);
    }
    for (var i = uid_hidden_sketch_objects_raynode.children.length - 1; i >= 0; i--) {
      uid_hidden_sketch_objects_raynode.children.splice(i,1);
    }
  }


  if (uid_current_api_call == "phi_undo" || uid_current_api_call == "phi_redo") {
    // When undoing/redoing we don't want to clear the entries related to reference objects, since these are not updated.
    var key_array = [... uid_object3dIDToUid_object.keys()];
    key_array.forEach(function(oid){
      var uido = uid_object3dIDToUid_object.getItem(oid);
      if (uid_reference_vertices.hasItem(uido.id) || uid_reference_edges.hasItem(uido.id) || uid_reference_faces.hasItem(uido.id)) {
        // This is a reference object, leave it in the hashtable
      }
      else {
        uid_object3dIDToUid_object.removeItem(oid);
      }
    });
  }
  else {
    uid_object3dIDToUid_object.clear();
  }
  uid_face_colors.clear();

  // Clear all existing objects from scene
  var i;
  var o;
  for (i = uid_faces.values().length-1; i >= 0; i--) {
    o = uid_faces.getItemAtIndex(i);
    uid_faces.removeItem(o.id);
    uid_objects_raynode.remove(o.object3d);
    o.freeFace();
    o.object3d = null;
    o = null;
  }


  for (i = uid_edges.values().length-1; i >= 0; i--) {
    o = uid_edges.getItemAtIndex(i);
    uid_edges.removeItem(o.id);
    uid_objects_raynode.remove(o.object3d);
    o.freeEdge();
    o.object3d = null;
    o = null;
  }

  for (i = uid_vertices.values().length-1; i >= 0; i--) {
    o = uid_vertices.getItemAtIndex(i);
    uid_vertices.removeItem(o.id);
    uid_objects_raynode.remove(o.object3d);
    o.freeVertex();
    o.object3d = null;
    o = null;
  }

  uid_faces.clear();
  uid_edges.clear();
  uid_vertices.clear();

  // Clear all lists and hashtables?
  if (uid_current_api_call != "phi_undo" && uid_current_api_call != "phi_redo") {
    hide_indicated_vertices();
    hide_shadows();
  }
  animate();
}

export function uid_delete_selected_objects(shift_key_is_pressed = false) {
    if (shift_key_is_pressed) set_shift(true);
    const can_delete_objs = can_delete_selected_objects();
    if (can_delete_objs[0])
      uil_delete_selected_objects(shift_key_is_pressed);
    else
      display_message_to_user(LOG.WARN, can_delete_objs[1]);
}

/** Find currently highlighted edge and then its midpoint */
export function uid_highlight_edge_midpoint() {
    const uido = find_currently_pointed_object();
    if (uido && uido.type == 'edge')
        show_edge_midpoint(uido.id);
}

export function uid_hide_midpoint_crosshair() {
    if (uid_edge_midpoint_visible)
        hide_edge_midpoint(true, false);
    hide_crosshair();
    animate();
}

export function uid_flip_rigid_move_manipulator_x() {
    const v_center = uid_rigid_move_manipulator.center;
    const v_dir = new THREE.Vector3(1, 0, 0);
    const v_perpendicular = new THREE.Vector3(0, 1, 0);
    uid_rigid_move_manipulator.match_to_vectors(v_center, v_dir, v_perpendicular);
    animate();
}

export function uid_flip_rigid_move_manipulator_y() {
    const v_center = uid_rigid_move_manipulator.center;
    const v_dir = new THREE.Vector3(0, 1, 0);
    const v_perpendicular = new THREE.Vector3(0, 0, 1);
    uid_rigid_move_manipulator.match_to_vectors(v_center, v_dir, v_perpendicular);
    animate();
}

export function uid_flip_rigid_move_manipulator_z() {
    const v_center = uid_rigid_move_manipulator.center;
    const v_dir = new THREE.Vector3(0, 0, 1);
    const v_perpendicular = new THREE.Vector3(1, 0, 0);
    uid_rigid_move_manipulator.match_to_vectors(v_center, v_dir, v_perpendicular);
    animate();
}

export function uid_all_selected_objects() {
    return flatten([
        uid_selected_faces.keys(),
        uid_selected_edges.keys(),
        uid_selected_vertices.keys(),
    ]);
}

export function uid_fix_objects(force_unfix = false) {
    const fixop = potential_fixing_operation_for_selected_objects();
    switch (fixop) {
        case 'enable_fixing':
            return uid_set_fixing_of_selected_objects(true && !force_unfix);

        case 'enable_unfixing':
            return uid_set_fixing_of_selected_objects(false);

        default:
            return display_message_to_user(LOG.WARN, "Cannot fix/unfix selected objects.");
    }
}

export function uid_add_or_abort_edge_drawing(shift_key_is_pressed) {
    if (App.state.drawing_edge)
        abort_drawing_edge_on_face();
    else if (!operation_state.add_edge.disabled.value)
        add_edge_action(shift_key_is_pressed);
    animate();
}

export function uid_toggle_tangencies() {
    if (App.state.tangencies_are_displayed)
        hide_tangencies();
    else
        display_tangencies();
}

export function uid_make_edges_symmetric_if_possible() {
    if (App.state.symmetry_plane_selected && (uid_selected_faces.length > 0 || uid_selected_edges.length > 0))
        uid_make_edges_symmetric_with_spinner(true, false);
}

export function uid_global_manipulator_coordinates() {
    // Clear any pop-out amount value, since we're no longer looking along the pop direction
    pop_amount_value = 0;
    uid_rigid_move_manipulator.attach_to_objects(uid_rigid_move_manipulator.is_for_fence, 0.5);
    animate();
}

export function uid_local_manipulator_coordinates() {
    // Clear any pop-out amount value, since we're no longer looking along the pop direction
    pop_amount_value = 0;
    uid_rigid_move_manipulator.attach_to_objects(true, 0.5);
    animate();
}

export function uid_toggle_drawing_edge_on_face() {
    if (App.state.carving)
        abort_drawing_edge_on_face();
    else if (!operation_state.carve.disabled.value)
        start_drawing_edge_on_face(true, shift_pressed());
}

export function uid_clone_selected_object() {
    if (uid_raynode !== uid_objects_raynode)
        return;
    else if (
        uid_selected_faces.length > 0
        && !operation_state.copy.disabled.value
    )
        clone_body();
    else if (
        uid_selected_edges.length > 1
        && uid_selected_vertices.length === uid_selected_edges.length
        && uid_selected_faces.length === 0
        && !operation_state.copy_edge.disabled.value
    )
        clone_edge();
}

export function uid_attempt_save() {
    if (uid_raynode == uid_objects_raynode)
        uid_download(true);
    else
        display_message_to_user(
            LOG.WARN,
            'Please exit or complete the current operation to save your work.',
        );
}

export function uid_split_patch_selected_faces(shift_key_is_pressed) {
    if (uid_selected_faces.length > 0) {
        uid_splitpatch_faces(
            uid_selected_faces.keys(),
            shift_key_is_pressed, false, false
        );
        animate();
    }
}

export function uid_fix_or_unfix_tangent_at_vertex() {
    unhighlight_currently_highlighted_object();
    const fixop = potential_fixing_operation_for_selected_objects()

    switch (fixop) {
        case 'enable_fixing':
            fix_edge_tangent_at_vertex(0, null);
            uid_deselect_all_objects();
            break;

        case 'enable_unfixing':
            unfix_edge_tangent_at_vertex(0, null, null);
            uid_deselect_all_objects();
            break;

        case 'disable':
        default:
            display_message_to_user(
                LOG.WARN,
                "In editing edge tangency mode. Select an edge to lock/unlock the tangent direction.",
            );
            break;
    }
}

export function uid_unfix_tangent_at_vertex() {
    if (potential_fixing_operation_for_selected_objects() !== 'disable') {
        unfix_edge_tangent_at_vertex(0, null, null);
        uid_deselect_all_objects();
    }
}

export function uid_toggle_snapping_to_object() {
    const total_selected_objects =
        uid_selected_vertices.length
        + uid_selected_edges.length
        + uid_selected_faces.length;

    if (App.state.snapping_to_object)
        abort_snap_to_object();
    else if (uid_raynode == uid_objects_raynode && total_selected_objects === 1)
        start_snap_to_object();

    animate();
}

export function uid_show_rigid_or_tangent_manipulator() {
    unhighlight_currently_highlighted_object();
    if (selected_objects_are_for_rotating_edge_tangent())
        uid_show_rigid_move_manipulator(
            false,
            0,
            true,
            false,
            false,
            true,
            shift_pressed(),
            false,
        );
    else
        uid_show_rigid_move_manipulator(
            false,
            0,
            true,
            false,
            false,
            false,
            shift_pressed(),
            false,
        );
}

export function uid_add_vertex() {
    if (App.state.drawing_edge || App.state.carving)
        // We are drawing an edge on a face, collecting samples, and the user indicates they want a vertex at that location
        add_vertex_location_while_drawing_edge_on_face();
    else if (operation_state.add_vertex.disabled.value)
        return;
    else if (App.state.adding_vertex)
        stop_adding_vertex();
    else
        start_adding_vertex(shift_pressed());
}

export function debug_face_labels() {
    uil_set_face_label(uid_faces.keys(), 'Face-label!');
    for (const fid of uid_faces.keys()) {
        const r = uil_get_face_label(fid);
        display_message_to_user(LOG.INFO, `${fid}: ${r}`);
    }
}

export function debug_wacky_shaders() {
    uid_state.shaders_on = !uid_state.shaders_on;

    uid_faces.values()
        .filter(f => !face_is_for_sketch(f.id))
        .forEach(f => {
            regenerate_face_material(f, f.material_name, true, App.state.wireframe_visible);
            if (f.getMaterial()) {
                f.getMaterial().depthTest = true;
                f.getMaterial().depthWrite = false; // XXX
            }
            if (f.getInnerMaterial()) {
                f.getInnerMaterial().depthTest = true;
                f.getInnerMaterial().depthWrite = false; // XXX
            }
            if (f.getMirrorMaterial()) {
                f.getMirrorMaterial().depthTest = true;
                f.getMirrorMaterial().depthWrite = false; // XXX
            }
            if (f.getMirrorInnerMaterial()) {
                f.getMirrorInnerMaterial().depthTest = true;
                f.getMirrorInnerMaterial().depthWrite = false; // XXX
            }
        });

    display_toggle_show_wireframe();
    if (
        (!uid_state.shaders_on && App.state.wireframe_visible)
        || (uid_state.shaders_on && !App.state.wireframe_visible)
    )
        display_toggle_show_wireframe();

    uid_unhighlight_all_objects();
    animate();
}

export function debug_core_version_info() {
    const r = get_core_version_info();
    display_message_to_user(LOG.INFO, r);
}

export function delete_all_2_vertices() {
    suppress_animate();
    suppress_undo_tracking();
    uid_deselect_all_objects();

    const vertices_to_delete = unique(
        uid_vertices.keys()
            .filter(vid => {
                const es = uil_get_edges_of_vertex(vid);
                return (es.length == 2 && !uil_is_object_fixed(vid) && !vertex_is_for_sketch(vid) &&
                        uil_is_joint_smooth(es[0], es[1]));
            })
    );

    const n = vertices_to_delete.length;
    uil_delete_vertices(vertices_to_delete, false);
    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    display_message_to_user(LOG.INFO, `Deleted ${n} vertices.`);
}

export function delete_all_2_vertices_preserve_shape() {
    suppress_animate();
    suppress_undo_tracking();
    uid_deselect_all_objects();

    // First remove non-smooth vertices without the flag to preserve edge shape
    const [nonsmooth, smooth] = pipe(
        uid_vertices.keys(),
        filter(vid => {
            const es = uil_get_edges_of_vertex(vid);
            return es.length === 2
                && !uil_is_object_fixed(vid)
                && !vertex_is_for_sketch(vid);
        }),
        partition(vid => {
            const es = uil_get_edges_of_vertex(vid);
            return uil_is_joint_smooth(es[0], es[1]);
        }),
    );

    // Don't delete nonsmooth vertices
    //uil_delete_vertices(nonsmooth, false);
    uil_delete_vertices(smooth, true);

    const n = smooth.length /* + nonsmooth.length */;
    unsuppress_undo_tracking_if_ready(false);
    unsuppress_animate_if_ready();
    display_message_to_user(LOG.INFO, `Deleted ${n} vertices.`);
}

export function handle_escape(
    preserve_keypress_states = false,
    hard = false,
    hide_references = false,
) {
    if (uid_in_dragging_mode)
      // Don't allow escape while in dragging mode
      return;

    interrupt_offset_face();

    if (App.state.setting_symmetry_plane)
        App.state.setting_symmetry_plane = false;

    // Hide any open modals
    var search_modal_was_open = App.state.modal.searchModal;

    if (!App.state.is_demo)
      hide_modal(null, preserve_keypress_states);

    App.state.spinner = undefined;

    // Reset shift, control, alt and command states
    if (!preserve_keypress_states) {
        set_shift(false);
        set_control(false);
        set_alt(false);
        set_gui(false);
    }

    App.state.currently_exporting_model = false;

    App.state.panning = false;
    App.state.rotating = false;
    App.state.allow_rotate = true;
    App.state.currently_rotating_viewpoint = false;

    if (App.state.converting_reference_to_phi)
      abort_convert_reference_faces_to_phi();

    if (App.state.drawing_edge)
        abort_drawing_edge_on_face();

    if (App.state.carving)
        abort_drawing_edge_on_face();

    App.state.allow_rotate = true;

    if (uid_state.edge_tangent_manipulators_are_shown)
      uid_toggle_edge_tangent_manipulators();
    else if (App.state.rigid_move_manipulator) {
      uid_deselect_all_objects();
      uid_hide_rigid_move_manipulator(false, 0, true);
    }
    else if (App.state.adding_vertex)  {
      stop_adding_vertex();
    }
    else if (uid_state.adding_edge_endpoints_status)  {
      // For now nothing, but don't deselect or you screw it.
    }
    else if (App.state.snapping_to_object) {
      abort_snap_to_object();
    }
    else if (App.state.projecting_edges) {
      abort_projecting_edges_onto_faces();
    }
    else if ((hide_references) &&
            (uid_state.showing_reference_stl_vertices || uid_state.showing_reference_partstudio_vertices ||
             App.state.showing_reference_stl || App.state.showing_reference_partstudio_models)) {
      hide_all_reference_stl_vertices();
      hide_all_reference_partstudio_vertices();
      hide_reference_partstudio_model();
      hide_reference_stl_model();
      hide_reference_parasolid_model();
      App.state.snapping_to_object = false;
      uid_raynode = uid_objects_raynode;
      uid_deselect_all_objects();
      uid_manip_raynode.remove(uid_axes_planes_raynode);
      if (App.state.axes_visible)
        uid_objects_raynode.add(uid_axes_planes_raynode);
    }
    else if (search_modal_was_open) {
      // Do nothing, i.e. don't deselect
    }
    else
      uid_deselect_all_objects();

    if (hard) {
      // Try a "hard reset"
      reset_animate_count();
      reset_zero_thickness_checking_count();
      reset_undo_tracking(false);
      reset_core_face_tessellation();

      console.log("Hard esc");
    }

    unsuppress_animate_if_ready();
    recalculate_view_dimensions();
    set_button_states();
}

export function* uid_every_object_of_body(bid) {
    yield bid;
    for (const fid of faces_of_body(bid)) {
        yield fid;
        for (const eid of uil_get_edges_of_face(fid)) {
            yield eid;
            for (const vid of uil_get_vertices_of_edge(eid))
                yield vid;
        }
    }
}

export function uid_body_is_completely_unfixed(bid) {
    return every(x => !uil_is_object_fixed(x))(uid_every_object_of_body(bid));
}

export function uid_quick_scale() {
    unhighlight_currently_highlighted_object();
    // If two vertices or one edge are selected bring up the set-distance modal.
    if (
        (uid_selected_vertices.length == 2 && uid_selected_edges.length == 0 && uid_selected_faces.length == 0)
        || (uid_selected_vertices.length == 0 && uid_selected_edges.length == 1 && uid_selected_faces.length == 0)
    ) {
        // Check that all selected objects are in the same body.
        let bid;
        if (uid_selected_vertices.length == 2) {
            bid = uil_get_body_of_vertex(uid_selected_vertices.firstKey());
            if (uil_get_body_of_vertex(uid_selected_vertices.getKeyAtIndex(1)) != bid) {
                display_message_to_user(LOG.WARN, 'Both vertices must be on the same body.');
                return;
            }
        } else if (uid_selected_edges.length == 1)
            bid = uil_get_body_of_edge(uid_selected_edges.firstKey());

        if (!uid_body_is_completely_unfixed(bid)) {
            display_message_to_user(LOG.WARN, "No faces, edges or vertices of the body must be fixed to perform a quick scale. If you want to scale only parts of the body, try a normal scale by hitting Shift-D or the Scale button.");
            return;
        }
        display_vertex_distance_modal();
    } else {
        display_message_to_user(LOG.WARN, "For a quick scale you need to select one edge or two vertices. Otherwise hit shift-D for the regular scale menu.");
    }
}

export function uid_start_offset_face_if_possible() {
    if (
        !App.state.offset_face_selecting_vertex
        && !App.state.offset_face_dragging
        && uid_selected_vertices.length == 0
        && uid_selected_edges.length == 0
        && uid_selected_faces.length == 1
    )
        start_offset_face();
}

export function uid_smoothen_selected_objects() {
    if (
        !has_selected_objects()
        || every(uil_is_object_fixed)(uid_all_selected_objects())
    )
        return;
    smoothen_objects(shift_pressed());
}

export function uid_toggle_editing_edge_tangent_mode() {
    if (App.state.editing_edge_tangent_mode)
        exit_editing_edge_tangent_mode();
    else
        enter_editing_edge_tangent_mode();
}

export function everything_is_hidden() {
    return uid_vertices.keys().every(mustNotDisplayVertex)
        && uid_edges.keys().every(mustNotDisplayEdge)
        && uid_faces.keys().every(mustNotDisplayFace);
}

/** true if no objects exist in the scene */
export function uid_scene_is_empty() {
    return uid_faces.keys().length === 0
        && uid_vertices.keys().length === 0
        && uid_edges.keys().length === 0
        && uid_reference_edges.length === 0
        && uid_reference_faces.length === 0
        && uid_reference_vertices.length === 0
        && App.state.imported_sketches.filter(x => !x.is_deleted).length === 0
        && App.state.imported_partstudio_reference_models.length === 0
        && App.state.imported_stl_models.length === 0;
}

export const uid_rotate_edge_list_around_axis = withSpinner('Rotating', (edge_list, axis_center, axis_direction, angle) => {
    uil_rotate_edge_list_around_axis(edge_list, axis_center, axis_direction, angle)
    uil_end_drag_manipulator();
}, (edge_list, axis_center, axis_direction, angle) => edge_list.length > 15);

export const uid_move_vertices = withSpinner("Moving", (vertices, origin, direction) => {
    start_move_vertices(vertices);
    move_vertices(vertices, origin, direction);
    end_move_vertices(vertices);
}, (vertices, origin, direction) => vertices.length > 9);

export const uid_translate_edge_list = withSpinner("Moving", (edge_list, uid_manip_previous_raycaster_origin, uid_manip_previous_raycaster_direction,
  uid_manip_current_raycaster_origin, uid_manip_current_raycaster_direction) => {
    uil_translate_edge_list(edge_list, uid_manip_previous_raycaster_origin, uid_manip_previous_raycaster_direction,
      uid_manip_current_raycaster_origin, uid_manip_current_raycaster_direction);
    uil_end_drag_manipulator();
}, xs => xs.length > 7);

export async function execute_hardcoded_boolean_operation() {
  display_result_of_boolean_operation(psdata);
}

async function sampleThreeJSLoadOfMOdel(data)
{
  const material = new THREE.MeshStandardMaterial({
    color: 0x00ff00, // Green, ensure this isn't too dark
    roughness: 0.5, // Adjust roughness
    metalness: 0.1 // Adjust metalness
  });
    const jobj = JSON.parse(data);
    let finalSize = 0;
    jobj.forEach(bodyStr => {
        Object.values(bodyStr).forEach(data => {
            data.Faces.forEach(f => {
                finalSize += f.tesselatedVertices.length * 3;
            });
        });
    });

    let vertices = new Float32Array(finalSize);
    let normals = new Float32Array(finalSize); // Uncommented and corrected
    let currentVIndex = 0;
    let currentNIndex = 0;

    function addVertex(x, y, z) {
        if (currentVIndex >= vertices.length) {
            console.error('Attempted to add more vertices than pre-allocated space.');
            return;
        }
        vertices[currentVIndex++] = x;
        vertices[currentVIndex++] = y;
        vertices[currentVIndex++] = z;
    }

    function addNormal(x, y, z) {
        if (currentNIndex >= normals.length) {
            console.error('Attempted to add more normals than pre-allocated space.');
            return;
        }
        normals[currentNIndex++] = x;
        normals[currentNIndex++] = y;
        normals[currentNIndex++] = z;
    }


    jobj.forEach(bodyStr => {
      Object.values(bodyStr).forEach(data => {
          data.Faces.forEach(f => {
              f.tesselatedVertices.forEach(v => {
                  addVertex(v.x, v.y, v.z);
              });
              f.tesselatedNormals.forEach(v => {
                  addNormal(v.x, v.y, v.z);
              });
          });
      });
  });
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  const mesh = new THREE.Mesh(geometry, material);
  mesh.scale.set(100,100,100);
  scene.add(mesh);
}

/* THIS IS THE VERSION OF TASSOS
export async function display_result_of_boolean_operation(data) {
  sampleThreeJSLoadOfMOdel(data);
  return;

  const jobj = JSON.parse(data);
  let fid = 10000;
  jobj.forEach(function(bodyStr){
    Object.entries(bodyStr).forEach(([pkId, data]) => {
      // The curve information is not required atm
      // Object.entries(data.Curves).forEach(([curveId, curveInfo]) => {
      //   curveInfo.forEach(function(v){
      //     t = new THREE.Vector3(v.x, v.y, v.z);
      //     t.multiplyScalar(100);
      //     add_small_ball(t.x, t.y, t.z, 0, 255, 0, 0.01);
      //     });
      //   });
        data.Faces.forEach(function(f){
          let ps_vertices = [];
          let ps_triangles = [];
          let c = 0;
          let i = 0;

          for (let index = 0; index < f.tesselatedVertices.length; index += 3) {
            // Read three vertices at once
            const v1 = f.tesselatedVertices[index];
            const v2 = f.tesselatedVertices[index + 1];
            const v3 = f.tesselatedVertices[index + 2];

            // Create THREE.Vector3 objects for each vertex
            const t1 = new THREE.Vector3(v1.x, v1.y, v1.z).multiplyScalar(100);
            const t2 = new THREE.Vector3(v2.x, v2.y, v2.z).multiplyScalar(100);
            const t3 = new THREE.Vector3(v3.x, v3.y, v3.z).multiplyScalar(100);

            // Push vectors to vertices array
            ps_vertices.push(t1, t2, t3);

            // Create triangle from the last three added vertices' indices
            let baseIndex = index;
            let tr = new Uid_face_triangle(baseIndex, baseIndex + 1, baseIndex + 2);
            ps_triangles.push(tr);

            // Add small balls at each vertex's location
            // add_small_ball(t1.x, t1.y, t1.z, 255, 0, 0, 0.01);
            // add_small_ball(t2.x, t2.y, t2.z, 255, 0, 0, 0.01);
            // add_small_ball(t3.x, t3.y, t3.z, 255, 0, 0, 0.01);
          }

          var ref_face_obj = new Uid_ref_face( fid, ps_vertices, ps_triangles, null, true, fid, "stl");
          uid_add_reference_face(ref_face_obj);
          fid++;
        });
      });
    });

  uid_reference_stl_raynode.add(uid_ref_stl_faces_raynode);

  App.state.showing_reference_parasolid = true;
  view_all(false);
}
*/

/* THIS IS THE VERSION OF STEPH */
export async function display_result_of_boolean_operation(data) {
  console.log(data);
  let jobj;
  try {
    jobj = JSON.parse(data);
    if (!Array.isArray(jobj)) {
      display_message_to_user(LOG.WARN, "Boolean operation failed.");
      return;
    }
  } catch (error) {
    display_message_to_user(LOG.WARN, "Boolean operation failed.");
    return;
  }

  // if (!Array.isArray(jobj)) {
  //   console.error('Expected jobj to be an array, but got:', typeof jobj);
  //   return;
  // }

  // jobj.forEach((jObject) => {
  //   Object.entries(jObject).forEach(([key, value]) => {
  //     process_jobObject(value);
  //   });
  // });

  // function process_jobObject(jbody)
  // {
  //   if (!jbody) {
  //     console.error('Invalid jObject:', jbody);
  //     return;
  //   }
  //   process_curves(jbody.Curves);
  //   process_faces(jbody.Faces);
  // }

  // function process_curves(curves) {
  //     Object.entries(curves).forEach(([curveId, curveInfo]) => {
  //       process_tesselated_info(curveInfo);
  //     });
  // }

  // function process_tesselated_info(tesselatedInfo) {
  //   if (!Array.isArray(tesselatedInfo)) {
  //     console.error('Expected tesselatedInfo to be an array, but got:', typeof tesselatedInfo);
  //     return;
  //   }

  //   tesselatedInfo.forEach(t => {
  //     add_small_ball(t.x, t.y, t.z, 0, 255, 0, 0.01);
  //   });
  // }

  // function process_faces(faces) {
  //   if (!Array.isArray(faces)) {
  //     console.error('Expected Faces to be an array, but got:', typeof faces);
  //     return;
  //   }

  //   let faceId = 10000;
  //   faces.forEach(face => {
  //     const {vertices, triangles} = process_face_vertices(face.tesselatedVertices);
  //     const referenceFaceObject = new Uid_ref_face(faceId, vertices, triangles);
  //     uid_add_reference_face(referenceFaceObject);
  //     faceId++;
  //   });
  // }

  // function process_face_vertices(vertices) {
  //   if (!Array.isArray(vertices)) {
  //     console.error('Expected vertices to be an array, but got:', typeof vertices);
  //     return {vertices: [], triangles: []};
  //   }

  //   const processedVertices = [];
  //   const triangles = [];
  //   let count = 0;
  //   let index = 0;

  //   vertices.forEach(vertex => {
  //     processedVertices.push(vertex);
  //     index++;
  //     count++;
  //     if (count === 3) {
  //       count = 0;
  //       const triangle = new Uid_face_triangle(index - 3, index - 2, index - 1);
  //       triangles.push(triangle);
  //     }
  //     add_small_ball(vertex.x, vertex.y, vertex.z, 255, 0, 0, 0.01);
  //   });

  //   return {vertices: processedVertices, triangles};
  // }
  jobj.forEach(function(bodyStr){
    if (bodyStr) {
      Object.entries(bodyStr).forEach(([pkId, data]) => {
        if (data) {
          Object.entries(data.Curves).forEach(([curveId, curveInfo]) => {
            curveInfo.forEach(function(v){
              let t = new THREE.Vector3(v.x, v.y, v.z);
              t.multiplyScalar(100);
              //add_small_ball(t.x, t.y, t.z, 0, 255, 0, 1);
            });
          });

          let fid = 10000;
          data.Faces.forEach(function(f){
            let ps_vertices = [];
            let ps_triangles = [];
            let c = 0;
            let i = 0;
            f.tesselatedVertices.forEach(function(v){
              let t = new THREE.Vector3(v.x, v.y, v.z);
              t.multiplyScalar(100);
              ps_vertices.push(t);
              i++;
              c++;
              if (c == 3) {
                c = 0;
                let tr = new Uid_face_triangle(i-3, i-2, i-1);
                ps_triangles.push(tr);
              }
              //add_small_ball(t.x, t.y, t.z, 255, 0, 0, 1);
            });
            var ref_face_obj = new Uid_ref_face( fid, ps_vertices, ps_triangles, null, true, fid, 'parasolid');
            uid_add_reference_face(ref_face_obj);
            fid++;
          });
        }
      });
    }
  });

  uid_reference_parasolid_raynode.add(uid_ref_parasolid_faces_raynode);

  App.state.showing_reference_parasolid = true;
  view_all(false);
}

export async function execute_boolean_operation(boolean_type) {
  // Find all selected bodies, in order, based on selected faces
  let body1id = 0;
  let body2id = 0;
  uid_selected_faces.keys().forEach(function(fid) {
    let bid = uil_get_body_of_face(fid);
    if (body1id == 0)
      body1id = bid;
    else if (body1id != bid && body2id == 0)
      body2id = bid;
  });

  if (body1id == 0 || body2id == 0) {
    display_message_to_user(LOG.WARN, "You need to have selected 2 bodies for the operation.");
    return;
  }

  display_message_to_user(LOG.INFO, "Performing " + boolean_type + " between the two selected bodies.");

  await export_as_step_to_parasolid(App.state.current_filename, body1id, body2id, boolean_type, is_connected_to_onshape());
}
